import React, { useState, useEffect, useMemo, createContext, useContext } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { PDFDocument } from 'pdf-lib';
import { FaUser, FaUsers, FaSignOutAlt, FaChartBar, FaClock, FaCheck, FaCheckCircle, FaTimes, FaPlus, FaEdit, FaTrash, FaFile, FaDownload, FaEye, FaEyeSlash, FaFilePdf, FaFolderOpen, FaBox, FaFileAlt, FaCog, FaUpload, FaExchangeAlt, FaBars, FaUndo, FaExclamationCircle, FaReply, FaLock } from 'react-icons/fa';
import EthiopianDatePicker from './EthiopianDatePicker';

const AuthContext = createContext();
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';
const API_ORIGIN = API_URL.replace(/\/api\/?$/, '');
const fileUrl = (p) => {
  if (!p) return p;
  let clean = String(p).replace(/\\/g, '/');
  if (!clean.startsWith('/') && !/^https?:\/\//i.test(clean)) clean = '/' + clean;
  return `${API_ORIGIN}${clean}`;
};

const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: { 'X-Portal': 'backoffice' },
});

api.interceptors.request.use((config) => {
  if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
    const h = config.headers;
    if (h) {
      if (typeof h.delete === 'function') h.delete('Content-Type');
      delete h['Content-Type'];
      delete h['content-type'];
    }
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      const url = error.config?.url || '';
      if (!url.includes('/auth/login') && !url.includes('/auth/register')
          && !url.includes('/auth/change-password') && !url.includes('/auth/forgot-password')
          && !url.includes('/auth/reset-password') && !url.includes('/auth/logout')) {
        api.post('/auth/logout').catch(() => {});
        localStorage.removeItem('username');
        localStorage.removeItem('role');
        localStorage.removeItem('sector');
        localStorage.removeItem('userId');
        localStorage.removeItem('services');
        localStorage.removeItem('must_change_password');
        window.location.reload();
      }
    }
    return Promise.reject(error);
  }
);

const TABLE_TO_SERVICE_MAP = {};
const SERVICE_TABLE_MAP = {
  'direct-fuel-beneficiary': 'gasstationtransfertocompany',
  'gas-station-transfer': 'gasstationtransfer',
  'diversion-license': 'diversionlicenseservice',
  'operator-change-license': 'gasstationoperatorchangelicenseservice',
  'new-fuel-company': 'new_fuel_company',
  'fuel-company-renewal': 'fuel_company_renewal',
  'fuel-company-transfer': 'fuel_company_transfer',
  'new-gas-station': 'new_gas_station',
  'gas-station-renewal': 'gas_station_renewal',
  'gas-station-connection-transfer': 'gas_station_connection_transfer',
  'new-depot-fuel-cargo': 'new_depot_fuel_cargo',
  'depot-fuel-cargo-renewal': 'depot_fuel_cargo_renewal',
  'depot-fuel-cargo-transfer': 'depot_fuel_cargo_transfer',
  'new-gas-station-retail': 'new_fuel_station_retail_competency',
  'gas-station-retail-renewal': 'gas_station_retail_renewal',
  'new-fuel-distributor': 'new_fuel_distributor_competenc',
  'fuel-distributor-renewal': 'fuel_distributor_renewal',
  'new-fuel-import': 'new_fuel_importer_competency',
  'fuel-import-renewal': 'fuel_import_renewal',
  'new-gas-import': 'new_gas_importer_competency',
  'gas-import-renewal': 'gas_import_renewal',
  'new-oil-exporter': 'new_fuel_exporter_competency',
  'oil-exporter-renewal': 'oil_exporter_renewal',
  'new-fuel-refinery': 'new_fuel_refining_competency',
  'fuel-refinery-renewal': 'fuel_refinery_renewal',
  'new-fuel-transportation': 'new_fuel_transportation_competency',
  'fuel-transportation-renewal': 'fuel_transportation_renewal',
  'fuel-order-cancellation': 'fuel_order_cancellation',
  'fuel-order-cancellation-service': 'fuel_order_cancellation_service',
  'vehicle-breakdown-reporting': 'vehicle_breakdown_reporting',
  'fuel-delivery-extension': 'fuel_delivery_extension',
  'electric-vehicle-charging': 'electric_vehicle_charging',
  'direct-fuel': 'gasstationtransfertocompany',
  'Fuel_Station_Retail_Construction_Permit': 'fuel_station_retail_construction',
  'Fuel_Distributor_Construction_Permit': 'fuel_distributor_construction',
  'Fuel_Importer_ Construction_Permit': 'fuel_importer_construction',
  'Gas_Importer_Construction_Permit': 'gas_importer_construction',
  'Fuel_Exporter_Construction_permit': 'fuel_exporter_construction',
  'Fuel_Refining_Construction_permit': 'fuel_refining_construction',
  'Fuel_Transportation_Construction_permit': 'fuel_transportation_construction',
  'gas-station-retail-construction': 'fuel_station_retail_construction',
  'fuel-distributor-construction': 'fuel_distributor_construction',
  'fuel-import-construction': 'fuel_importer_construction',
  'gas-import-construction': 'gas_importer_construction',
  'oil-exporter-construction': 'fuel_exporter_construction',
  'fuel-refinery-construction': 'fuel_refining_construction',
  'fuel-transportation-construction': 'fuel_transportation_construction',
};

for (const [k, v] of Object.entries(SERVICE_TABLE_MAP)) {
  TABLE_TO_SERVICE_MAP[v] = k;
}

const TOP_ROLES_FILTER = ['Director', 'DeputyDir', 'deputydirector', 'SuperAdmin', 'admin'];
function filterVisibleRequests(combined, { role, sector, roleMap, transfersMap, archivedSet, userServices, userId }) {
  if (!Array.isArray(combined)) return [];
  const r = (role || '').toLowerCase();
  const isTop = TOP_ROLES_FILTER.some(x => x.toLowerCase() === r);
  return combined.filter(item => {
    const archiveKey = item.serviceType + ':' + item.id;
    if (archivedSet && archivedSet.has(archiveKey)) return false;
    if (isTop) return true;
    const transferKey = item.serviceType + ':' + item.id;
    const t = transfersMap && transfersMap[transferKey];
    if (role === 'BackOfficer' || role === 'TeamLeader') {
      if (t && String(t.assigned_to) === String(userId)) return true;
      return !!(t && !t.assigned_to && t.role === role);
    }
    const serviceSector = (roleMap && (roleMap[item.serviceType + ':sector'] || '')).trim();
    const curSector = (sector || '').trim();
    if (curSector && curSector !== 'both' && serviceSector && serviceSector.toLowerCase() !== curSector.toLowerCase()) {
      return false;
    }
    if (role === 'DeptHead') {
      if (Array.isArray(userServices) && userServices.length > 0 && !userServices.includes(item.serviceType)) return false;
      return true;
    }
    if (t && t.role === role) return true;
    const assigned = (roleMap && roleMap[item.serviceType]) || 'DeptHead';
    return assigned === role;
  });
}

function getUserServices() {
  try {
    const v = localStorage.getItem('services');
    const arr = v ? JSON.parse(v) : [];
    return Array.isArray(arr) ? arr : [];
  } catch (e) {
    return [];
  }
}

function getQueueFlags(request) {
  const message = (request && request.transferMessage) || '';
  const approvedForBackOffice = !!request
    && request.status === 'approved'
    && message.indexOf('[Approved]') === 0;
  return {
    awaitingDeptHeadApproval: !!(request && request.assignedRole === 'DeptHead' && /for approval/i.test(message) && !request.dept_head_approved),
    awaitingBackOfficeReview: !!(request && (
      (message.indexOf('[Correction Resubmitted]') === 0 && request.status !== 'returned')
      || approvedForBackOffice
    )),
  };
}

function generatePDF(item) {
  const doc = new jsPDF();
  
  doc.setFontSize(18);
  doc.setTextColor(0, 100, 0);
  doc.text('PEA SERVICE REQUEST FORM', 105, 20, { align: 'center' });
  
  doc.setFontSize(10);
  doc.setTextColor(100);
  doc.text(`Reference Number: ${item.reference_no}`, 14, 35);
  doc.text(`Status: ${item.status?.toUpperCase() || 'PENDING'}`, 14, 42);
  
  const SERVICE_NAME_MAP = {
    'direct-fuel': 'የነዳጅ ቀጥታ ተጠቃሚ ተቋማት ትስስር ፈቃድ አገልግሎት / Service for direct fuel beneficiary',
    'direct-fuel-beneficiary': 'የነዳጅ ቀጥታ ተጠቃሚ ተቋማት ትስስር ፈቃድ አገልግሎት / Service for direct fuel beneficiary',
    'gas-station-transfer': 'Gas Station Transfer Service', 'diversion-license': 'Diversion License Service',
    'operator-change-license': 'Operator/Dealer Change License Service', 'new-fuel-company': 'New Fuel Distribution Company Connection License',
    'fuel-company-renewal': 'Fuel Distribution Company License Renewal', 'fuel-company-transfer': 'Fuel Distribution Company License Transfer',
    'new-gas-station': 'New Gas Station Connection License', 'gas-station-renewal': 'Gas Station License Renewal',
    'gas-station-connection-transfer': 'Gas Station License Transfer', 'new-depot-fuel-cargo': 'New Depot Fuel Cargo Connection License',
    'depot-fuel-cargo-renewal': 'Depot Fuel Cargo License Renewal', 'depot-fuel-cargo-transfer': 'Depot Fuel Cargo License Transfer',
    'new-gas-station-retail': 'New Gas Station Retail Competency and Licensing', 'new-fuel-distributor': 'New Fuel Distributor Competency and Licensing',
    'new-fuel-import': 'New Fuel Import Competency and Licensing', 'new-gas-import': 'New Gas Import Competency and Licensing',
    'new-oil-exporter': 'New Oil Exporter Competency and Licensing', 'new-fuel-refinery': 'New Fuel Refinery Competency and Licensing',
    'new-fuel-transportation': 'New Fuel Transportation Competency and Licensing', 'gas-station-retail-renewal': 'Gas Station Retail License Renewal',
    'fuel-distributor-renewal': 'Fuel Distributor License Renewal', 'fuel-import-renewal': 'Fuel Import License Renewal',
    'gas-import-renewal': 'Gas Import License Renewal', 'oil-exporter-renewal': 'Oil Exporter License Renewal',
    'fuel-refinery-renewal': 'Fuel Refinery License Renewal', 'fuel-transportation-renewal': 'Fuel Transportation License Renewal',
    'fuel-order-cancellation': 'Fuel Order Cancellation Service',
    'fuel-order-cancellation-service': 'የነዳጅ ጭነት ትእዛዝ አገልግሎት ማቋረጥ / Fuel delivery order cancellation service',
    'vehicle-breakdown-reporting': 'ብልሽት የገጠመውን ተሸከርካሪ ማሳወቅ አገልግሎት / Vehicle Breakdown Reporting Service',
    'fuel-delivery-extension': 'የነዳጅ አቅርቦት ማራዘም ጥያቄ አገልግሎት / Fuel Delivery Extension Service',
    'electric-vehicle-charging': 'የኤሌክትሪክ ተሽከርካሪ ቻርጅ መሙሌት አገልግሎት / Electric Vehicle Charging Service License Request',
    'Fuel_Station_Retail_Construction_Permit': 'Fuel Station Retail Construction Permit',
    'Fuel_Distributor_Construction_Permit': 'Fuel Distributor Construction Permit',
    'Fuel_Importer_ Construction_Permit': 'Fuel Importer Construction Permit',
    'Gas_Importer_Construction_Permit': 'Gas Importer Construction Permit',
    'Fuel_Exporter_Construction_permit': 'Fuel Exporter Construction Permit',
    'Fuel_Refining_Construction_permit': 'Fuel Refining Construction Permit',
    'Fuel_Transportation_Construction_permit': 'Fuel Transportation Construction Permit',
  };
  doc.text(`Service: ${SERVICE_NAME_MAP[item.serviceType] || item.service_type || 'Direct Fuel Beneficiary'}`, 14, 49);

                  const EXCLUDED = new Set(['id', 'user_id', 'serviceType', 'service_type', 'status', 'created_at', 'updated_at', 'request_date', 'decision_date', 'response_reason', 'rejection_reason', 'remark', 'approved_letter', 'application_letter', 'support_letter', 'aggrement_letter', 'certificate_path', 'assignedToName', 'assignedByName', 'transferMessage', 'existing_approved_letter', 'reference_no', 'password', 'fan_national_id', 'additional_attachments', 'fuel_retail_price_from', 'fuel_retail_price_to', 'dept_head_approved', 'work_completed', 'client_letter', 'client_letter_name', 'client_letter_sent_at', 'archive_document', 'letter_released', 'letter_released_at']);
  const LABEL_MAP = {
    nameofapplicant: 'የአመልካች ስም / Applicant Name', nationality: 'ዜግነት / Nationality', idno: 'የመታወቂያ ቁጥር / ID No', location: 'አካባቢ / Location',
    dateandplaceissued: 'የተሰጠበት ቀንና ቦታ / Date Issued', applicant_region: 'የአመልካች ክልል / Applicant Region', applicant_subcity: 'የአመልካች ከተማ / Applicant SubCity',
    applicant_wereda: 'የአመልካች ወረዳ / Applicant Woreda', applicant_hno: 'የአመልካች ቤት ቁጥር / Applicant H.No', applicant_pobox: 'የአመልካች ፖስታ ሳጥን / Applicant P.O.Box',
    applicant_telno: 'የአመልካች ስልክ / Applicant Tel No', applicant_email: 'የአመልካች ኢሜይል / Applicant Email',
    site_region: 'የቦታው ክልል / Site Region', site_zone: 'የቦታው ዞን / Site Zone', site_subcity: 'የቦታው ከተማ / Site SubCity',
    site_woreda: 'የቦታው ወረዳ / Site Woreda', site_hno: 'የቦታው ቤት ቁጥር / Site H.No', site_phone: 'የቦታው ስልክ / Site Phone',
    existingcompanyname: 'ያለው ኩባንያ / Existing Company', transferto: 'ወደሚዛወርበት / Transfer To',
    fuel_distributer_name: 'የነዳጅ አከፋፋይ ስም / Fuel Distributor', manager_name: 'የሥራ አስኪያጅ ስም / Manager Name',
    manager_nationality: 'የሥራ አስኪያጅ ዜግነት / Manager Nationality', manager_idno: 'የሥራ አስኪያጅ መታወቂያ / Manager ID No',
    manager_region: 'የሥራ አስኪያጅ ክልል / Manager Region', manager_subcity: 'የሥራ አስኪያጅ ከተማ / Manager SubCity',
    manager_woreda: 'የሥራ አስኪያጅ ወረዳ / Manager Woreda', manager_hno: 'የሥራ አስኪያጅ ቤት ቁጥር / Manager H.No',
    manager_k_ketema: 'የሥራ አስኪያጅ ከ/ከተማ / Manager K/Ketema', manager_wereda: 'የሥራ አስኪያጅ ወረዳ / Manager Woreda',
    manager_telno: 'የሥራ አስኪያጅ ስልክ / Manager Tel No', manager_mobno: 'የሥራ አስኪያጅ ሞባይል / Manager Mobile',
    manager_mobile: 'የሥራ አስኪያጅ ሞባይል / Manager Mobile', manager_email: 'የሥራ አስኪያጅ ኢሜይል / Manager Email',
    manager_fixed_phone: 'የሥራ አስኪያጅ ቋሚ ስልክ / Manager Fixed Phone', manager_pobox: 'የሥራ አስኪያጅ ፖስታ ሳጥን / Manager P.O.Box',
    new_dealer_name: 'የአዲስ አከፋፋይ ስም / New Dealer Name', merchant_id_short_code: 'የነጋዴ መለያ / Merchant ID',
    phone_orders: 'የስልክ ትእዛዝ / Phone Orders', dealer_requester_name: 'የአከፋፋይ ጠያቂ / Dealer Requester',
    first_destination_name: 'የመጀመሪያ መዳረሻ / First Destination', first_destination_region: 'የመጀመሪያ መዳረሻ ክልል / First Destination Region',
    first_destination_woreda: 'የመጀመሪያ መዳረሻ ወረዳ / First Destination Woreda', first_destination_specific_place: 'የመጀመሪያ መዳረሻ ቦታ / First Destination Place',
    gas_station_name: 'የነዳጅ ማደያ ስም / Gas Station Name', gas_station_region: 'የነዳጅ ማደያ ክልል / Gas Station Region',
    gas_station_woreda: 'የነዳጅ ማደያ ወረዳ / Gas Station Woreda', gas_station_specific_place: 'የነዳጅ ማደያ ቦታ / Gas Station Place',
    change_of_place: 'የቦታ ለውጥ / Change of Place', changing_truck: 'የሚለወጥ መኪና / Changing Truck',
    detailed_reason: 'ዝርዝር ምክንያት / Detailed Reason',
    organization_name: 'ድርጅት / Organization', project_name: 'የፕሮጀክት ስም / Project Name',
    product_type: 'የምርት አይነት / Product Type', product_amount: 'የምርት መጠን / Product Amount',
    no_of_machines: 'የማሽኖች ብዛት / No. of Machines', fuel_type: 'የነዳጅ አይነት / Fuel Type',
    amount_of_fuel: 'የነዳጅ መጠን / Amount of Fuel', monthly_fuel_need: 'ወርሃዊ የነዳጅ ፍላጎት / Monthly Fuel Need',
    supplier_name: 'አቅራቢ / Supplier', support_organization_name: 'ድጋፍ ሰጪ ድርጅት / Support Organization',
    project_end_date: 'የፕሮጀክት መጨረሻ ቀን / Project End Date', fuel_distributer: 'የነዳጅ አከፋፋይ / Fuel Distributor',
    fuel_distributor_letter_no: 'የነዳጅ አከፋፋይ ደብዳቤ ቁጥር / Fuel Distributor Letter No',
    fuel_distributor_letter_date: 'የነዳጅ አከፋፋይ ደብዳቤ ቀን / Fuel Distributor Letter Date',
    support_org_letter_no: 'የድጋፍ ድርጅት ደብዳቤ ቁጥር / Support Org Letter No',
    support_org_letter_date: 'የድጋፍ ድርጅት ደብዳቤ ቀን / Support Org Letter Date',
    company_name: 'የኩባንያ ስም / Company Name', importer_company_name: 'የአስመጪ ኩባንያ ስም / Importer Company Name',
    exporter_company_name: 'የላኪ ኩባንያ ስም / Exporter Company Name', refinery_name: 'የማጣሪያ ስም / Refinery Name',
    transporter_company_name: 'የማጓጓዣ ኩባንያ ስም / Transporter Company Name',
    depot_name: 'የዴፖ ስም / Depot Name', distributor_company_name: 'የአከፋፋይ ኩባንያ ስም / Distributor Company Name',
    gas_station_retail_name: 'የነዳጅ ማደያ ችርቻሮ ስም / Gas Station Retail Name',
    gas_importer_company_name: 'የጋዝ አስመጪ ኩባንያ ስም / Gas Importer Company Name',
    existing_ref_no: 'ያለው ማጣቀሻ ቁጥር / Existing Ref No', renewal_year: 'የእድሳት ዘመን / Renewal Year',
    payment_fee: 'የክፍያ መጠን / Payment Fee', renewal_date: 'የእድሳት ቀን / Renewal Date',
    transfer_from: 'ከሚዛወርበት / Transfer From', transfer_to: 'ወደሚዛወርበት / Transfer To', transfer_date: 'የዝውውር ቀን / Transfer Date', transfer_reason: 'የሚዛወርበት ምክንያት / Reason for Transfer',
    type_of_operation: 'የስራ አይነት / Type of Operation',
    applicant_name: 'የአመልካች ስም / Applicant Name', place_of_renewed: 'የታደሰበት ቦታ / Place of Renewed',
    date_of_renewed: 'የታደሰበት ቀን / Date of Renewed', region: 'ክልል / Region', subcity: 'ከተማ / SubCity',
    woreda: 'ወረዳ / Woreda', hno: 'ቤት ቁጥር / H.No', pobox: 'ፖስታ ሳጥን / P.O.Box', telno: 'ስልክ / Tel No', email: 'ኢሜይል / Email',
    manager_date_place_issued: 'የሥራ አስኪያጅ የተሰጠበት ቀን / Manager Date Issued',
    manager_ketema: 'የሥራ አስኪያጅ ከተማ / Manager Ketema',
    fuel_loading_date: 'የነዳጅ ጭነት ቀን / Fuel Loading Date', fuel_load_amount: 'የነዳጅ ጭነት መጠን / Fuel Load Amount',
    plate_number: 'የታርጋ ቁጥር / Plate Number', trailer_plate_number: 'የተሳቢ ታርጋ / Trailer Plate Number',
    fdc: 'ኤፍዲሲ / FDC', loading_fuel_type: 'የነዳጅ አይነት / Type of Fuel', replacing_plate_number: 'አዲስ ታርጋ / Replacing Plate Number',
    replacing_trailer_plate_number: 'የሚተካ ተሳቢ ታርጋ / Replacing Trailer Plate Number',
    replaced_plate_number: 'የተተካ ታርጋ / Replaced Plate Number',
    replaced_trailer_plate_number: 'የተተካ ተሳቢ ታርጋ / Replaced Trailer Plate Number',
    name: 'ስም / Name', name_amharic: 'ስም (በአማርኛ) / Name (Amharic)',
    work_address: 'የስራ አድራሻ / Work Address', subcity_zone: 'ከተማ ዞን / SubCity Zone',
    woreda_kebele: 'ወረዳ/ቀበሌ / Woreda/Kebele', id_place: 'የመታወቂያ ቦታ / ID Place', id_date: 'የመታወቂያ ቀን / ID Date',
    product_work_type: 'የምርት ስራ አይነት / Product Work Type', work_percentage: 'የስራ መቶኛ / Work Percentage',
    machine_type: 'የማሽን አይነት / Machine Type', fuel_amount: 'የነዳጅ መጠን / Fuel Amount',
    monthly_fuel_consumption: 'ወርሃዊ የነዳጅ ፍጆታ / Monthly Fuel Consumption',
    type_of_machine: 'የማሽን አይነት / Type of Machine', id_renewal_date: 'የመታወቂያ እድሳት ቀን / ID Renewal Date',
    id_renewal_place: 'የመታወቂያ እድሳት ቦታ / ID Renewal Place', phone_no: 'ስልክ ቁጥር / Phone No',
    po_box: 'ፖስታ ሳጥን / P.O.Box',
    fuel_company_name: 'የነዳጅ ካምፓኒ ሥም / Fuel Company Name',
    letter_issued_date: 'ደብዳቤ የተጻፈበት ቀን / Letter Issued Date',
    letter_number: 'የደብዳቤ ቁጥር / Letter Number',
    fuel_unloading_site: 'የነዳጅ ጭነት የሚራገፍበት ቦታ / Fuel Unloading Site',
    reason_for_cancellation: 'የሚሰረዝበት ምክንያት / Reason for Cancellation',
    requester_name: 'የጠየቀው ካምፓኒ / ግለሰብ ሥም / Requester Company or Person Name',
    fuel_load_date: 'ነዳጅ የተጫነበት ቀን / Fuel Loading Date',
    order_number: 'የትዕዛዝ ቁጥር / Order Number',
    fdc_number: 'የFDC ቁጥር / FDC Number',
    letter_date: 'ማመልከቻ / ደብዳቤ የተጻፈበት ቀን / Application Letter Date',
    breakdown_location: 'ብልሽቱ የተከሰተበት ቦታ / Breakdown Location',
    breakdown_date: 'ብልሽቱ የተከሰሰበት ቀን / Breakdown Date',
    breakdown_description: 'የብልሽቱ ዓይነት በአጭሩ / Brief Description of Breakdown',
    report_date: 'ያመለከቱበት ቀን / Reporting Date',
    extension_start_date: 'የሚራዘምለት መነሻ ቀን / Extension Start Date',
    extension_end_date: 'የሚራዘምለት መጨረሻ ቀን / Extension End Date',
    fuel_quantity: 'የነዳጅ መጠን / Fuel Quantity (L)',
    license_type: 'የፈቃድ ዓይነት / Type of License', license_category: 'የፍቃድ ደረጃ / License Category',
    company_city: 'ከተማ / City', company_subcity: 'ክፍለ ከተማ / Sub-City', company_woreda: 'ወረዳ / Woreda',
    company_zone: 'ዞን / Zone', company_phone_no: 'ስልክ ቁ / Phone No', company_po_box: 'የፓ.ሳ.ቁ / P.O. Box',
    company_tin_no: 'የግ.ከ.ቁ / TIN No', company_fax_no: 'ፋክስ ቁጥር / Fax No', company_email: 'ኢሜይል / Email',
    company_landmark: 'የቦታዉ ልዩ ስም (አመላካች) / Specific Name/Landmark',
    site_city: 'ከተማ / Site City', site_house_no: 'የቤት ቁ / House No', site_phone_no: 'ስልክ ቁ / Site Phone No',
    gm_name: 'የሥራ አስኪያጅ ስም / General Manager Name', gm_job_title: 'የስራ ኃላፊነት / Job Title/Position',
    gm_city: 'ከተማ / City', gm_phone_no: 'የስልክ ቁጥር / Phone Number', gm_email: 'ኢ.ሜ.ል / Email',
    charging_capacity_kw: 'የቻርጅ መሙያ አገልግሎት አቅም / Charging Capacity (KW/KVA)',
    electric_supply_source: 'የኤሌክትሪክ አቅርቦት ምንጭ / Source of Electric Supply',
    estimated_start_date: 'ግምታዊ መጀመሪያ ቀን / Estimated Start Date',
    id_photo: 'የመታወቂያ ፎቶ / ID Photo',
  };

  const entries = Object.entries(item).filter(([k, v]) => !EXCLUDED.has(k) && (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') && v !== '' && !k.startsWith('_'));
  
  doc.setFontSize(10);
  let yPos = 60;
  const pageHeight = 280;
  const leftMargin = 14;

  entries.forEach(([k, v], i) => {
    if (yPos > pageHeight) {
      doc.addPage();
      yPos = 20;
    }
    if (i > 0 && i % 30 === 0) {
      doc.setFontSize(12);
      doc.text('--- Continued ---', 105, yPos, { align: 'center' });
      yPos += 7;
      doc.setFontSize(10);
    }
    let displayVal = v;
    if (typeof v === 'string' && v.match(/^\d{4}-\d{2}-\d{2}/)) {
      try { displayVal = new Date(v).toLocaleDateString(); } catch(e) {}
    }
    if (typeof v === 'boolean') displayVal = v ? 'Yes' : 'No';
    const label = LABEL_MAP[k] || k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
    doc.setFont(undefined, 'bold');
    doc.text(`${label}:`, leftMargin, yPos);
    doc.setFont(undefined, 'normal');
    const labelEnd = leftMargin + doc.getTextWidth(`${label}:  `);
    const maxWidth = 190 - labelEnd;
    if (doc.getTextWidth(String(displayVal)) > maxWidth) {
      const lines = doc.splitTextToSize(String(displayVal), maxWidth);
      lines.forEach((line, li) => {
        if (yPos > pageHeight) {
          doc.addPage();
          yPos = 20;
        }
        doc.text(line, labelEnd, yPos);
        if (li < lines.length - 1) yPos += 7;
      });
    } else {
      doc.text(String(displayVal), labelEnd, yPos);
    }
    yPos += 7;
  });

  yPos += 10;
  if (yPos > pageHeight) { doc.addPage(); yPos = 20; }
  doc.setFontSize(12);
  doc.text('ATTACHMENTS', 14, yPos);
  doc.setLineWidth(0.5);
  doc.line(14, yPos + 2, 196, yPos + 2);
  yPos += 8;
  doc.setFontSize(10);
  const attachFields = [
    ['Application Letter', item.application_letter],
    ['Support Letter', item.support_letter],
    ['Agreement Letter', item.aggrement_letter],
    ['Site Plan and Design Drawing', item.site_plan_drawing],
    ['Approval/Rejection Letter', item.approved_letter],
  ];
  attachFields.forEach(([label, val]) => {
    doc.text(`${label}: ${val ? 'Attached' : 'Not provided'}`, 14, yPos);
    yPos += 7;
  });

  yPos += 5;
  if (yPos > pageHeight) { doc.addPage(); yPos = 20; }
  doc.setFontSize(12);
  doc.text('RESPONSE DETAILS', 14, yPos);
  doc.line(14, yPos + 2, 196, yPos + 2);
  yPos += 8;
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.text('Status:', 14, yPos);
  doc.setFont(undefined, 'normal');
  doc.text(item.status?.toUpperCase() || 'PENDING', 55, yPos);
  yPos += 7;
  if (item.response_reason) {
    doc.setFont(undefined, 'bold');
    doc.text('Response Reason:', 14, yPos);
    doc.setFont(undefined, 'normal');
    doc.text(item.response_reason, 55, yPos);
    yPos += 7;
  }
  if (item.rejection_reason) {
    doc.setFont(undefined, 'bold');
    doc.text('Rejection Reason:', 14, yPos);
    doc.setFont(undefined, 'normal');
    doc.text(item.rejection_reason, 55, yPos);
    yPos += 7;
  }
  if (item.assignedToName) {
    doc.setFont(undefined, 'bold');
    doc.text('Assigned To:', 14, yPos);
    doc.setFont(undefined, 'normal');
    doc.text(item.assignedToName, 55, yPos);
    yPos += 7;
  }
  if (item.decision_date) {
    doc.text('Decision Date:', 14, yPos);
    doc.text(new Date(item.decision_date).toLocaleDateString(), 55, yPos);
    yPos += 7;
  }
  if (item.remark) {
    yPos += 3;
    doc.setFont(undefined, 'bold');
    doc.text('Remark:', 14, yPos);
    doc.setFont(undefined, 'normal');
    yPos += 7;
    const remarkLines = doc.splitTextToSize(item.remark, 180);
    remarkLines.forEach(line => {
      if (yPos > pageHeight) { doc.addPage(); yPos = 20; }
      doc.text(line, 14, yPos);
      yPos += 7;
    });
  }
  
  yPos += 10;
  if (yPos > 275) yPos = 275;
  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.text(`Generated by PEA Service on ${new Date().toLocaleString()}`, 105, yPos, { align: 'center' });
  
  doc.save(`${item.reference_no || 'request'}_details.pdf`);
  toast.success('PDF downloaded successfully');
}

async function generateApprovalLetter(item) {
  try {
    const res = await api.post('/approval-letter/generate-approval-letter', {
      request_id: item.id,
      service_type: item.serviceType,
    }, { responseType: 'blob' });
    const blob = res.data;
    const url = URL.createObjectURL(blob);
    const letterKind = (item.status === 'approved' || item.status === 'paid') ? 'approval' : 'rejection';
    const referenceNo = String(item.reference_no || 'request').replace(/[\\/:*?"<>|]/g, '-');
    const a = document.createElement('a');
    a.href = url;
    a.download = `${referenceNo}_${letterKind}_letter.docx`;
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
    toast.success(`${letterKind === 'approval' ? 'Approval' : 'Rejection'} letter downloaded`);
  } catch (err) {
    toast.error('Error: ' + err.message);
  }
}

function Header({ onLogout }) {
  return (
    <header className="bg-gradient-to-r from-[#FF6B00] to-[#cc5500] text-white shadow-lg">
      <div className="container mx-auto px-4 md:px-6 py-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            <img src={process.env.PUBLIC_URL + '/Logo1.png'} alt="PEA Logo" className="h-12 md:h-20 flex-shrink-0" />
            <h1 className="text-sm md:text-lg lg:text-2xl font-bold leading-tight truncate">የነዳጅና ኢነርጂ ባለሥልጣን<br className="hidden md:inline" /> Petroleum and Energy Authority</h1>
          </div>
          <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
            <span className="flex items-center gap-1 md:gap-2 text-sm md:text-base">
              <FaUser /> <span className="hidden sm:inline">{localStorage.getItem('username') || 'User'}</span>
            </span>
            <button onClick={onLogout} className="flex items-center gap-1 md:gap-2 text-sm md:text-base hover:text-gray-200">
              <FaSignOutAlt /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="bg-green-500 text-white py-3 md:py-4 px-4 md:px-6 mt-auto">
      <div className="container mx-auto text-center">
        <p className="text-xs md:text-sm">&copy; 2026 PEA Backoffice. All rights reserved.</p>
      </div>
    </footer>
  );
}

function Dashboard() {
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0, returned: 0, payment_pending: 0, paid: 0 });
  const [customerCount, setCustomerCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [directFuelRes, gsTransferRes, diversionRes, operatorChangeRes, nfcRes, fuelRenewalRes, fuelTransferRes, newGasStationRes, gsRenewalRes, gsConnectionTransferRes, depotFuelCargoRes, depotFuelCargoRenewalRes, depotFuelCargoTransferRes, newGasStationRetailRes, newFuelDistributorRes, newFuelImportRes, newGasImportRes, newOilExporterRes, newFuelRefineryRes, newFuelTransportationRes, gsRetailRenewalRes, fuelDistributorRenewalRes, fuelImportRenewalRes, gasImportRenewalRes, oilExporterRenewalRes, fuelRefineryRenewalRes, fuelTransportationRenewalRes, fuelOrderCancellationRes, fuelOrderCancellationServiceRes, vehicleBreakdownReportingRes, fuelDeliveryExtensionRes, electricVehicleChargingRes, constructionGasRetailRes, constructionFuelDistributorRes, constructionFuelImportRes, constructionGasImportRes, constructionOilExporterRes, constructionFuelRefineryRes, constructionFuelTransportationRes] = await Promise.all([
        api.get('/direct-fuel-beneficiary').catch(() => ({ data: [] })),
        api.get('/gas-station-transfer').catch(() => ({ data: [] })),
        api.get('/diversion-license').catch(() => ({ data: [] })),
        api.get('/operator-change-license').catch(() => ({ data: [] })),
        api.get('/new-fuel-company').catch(() => ({ data: [] })),
        api.get('/fuel-company-renewal').catch(() => ({ data: [] })),
        api.get('/fuel-company-transfer').catch(() => ({ data: [] })),
        api.get('/new-gas-station').catch(() => ({ data: [] })),
        api.get('/gas-station-renewal').catch(() => ({ data: [] })),
        api.get('/gas-station-connection-transfer').catch(() => ({ data: [] })),
        api.get('/new-depot-fuel-cargo').catch(() => ({ data: [] })),
        api.get('/depot-fuel-cargo-renewal').catch(() => ({ data: [] })),
        api.get('/depot-fuel-cargo-transfer').catch(() => ({ data: [] })),
        api.get('/new-gas-station-retail').catch(() => ({ data: [] })),
        api.get('/new-fuel-distributor').catch(() => ({ data: [] })),
        api.get('/new-fuel-import').catch(() => ({ data: [] })),
        api.get('/new-gas-import').catch(() => ({ data: [] })),
        api.get('/new-oil-exporter').catch(() => ({ data: [] })),
        api.get('/new-fuel-refinery').catch(() => ({ data: [] })),
        api.get('/new-fuel-transportation').catch(() => ({ data: [] })),
        api.get('/gas-station-retail-renewal').catch(() => ({ data: [] })),
        api.get('/fuel-distributor-renewal').catch(() => ({ data: [] })),
        api.get('/fuel-import-renewal').catch(() => ({ data: [] })),
        api.get('/gas-import-renewal').catch(() => ({ data: [] })),
        api.get('/oil-exporter-renewal').catch(() => ({ data: [] })),
        api.get('/fuel-refinery-renewal').catch(() => ({ data: [] })),
        api.get('/fuel-transportation-renewal').catch(() => ({ data: [] })),
        api.get('/fuel-order-cancellation').catch(() => ({ data: [] })),
        api.get('/fuel-order-cancellation-service').catch(() => ({ data: [] })),
        api.get('/vehicle-breakdown-reporting').catch(() => ({ data: [] })),
        api.get('/fuel-delivery-extension').catch(() => ({ data: [] })),
        api.get('/electric-vehicle-charging').catch(() => ({ data: [] })),
        api.get('/gas-station-retail-construction').catch(() => ({ data: [] })),
        api.get('/fuel-distributor-construction').catch(() => ({ data: [] })),
        api.get('/fuel-import-construction').catch(() => ({ data: [] })),
        api.get('/gas-import-construction').catch(() => ({ data: [] })),
        api.get('/oil-exporter-construction').catch(() => ({ data: [] })),
        api.get('/fuel-refinery-construction').catch(() => ({ data: [] })),
        api.get('/fuel-transportation-construction').catch(() => ({ data: [] }))
      ]);

      const allRequests = [...directFuelRes.data, ...gsTransferRes.data, ...diversionRes.data, ...operatorChangeRes.data, ...nfcRes.data, ...fuelRenewalRes.data, ...fuelTransferRes.data, ...newGasStationRes.data, ...gsRenewalRes.data, ...gsConnectionTransferRes.data, ...depotFuelCargoRes.data, ...depotFuelCargoRenewalRes.data, ...depotFuelCargoTransferRes.data, ...newGasStationRetailRes.data, ...newFuelDistributorRes.data, ...newFuelImportRes.data, ...newGasImportRes.data, ...newOilExporterRes.data, ...newFuelRefineryRes.data, ...newFuelTransportationRes.data, ...gsRetailRenewalRes.data, ...fuelDistributorRenewalRes.data, ...fuelImportRenewalRes.data, ...gasImportRenewalRes.data, ...oilExporterRenewalRes.data, ...fuelRefineryRenewalRes.data, ...fuelTransportationRenewalRes.data, ...fuelOrderCancellationRes.data, ...fuelOrderCancellationServiceRes.data, ...vehicleBreakdownReportingRes.data, ...fuelDeliveryExtensionRes.data, ...electricVehicleChargingRes.data, ...constructionGasRetailRes.data, ...constructionFuelDistributorRes.data, ...constructionFuelImportRes.data, ...constructionGasImportRes.data, ...constructionOilExporterRes.data, ...constructionFuelRefineryRes.data, ...constructionFuelTransportationRes.data];

      const tagSvc = (arr, t) => arr.forEach(r => { r.serviceType = t; });
      tagSvc(directFuelRes.data, 'direct-fuel');
      tagSvc(gsTransferRes.data, 'gas-station-transfer');
      tagSvc(diversionRes.data, 'diversion-license');
      tagSvc(operatorChangeRes.data, 'operator-change-license');
      tagSvc(nfcRes.data, 'new-fuel-company');
      tagSvc(fuelRenewalRes.data, 'fuel-company-renewal');
      tagSvc(fuelTransferRes.data, 'fuel-company-transfer');
      tagSvc(newGasStationRes.data, 'new-gas-station');
      tagSvc(gsRenewalRes.data, 'gas-station-renewal');
      tagSvc(gsConnectionTransferRes.data, 'gas-station-connection-transfer');
      tagSvc(depotFuelCargoRes.data, 'new-depot-fuel-cargo');
      tagSvc(depotFuelCargoRenewalRes.data, 'depot-fuel-cargo-renewal');
      tagSvc(depotFuelCargoTransferRes.data, 'depot-fuel-cargo-transfer');
      tagSvc(newGasStationRetailRes.data, 'new-gas-station-retail');
      tagSvc(newFuelDistributorRes.data, 'new-fuel-distributor');
      tagSvc(newFuelImportRes.data, 'new-fuel-import');
      tagSvc(newGasImportRes.data, 'new-gas-import');
      tagSvc(newOilExporterRes.data, 'new-oil-exporter');
      tagSvc(newFuelRefineryRes.data, 'new-fuel-refinery');
      tagSvc(newFuelTransportationRes.data, 'new-fuel-transportation');
      tagSvc(gsRetailRenewalRes.data, 'gas-station-retail-renewal');
      tagSvc(fuelDistributorRenewalRes.data, 'fuel-distributor-renewal');
      tagSvc(fuelImportRenewalRes.data, 'fuel-import-renewal');
      tagSvc(gasImportRenewalRes.data, 'gas-import-renewal');
      tagSvc(oilExporterRenewalRes.data, 'oil-exporter-renewal');
      tagSvc(fuelRefineryRenewalRes.data, 'fuel-refinery-renewal');
      tagSvc(fuelTransportationRenewalRes.data, 'fuel-transportation-renewal');
      tagSvc(fuelOrderCancellationRes.data, 'fuel-order-cancellation');
      tagSvc(fuelOrderCancellationServiceRes.data, 'fuel-order-cancellation-service');
      tagSvc(vehicleBreakdownReportingRes.data, 'vehicle-breakdown-reporting');
      tagSvc(fuelDeliveryExtensionRes.data, 'fuel-delivery-extension');
      tagSvc(electricVehicleChargingRes.data, 'electric-vehicle-charging');
      tagSvc(constructionGasRetailRes.data, 'Fuel_Station_Retail_Construction_Permit');
      tagSvc(constructionFuelDistributorRes.data, 'Fuel_Distributor_Construction_Permit');
      tagSvc(constructionFuelImportRes.data, 'Fuel_Importer_ Construction_Permit');
      tagSvc(constructionGasImportRes.data, 'Gas_Importer_Construction_Permit');
      tagSvc(constructionOilExporterRes.data, 'Fuel_Exporter_Construction_permit');
      tagSvc(constructionFuelRefineryRes.data, 'Fuel_Refining_Construction_permit');
      tagSvc(constructionFuelTransportationRes.data, 'Fuel_Transportation_Construction_permit');

      let roleMap = {};
      let transfersMap = {};
      let archivedSet = new Set();
      const [svcRes, assignRes, archRes] = await Promise.all([
        api.get('/services').catch(() => ({ data: [] })),
        api.get('/request-assignments').catch(() => ({ data: [] })),
        api.get('/archive', { params: { limit: 10000 } }).catch(() => ({ data: {} }))
      ]);
      (svcRes.data || []).forEach(s => { if (s.type) { roleMap[s.type] = s.assigned_role || 'DeptHead'; roleMap[s.type + ':sector'] = s.sector || ''; } });
      (assignRes.data || []).forEach(a => {
        const key = a.service_type + ':' + a.request_id;
        if (a.assigned_role !== 'Customer' && !(key in transfersMap)) transfersMap[key] = { role: a.assigned_role, assigned_to: a.assigned_to };
      });
      if (archRes.data && archRes.data.data) {
        archRes.data.data.forEach(a => {
          const svcType = TABLE_TO_SERVICE_MAP[a.source_table];
          if (svcType) archivedSet.add(svcType + ':' + a.source_id);
        });
      }

      const visible = filterVisibleRequests(allRequests, {
        role: localStorage.getItem('role') || 'BackOfficer',
        sector: localStorage.getItem('sector') || '',
        roleMap, transfersMap, archivedSet,
        userServices: getUserServices(),
        userId: localStorage.getItem('userId')
      });
      const count = st => visible.filter(r => r.status === st).length;
      const total = visible.length;
      const pending = count('pending');
      const approved = count('approved');
      const rejected = count('rejected');
      const returned = count('returned');
      const payment_pending = count('payment_pending');
      const paid = count('paid');

      setStats({ total, pending, approved, rejected, returned, payment_pending, paid });
      const usersRes = await api.get('/auth/users').catch(() => ({ data: [] }));
      setCustomerCount(usersRes.data.filter(u => u.role === 'user').length);
    } catch (error) {
      toast.error('Failed to fetch statistics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-gray-500">Loading...</div></div>;
  }

  return (
    <div className="container mx-auto p-4 md:p-6">
      <h2 className="text-xl md:text-2xl font-bold mb-6">Dashboard</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-gray-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Total Requests</p>
              <p className="text-3xl font-bold">{stats.total || 0}</p>
            </div>
            <FaChartBar className="text-gray-400 text-3xl" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Pending</p>
              <p className="text-3xl font-bold">{stats.pending || 0}</p>
            </div>
            <FaClock className="text-yellow-500 text-3xl" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Approved</p>
              <p className="text-3xl font-bold">{stats.approved || 0}</p>
            </div>
            <FaCheck className="text-green-500 text-3xl" />
          </div>
        </div>
<div className="bg-white rounded-lg shadow p-6 border-l-4 border-red-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Rejected</p>
              <p className="text-3xl font-bold">{stats.rejected || 0}</p>
            </div>
            <FaTimes className="text-red-500 text-3xl" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-amber-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Returned</p>
              <p className="text-3xl font-bold">{stats.returned || 0}</p>
            </div>
            <FaUndo className="text-amber-500 text-3xl" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Payment Pending</p>
              <p className="text-3xl font-bold">{stats.payment_pending || 0}</p>
            </div>
            <FaClock className="text-purple-500 text-3xl" />
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-teal-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Paid</p>
              <p className="text-3xl font-bold">{stats.paid || 0}</p>
            </div>
            <FaCheckCircle className="text-teal-500 text-3xl" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 text-sm">Registered Customers</p>
              <p className="text-3xl font-bold">{customerCount || 0}</p>
            </div>
            <FaUsers className="text-blue-500 text-3xl" />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6">
        <h3 className="text-lg font-bold mb-4">Quick Overview</h3>
        <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
          <div className="flex h-4 rounded-full overflow-hidden">
            <div 
              className="bg-yellow-500" 
              style={{ width: `${stats.total ? (stats.pending / stats.total * 100) : 0}%` }}
            ></div>
            <div 
              className="bg-amber-500" 
              style={{ width: `${stats.total ? (stats.returned / stats.total * 100) : 0}%` }}
            ></div>
            <div 
              className="bg-purple-500" 
              style={{ width: `${stats.total ? (stats.payment_pending / stats.total * 100) : 0}%` }}
            ></div>
            <div 
              className="bg-teal-500" 
              style={{ width: `${stats.total ? (stats.paid / stats.total * 100) : 0}%` }}
            ></div>
            <div 
              className="bg-green-500" 
              style={{ width: `${stats.total ? (stats.approved / stats.total * 100) : 0}%` }}
            ></div>
            <div 
              className="bg-red-500" 
              style={{ width: `${stats.total ? (stats.rejected / stats.total * 100) : 0}%` }}
            ></div>
          </div>
        </div>
        <div className="flex gap-4 text-sm flex-wrap">
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-yellow-500 rounded"></span> Pending ({stats.pending || 0})</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-amber-500 rounded"></span> Returned ({stats.returned || 0})</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-purple-500 rounded"></span> Payment Pending ({stats.payment_pending || 0})</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-teal-500 rounded"></span> Paid ({stats.paid || 0})</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-green-500 rounded"></span> Approved ({stats.approved || 0})</span>
          <span className="flex items-center gap-1"><span className="w-3 h-3 bg-red-500 rounded"></span> Rejected ({stats.rejected || 0})</span>
        </div>
      </div>
    </div>
  );
}

function Services() {
  const [services, setServices] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [formData, setFormData] = useState({ name: '', name_amharic: '', type: '', status: true, assigned_role: 'DeptHead', sector: '' });

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await api.get('/services');
      setServices(response.data);
    } catch (error) {
      toast.error('Failed to fetch services');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingService) {
        await api.put(`/services/${editingService.id}`, formData);
        toast.success('Service updated successfully');
      } else {
        await api.post('/services', formData);
        toast.success('Service created successfully');
      }
      setShowModal(false);
      setEditingService(null);
      setFormData({ name: '', name_amharic: '', status: true, assigned_role: 'DeptHead', sector: '' });
      fetchServices();
    } catch (error) {
      toast.error('Failed to save service');
    }
  };

  const handleEdit = (service) => {
    setEditingService(service);
    setFormData({ name: service.name, name_amharic: service.name_amharic || '', type: service.type || '', status: service.status, assigned_role: service.assigned_role || 'DeptHead', sector: service.sector || '' });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this service?')) {
      try {
        await api.delete(`/services/${id}`);
        toast.success('Service deleted successfully');
        fetchServices();
      } catch (error) {
        toast.error('Failed to delete service');
      }
    }
  };

  return (
    <div className="container mx-auto p-4 md:p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl md:text-2xl font-bold">የምንስጣቸው አገልግሎቶች / Available Services</h2>
        <button
          onClick={() => { setShowModal(true); setEditingService(null); setFormData({ name: '', name_amharic: '', type: '', status: true, assigned_role: 'DeptHead', sector: '' }); }}
          className="bg-[#4CAF50] text-white px-4 py-2 rounded flex items-center gap-2 hover:bg-green-600"
        >
          <FaPlus /> Add Service
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto"><table className="min-w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">ስም (Amharic)</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Role</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sector</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {services.map((service) => (
              <tr key={service.id}>
                <td className="px-6 py-4">{service.id}</td>
                <td className="px-6 py-4 text-gray-600">{service.name_amharic}</td>
                <td className="px-6 py-4">{service.name}</td>
                <td className="px-6 py-4 text-gray-500 text-sm">{service.type}</td>
                <td className="px-6 py-4">
                  <span className={`px-2 py-1 rounded text-xs ${service.status ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                    {service.status ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-6 py-4">
                  <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">{service.assigned_role || 'DeptHead'}</span>
                </td>
                <td className="px-6 py-4">
                  <span className="px-2 py-1 rounded text-xs bg-purple-100 text-purple-800">{service.sector || 'N/A'}</span>
                </td>
                <td className="px-6 py-4 flex gap-2">
                  <button onClick={() => handleEdit(service)} className="text-blue-600 hover:text-blue-800">
                    <FaEdit />
                  </button>
                  <button onClick={() => handleDelete(service.id)} className="text-red-600 hover:text-red-800">
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {services.length === 0 && (
          <p className="p-6 text-center text-gray-500">No services found</p>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-xl font-bold mb-4">{editingService ? 'Edit Service' : 'Add Service'}</h3>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">የአገልግሎት ስም (Amharic)</label>
                <input
                  type="text"
                  value={formData.name_amharic}
                  onChange={(e) => setFormData({ ...formData, name_amharic: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Service Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Type (route key)</label>
                <input
                  type="text"
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Assigned Role</label>
                <select
                  value={formData.assigned_role}
                  onChange={(e) => setFormData({ ...formData, assigned_role: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                >
                <option value="Director">Director</option>
                <option value="DeputyDir">Deputy Director</option>
                <option value="DeptHead">Department Head</option>
                <option value="SuperAdmin">Super Admin</option>
                <option value="BackOfficer">Back Officer</option>

                  <option value="ITAdmin">IT Admin</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Sector</label>
                <select
                  value={formData.sector}
                  onChange={(e) => setFormData({ ...formData, sector: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                >
                  <option value="">-- No Sector --</option>
                  <option value="Petroleum competency certification">Petroleum competency certification</option>
                  <option value="Petroleum marketing, supply & distribution">Petroleum marketing, supply & distribution</option>
                  <option value="Energy investment licensing">Energy investment licensing</option>
                </select>
              </div>
              <div className="mb-4">
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="text-sm font-medium">Active</span>
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowModal(false)} className="px-4 py-2 border rounded hover:bg-gray-100">
                  Cancel
                </button>
                <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-600">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const getRequestDocuments = (request) => {
  const documents = [
    ...(request.payment_receipt ? [{ label: 'Payment Receipt', path: request.payment_receipt }] : []),
    { label: 'Application Letter', path: request.application_letter },
    { label: 'Support Letter', path: request.support_letter },
    { label: 'Agreement Letter', path: request.aggrement_letter },
    { label: 'Site Plan and Design Drawing', path: request.site_plan_drawing },
    { label: 'Approved Letter', path: request.approved_letter }
  ];
  let additional = request.additional_attachments || [];
  if (typeof additional === 'string') {
    try {
      additional = JSON.parse(additional);
    } catch (e) {
      additional = [];
    }
  }
  if (Array.isArray(additional)) {
    additional.forEach((attachment, index) => {
      const path = typeof attachment === 'string' ? attachment : attachment?.file || attachment?.path || attachment?.url;
      if (path) documents.push({ label: attachment?.name || `Additional Attachment ${index + 1}`, path });
    });
  }
  return documents.filter((document, index, all) => document.path && all.findIndex(item => item.path === document.path) === index).slice(0, 10);
};

const getPaymentReceiptDocuments = (request) => {
  const paymentReceipt = request?.payment_receipt;
  if (!paymentReceipt) return [];
  return [{ label: 'Payment Receipt', path: paymentReceipt }];
};

function DocumentViewerModal({ request, onClose, paymentOnly = false }) {
  const documents = useMemo(() => paymentOnly ? getPaymentReceiptDocuments(request) : getRequestDocuments(request), [request, paymentOnly]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-2 md:p-6">
      <div className="bg-white rounded-lg w-full max-w-6xl h-full max-h-[95vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between gap-3 px-4 py-3 border-b">
          <div className="min-w-0">
            <h3 className="font-bold truncate">{paymentOnly ? 'Payment Receipt' : 'Documents'} - {request.reference_no}</h3>
            <p className="text-xs text-gray-500">Preview only · {documents.length} document{documents.length === 1 ? '' : 's'}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800 text-xl" aria-label="Close document viewer">
            <FaTimes />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto bg-gray-100 p-3 space-y-4" onContextMenu={(event) => event.preventDefault()}>
          {documents.length === 0 ? (
            <p className="text-center text-gray-500 py-10">No documents uploaded</p>
          ) : (
            documents.map((doc, index) => (
                <div key={`${doc.path}-${index}`} className="bg-white rounded border overflow-hidden">
                  <div className="flex items-center justify-between gap-3 px-3 py-2 border-b bg-gray-50">
                    <span className="text-sm font-medium truncate">{index + 1}. {doc.label}</span>
                    <a href={fileUrl(doc.path)} target="_blank" rel="noopener noreferrer" className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700">
                      Download
                    </a>
                  </div>
                  <iframe
                    src={`${fileUrl(doc.path)}#toolbar=0&navpanes=0`}
                    title={doc.label}
                    className="w-full h-[75vh] bg-white"
                  />
                </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function ServiceRequests() {
  console.log('ServiceRequests MOUNTED');
  const [allRequests, setAllRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [documentViewerRequest, setDocumentViewerRequest] = useState(null);
  const [responseData, setResponseData] = useState({ status: '', response_reason: '', response_date: '', fuel_retail_price_from: '', fuel_retail_price_to: '', supplier_name: '' });
  const [approvedLetter, setApprovedLetter] = useState(null);
  const [generatingCert, setGeneratingCert] = useState(false);
  const currentRole = localStorage.getItem('role') || 'BackOfficer';
  const currentSector = localStorage.getItem('sector') || '';
  const CERT_SERVICE_TYPES = ['new-gas-station-retail', 'new-fuel-distributor', 'new-fuel-import', 'new-gas-import', 'new-oil-exporter', 'new-fuel-refinery', 'new-fuel-transportation'];
  const CONSTRUCTION_CERT_SERVICE_TYPES = ['Fuel_Station_Retail_Construction_Permit', 'Fuel_Distributor_Construction_Permit', 'Fuel_Importer_ Construction_Permit', 'Gas_Importer_Construction_Permit', 'Fuel_Exporter_Construction_permit', 'Fuel_Refining_Construction_permit', 'Fuel_Transportation_Construction_permit', 'gas-station-retail-construction', 'fuel-distributor-construction', 'fuel-import-construction', 'gas-import-construction', 'oil-exporter-construction', 'fuel-refinery-construction', 'fuel-transportation-construction'];
  const isDirectFuelBackOfficer = currentRole === 'BackOfficer' && selectedRequest?.serviceType === 'direct-fuel';
  const LETTER_APPROVAL_SERVICES = ['direct-fuel', 'gas-station-transfer', 'diversion-license', 'operator-change-license', ...CERT_SERVICE_TYPES, ...CONSTRUCTION_CERT_SERVICE_TYPES];
  const deptHeadApproved = !!selectedRequest && (selectedRequest.status === 'approved' || selectedRequest.dept_head_approved === true);
  const printBlocked = currentRole === 'BackOfficer' && LETTER_APPROVAL_SERVICES.includes(selectedRequest?.serviceType) && !deptHeadApproved;
  const isBackOfficerRequestWorkflow = isDirectFuelBackOfficer || (
    currentRole === 'BackOfficer' &&
    ['gas-station-transfer', 'diversion-license', 'operator-change-license', ...CERT_SERVICE_TYPES, ...CONSTRUCTION_CERT_SERVICE_TYPES].includes(selectedRequest?.serviceType)
  );
  const hasIdPhotoPlaceholder = ['gas-station-transfer', 'diversion-license', 'operator-change-license'].includes(selectedRequest?.serviceType);
  const [servicesRoleMap, setServicesRoleMap] = useState({});
  const [backOfficers, setBackOfficers] = useState([]);
  const [teamLeaders, setTeamLeaders] = useState([]);
  const [usersById, setUsersById] = useState({});
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedBackOfficer, setSelectedBackOfficer] = useState('');
  const [transferMessage, setTransferMessage] = useState('');
  const [transferTargetRole, setTransferTargetRole] = useState('');
  const [showReturnCustomerModal, setShowReturnCustomerModal] = useState(false);
  const [showReturnDeptModal, setShowReturnDeptModal] = useState(false);
  const [returnCustomerReason, setReturnCustomerReason] = useState('');
  const [showDirectFuelApprovalAction, setShowDirectFuelApprovalAction] = useState(false);
  const [showDirectFuelCustomerActions, setShowDirectFuelCustomerActions] = useState(false);
  const [showDirectFuelDeptActions, setShowDirectFuelDeptActions] = useState(false);
  const [showDirectFuelPaymentForm, setShowDirectFuelPaymentForm] = useState(false);
  const [showDirectFuelCorrectionForm, setShowDirectFuelCorrectionForm] = useState(false);
  const [showDirectFuelDeptApprovalForm, setShowDirectFuelDeptApprovalForm] = useState(false);
  const [showDirectFuelDeptCommentForm, setShowDirectFuelDeptCommentForm] = useState(false);
  const [directFuelPaymentInstructions, setDirectFuelPaymentInstructions] = useState('');
  const [directFuelDeptApprovalNote, setDirectFuelDeptApprovalNote] = useState('');
  const [directFuelDeptCommentNote, setDirectFuelDeptCommentNote] = useState('');
  const [clientLetterFile, setClientLetterFile] = useState(null);
  const [clientLetterSending, setClientLetterSending] = useState(false);
  const [clientLetterEmail, setClientLetterEmail] = useState('');
  const [clientLetterEmailSending, setClientLetterEmailSending] = useState(false);
  const [workflowHistory, setWorkflowHistory] = useState([]);
  const [archiving, setArchiving] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [listFilter, setListFilter] = useState('all');

  const TOP_MANAGEMENT_ROLES = ['Director', 'DeputyDir', 'deputydirector', 'SuperAdmin', 'admin'];
  const isTopRole = TOP_MANAGEMENT_ROLES.includes(currentRole);
  // Workflow actions available to the current role
  const canTransferForward = ['DeptHead', ...TOP_MANAGEMENT_ROLES].includes(currentRole);
  const canTransferToTL = ['DeptHead', ...TOP_MANAGEMENT_ROLES].includes(currentRole);
  const canTransferToBO = ['DeptHead', 'TeamLeader', ...TOP_MANAGEMENT_ROLES].includes(currentRole);
  const canReturnToDeptHead = currentRole === 'TeamLeader' || currentRole === 'BackOfficer';
  const canReturnToCustomer = ['TeamLeader', 'BackOfficer', 'DeptHead', ...TOP_MANAGEMENT_ROLES].includes(currentRole);
  const canApproveAsDeptHead = ['DeptHead', 'TeamLeader', ...TOP_MANAGEMENT_ROLES].includes(currentRole);
  const canSendClientLetter = ['direct-fuel', 'gas-station-transfer', 'diversion-license', 'operator-change-license', ...CERT_SERVICE_TYPES, ...CONSTRUCTION_CERT_SERVICE_TYPES].includes(selectedRequest?.serviceType) && ['BackOfficer', 'TeamLeader', 'DeptHead', ...TOP_MANAGEMENT_ROLES].includes(currentRole);
  const requestSector = (selectedRequest ? servicesRoleMap[`${selectedRequest.serviceType}:sector`] : '').trim();
  const requestServiceType = selectedRequest?.serviceType || '';
  const userHandlesService = (user, serviceType) => {
    const owned = Array.isArray(user.services) ? user.services : [];
    if (serviceType === 'direct-fuel' || serviceType === 'direct-fuel-beneficiary') return owned.includes('direct-fuel') || owned.includes('direct-fuel-beneficiary');
    return owned.includes(serviceType);
  };
  const getEligibleTransferUsers = (role) => {
    const users = role === 'TeamLeader' ? teamLeaders : backOfficers;
    if (!requestServiceType) return users;
    if (role === 'BackOfficer') return users.filter(user => userHandlesService(user, requestServiceType));
    if (!requestSector || requestSector.toLowerCase() === 'both') return users;
    return users.filter(user => {
      const userSector = (user.sector || '').trim().toLowerCase();
      return userSector === requestSector.toLowerCase() || userSector === 'both';
    });
  };
  const transferCandidates = getEligibleTransferUsers(transferTargetRole);
  const transferCandidateGroups = transferCandidates.reduce((groups, user) => {
    const key = (user.sector || '').trim() || 'No sector';
    (groups[key] ||= []).push(user);
    return groups;
  }, {});

  const NO_FUEL_PRICE_TYPES = [...CERT_SERVICE_TYPES, ...CONSTRUCTION_CERT_SERVICE_TYPES];

  const handleGenerateCertificate = async () => {
    if (!selectedRequest) return;
    setGeneratingCert(true);
    try {
      const isConstruction = CONSTRUCTION_CERT_SERVICE_TYPES.includes(selectedRequest.serviceType);
      const endpoint = isConstruction ? '/construction-certificates/generate' : '/certificates/generate';
      const body = isConstruction
        ? { service_type: selectedRequest.serviceType, request_id: selectedRequest.id }
        : { service_type: selectedRequest.serviceType, request_id: selectedRequest.id, official_name: '' };
      const res = await api.post(endpoint, body);
      toast.success('Certificate generated successfully');
      const updated = { ...selectedRequest, certificate_path: res.data.certificate_url, work_completed: true };
      setSelectedRequest(updated);
      const reqs = allRequests.map(r => r.id === updated.id && r.serviceType === updated.serviceType ? updated : r);
      setAllRequests(reqs);
    } catch (err) {
      toast.error('Failed to generate certificate: ' + (err.response?.data?.error || err.message));
    } finally {
      setGeneratingCert(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchRequests();
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/auth/users');
      const currentUserSector = localStorage.getItem('sector') || '';
      const rank = (u) => (currentUserSector && currentUserSector !== 'both' && u.sector === currentUserSector ? 0 : 1);
      setBackOfficers(res.data.filter(u => u.role === 'BackOfficer').sort((a, b) => rank(a) - rank(b)));
      setTeamLeaders(res.data.filter(u => u.role === 'TeamLeader').sort((a, b) => rank(a) - rank(b)));
      const byId = {};
      res.data.forEach(u => { byId[String(u.id)] = u; });
      setUsersById(byId);
    } catch (error) {
      console.error('Failed to fetch users');
    }
  };

  const fetchRequests = async () => {
    try {
      setLoadingRequests(true);
      const [directFuelRes, gsTransferRes, diversionRes, operatorChangeRes, nfcRes, fuelRenewalRes, fuelTransferRes, newGasStationRes, gsRenewalRes, gsConnectionTransferRes, depotFuelCargoRes, depotFuelCargoRenewalRes, depotFuelCargoTransferRes, newGasStationRetailRes, newFuelDistributorRes, newFuelImportRes, newGasImportRes, newOilExporterRes, newFuelRefineryRes, newFuelTransportationRes, gsRetailRenewalRes, fuelDistributorRenewalRes, fuelImportRenewalRes, gasImportRenewalRes, oilExporterRenewalRes, fuelRefineryRenewalRes, fuelTransportationRenewalRes, fuelOrderCancellationRes, fuelOrderCancellationServiceRes, vehicleBreakdownReportingRes, fuelDeliveryExtensionRes, electricVehicleChargingRes, fuelTransportationConstructionRes, gasStationRetailConstructionRes, fuelDistributorConstructionRes, fuelImportConstructionRes, gasImportConstructionRes, oilExporterConstructionRes, fuelRefineryConstructionRes] = await Promise.all([
        api.get('/direct-fuel-beneficiary').catch(() => ({ data: [] })),
        api.get('/gas-station-transfer').catch(() => ({ data: [] })),
        api.get('/diversion-license').catch(() => ({ data: [] })),
        api.get('/operator-change-license').catch(() => ({ data: [] })),
        api.get('/new-fuel-company').catch(() => ({ data: [] })),
        api.get('/fuel-company-renewal').catch(() => ({ data: [] })),
        api.get('/fuel-company-transfer').catch(() => ({ data: [] })),
        api.get('/new-gas-station').catch(() => ({ data: [] })),
        api.get('/gas-station-renewal').catch(() => ({ data: [] })),
        api.get('/gas-station-connection-transfer').catch(() => ({ data: [] })),
        api.get('/new-depot-fuel-cargo').catch(() => ({ data: [] })),
        api.get('/depot-fuel-cargo-renewal').catch(() => ({ data: [] })),
        api.get('/depot-fuel-cargo-transfer').catch(() => ({ data: [] })),
        api.get('/new-gas-station-retail').catch(() => ({ data: [] })),
        api.get('/new-fuel-distributor').catch(() => ({ data: [] })),
        api.get('/new-fuel-import').catch(() => ({ data: [] })),
        api.get('/new-gas-import').catch(() => ({ data: [] })),
        api.get('/new-oil-exporter').catch(() => ({ data: [] })),
        api.get('/new-fuel-refinery').catch(() => ({ data: [] })),
        api.get('/new-fuel-transportation').catch(() => ({ data: [] })),
        api.get('/gas-station-retail-renewal').catch(() => ({ data: [] })),
        api.get('/fuel-distributor-renewal').catch(() => ({ data: [] })),
        api.get('/fuel-import-renewal').catch(() => ({ data: [] })),
        api.get('/gas-import-renewal').catch(() => ({ data: [] })),
        api.get('/oil-exporter-renewal').catch(() => ({ data: [] })),
        api.get('/fuel-refinery-renewal').catch(() => ({ data: [] })),
        api.get('/fuel-transportation-renewal').catch(() => ({ data: [] })),
        api.get('/fuel-order-cancellation').catch(() => ({ data: [] })),
        api.get('/fuel-order-cancellation-service').catch(() => ({ data: [] })),
        api.get('/vehicle-breakdown-reporting').catch(() => ({ data: [] })),
        api.get('/fuel-delivery-extension').catch(() => ({ data: [] })),
        api.get('/electric-vehicle-charging').catch(() => ({ data: [] })),
        api.get('/fuel-transportation-construction').catch(() => ({ data: [] })),
        api.get('/gas-station-retail-construction').catch(() => ({ data: [] })),
        api.get('/fuel-distributor-construction').catch(() => ({ data: [] })),
        api.get('/fuel-import-construction').catch(() => ({ data: [] })),
        api.get('/gas-import-construction').catch(() => ({ data: [] })),
        api.get('/oil-exporter-construction').catch(() => ({ data: [] })),
        api.get('/fuel-refinery-construction').catch(() => ({ data: [] }))
      ]);

      directFuelRes.data.forEach(r => r.serviceType = 'direct-fuel');
      gsTransferRes.data.forEach(r => r.serviceType = 'gas-station-transfer');
      diversionRes.data.forEach(r => r.serviceType = 'diversion-license');
      operatorChangeRes.data.forEach(r => r.serviceType = 'operator-change-license');
      nfcRes.data.forEach(r => r.serviceType = 'new-fuel-company');
      fuelRenewalRes.data.forEach(r => r.serviceType = 'fuel-company-renewal');
      fuelTransferRes.data.forEach(r => r.serviceType = 'fuel-company-transfer');
      newGasStationRes.data.forEach(r => r.serviceType = 'new-gas-station');
      gsRenewalRes.data.forEach(r => r.serviceType = 'gas-station-renewal');
      gsConnectionTransferRes.data.forEach(r => r.serviceType = 'gas-station-connection-transfer');
      depotFuelCargoRes.data.forEach(r => r.serviceType = 'new-depot-fuel-cargo');
      depotFuelCargoRenewalRes.data.forEach(r => r.serviceType = 'depot-fuel-cargo-renewal');
      depotFuelCargoTransferRes.data.forEach(r => r.serviceType = 'depot-fuel-cargo-transfer');
      newGasStationRetailRes.data.forEach(r => r.serviceType = 'new-gas-station-retail');
      newFuelDistributorRes.data.forEach(r => r.serviceType = 'new-fuel-distributor');
      newFuelImportRes.data.forEach(r => r.serviceType = 'new-fuel-import');
      newGasImportRes.data.forEach(r => r.serviceType = 'new-gas-import');
      newOilExporterRes.data.forEach(r => r.serviceType = 'new-oil-exporter');
      newFuelRefineryRes.data.forEach(r => r.serviceType = 'new-fuel-refinery');
      newFuelTransportationRes.data.forEach(r => r.serviceType = 'new-fuel-transportation');
      gsRetailRenewalRes.data.forEach(r => r.serviceType = 'gas-station-retail-renewal');
      fuelDistributorRenewalRes.data.forEach(r => r.serviceType = 'fuel-distributor-renewal');
      fuelImportRenewalRes.data.forEach(r => r.serviceType = 'fuel-import-renewal');
      gasImportRenewalRes.data.forEach(r => r.serviceType = 'gas-import-renewal');
      oilExporterRenewalRes.data.forEach(r => r.serviceType = 'oil-exporter-renewal');
      fuelRefineryRenewalRes.data.forEach(r => r.serviceType = 'fuel-refinery-renewal');
      fuelTransportationRenewalRes.data.forEach(r => r.serviceType = 'fuel-transportation-renewal');
      fuelOrderCancellationRes.data.forEach(r => r.serviceType = 'fuel-order-cancellation');
      fuelOrderCancellationServiceRes.data.forEach(r => r.serviceType = 'fuel-order-cancellation-service');
      vehicleBreakdownReportingRes.data.forEach(r => r.serviceType = 'vehicle-breakdown-reporting');
      fuelDeliveryExtensionRes.data.forEach(r => r.serviceType = 'fuel-delivery-extension');
      electricVehicleChargingRes.data.forEach(r => r.serviceType = 'electric-vehicle-charging');
      fuelTransportationConstructionRes.data.forEach(r => r.serviceType = 'Fuel_Transportation_Construction_permit');
      gasStationRetailConstructionRes.data.forEach(r => r.serviceType = 'Fuel_Station_Retail_Construction_Permit');
      fuelDistributorConstructionRes.data.forEach(r => r.serviceType = 'Fuel_Distributor_Construction_Permit');
      fuelImportConstructionRes.data.forEach(r => r.serviceType = 'Fuel_Importer_ Construction_Permit');
      gasImportConstructionRes.data.forEach(r => r.serviceType = 'Gas_Importer_Construction_Permit');
      oilExporterConstructionRes.data.forEach(r => r.serviceType = 'Fuel_Exporter_Construction_permit');
      fuelRefineryConstructionRes.data.forEach(r => r.serviceType = 'Fuel_Refining_Construction_permit');

      const combined = [...directFuelRes.data, ...gsTransferRes.data, ...diversionRes.data, ...operatorChangeRes.data, ...nfcRes.data, ...fuelRenewalRes.data, ...fuelTransferRes.data, ...newGasStationRes.data, ...gsRenewalRes.data, ...gsConnectionTransferRes.data, ...depotFuelCargoRes.data, ...depotFuelCargoRenewalRes.data, ...depotFuelCargoTransferRes.data, ...newGasStationRetailRes.data, ...newFuelDistributorRes.data, ...newFuelImportRes.data, ...newGasImportRes.data, ...newOilExporterRes.data, ...newFuelRefineryRes.data, ...newFuelTransportationRes.data, ...gsRetailRenewalRes.data, ...fuelDistributorRenewalRes.data, ...fuelImportRenewalRes.data, ...gasImportRenewalRes.data, ...oilExporterRenewalRes.data, ...fuelRefineryRenewalRes.data, ...fuelTransportationRenewalRes.data, ...fuelOrderCancellationRes.data, ...fuelOrderCancellationServiceRes.data, ...vehicleBreakdownReportingRes.data, ...fuelDeliveryExtensionRes.data, ...electricVehicleChargingRes.data, ...fuelTransportationConstructionRes.data, ...gasStationRetailConstructionRes.data, ...fuelDistributorConstructionRes.data, ...fuelImportConstructionRes.data, ...gasImportConstructionRes.data, ...oilExporterConstructionRes.data, ...fuelRefineryConstructionRes.data];

      let roleMap = servicesRoleMap;
      try {
        const svcRes = await api.get('/services');
        const map = {};
        svcRes.data.forEach(s => { if (s.type) { map[s.type] = s.assigned_role || 'DeptHead'; map[s.type + ':sector'] = s.sector || ''; } });
        setServicesRoleMap(map);
        roleMap = map;
      } catch (e) {}

let transfersMap = {};
      let customerResponseMap = {};
      try {
        const assignRes = await api.get('/request-assignments');
          assignRes.data.forEach(a => {
            const key = a.service_type + ':' + a.request_id;
            if (a.assigned_role === 'Customer') {
              if (a.transfer_message) {
                const match = /^\[(Payment|Correction) Request\]\s*/.exec(a.transfer_message);
                if (!customerResponseMap[key]) customerResponseMap[key] = [];
                customerResponseMap[key].push({
                  type: match ? match[1] : '',
                  message: match ? a.transfer_message.slice(match[0].length) : a.transfer_message,
                  by_name: a.assigned_by_name || '',
                  at: a.transferred_at || ''
                });
              }
              return;
            }
            if (!(key in transfersMap)) {
              transfersMap[key] = {
                role: a.assigned_role,
                assigned_to: a.assigned_to,
                assigned_to_name: a.assigned_to_name || '',
                assigned_by_name: a.assigned_by_name || '',
                transfer_message: a.transfer_message || ''
              };
            }
          });
      } catch (e) {}

      let archivedSet = new Set();
      try {
        const archRes = await api.get('/archive', { params: { limit: 10000 } });
        if (archRes.data && archRes.data.data) {
          archRes.data.data.forEach(a => {
            const svcType = TABLE_TO_SERVICE_MAP[a.source_table];
            if (svcType) {
              archivedSet.add(svcType + ':' + a.source_id);
            }
          });
        }
      } catch (e) {}

      const filtered = filterVisibleRequests(combined, { role: currentRole, sector: currentSector, roleMap, transfersMap, archivedSet, userServices: getUserServices(), userId: localStorage.getItem('userId') });

      filtered.forEach(r => {
        const t = transfersMap[r.serviceType + ':' + r.id];
        if (t) {
          r.assignedToName = t.assigned_to_name;
          r.assignedByName = t.assigned_by_name;
          r.transferMessage = t.transfer_message;
          r.assignedTo = t.assigned_to;
          r.assignedRole = t.role;
        }
        const cr = customerResponseMap[r.serviceType + ':' + r.id];
        const customerResponses = cr ? [...cr] : [];
        if (r.response_reason && (r.status === 'payment_pending' || r.status === 'returned')) {
          const currentType = r.status === 'payment_pending' ? 'Payment' : 'Correction';
          const matchingResponse = customerResponses.find(response => response.message === r.response_reason);
          if (matchingResponse) {
            if (!matchingResponse.type) matchingResponse.type = currentType;
          } else {
            customerResponses.push({ message: r.response_reason, type: currentType });
          }
        }
        if (customerResponses.length > 0) r.customerResponses = customerResponses;
        Object.assign(r, getQueueFlags(r));
      });

      filtered.sort((a, b) => {
        const dateA = a.created_at || a.id;
        const dateB = b.created_at || b.id;
        return dateA < dateB ? 1 : dateA > dateB ? -1 : 0;
      });
      setAllRequests(filtered);
      setLoadingRequests(false);
    } catch (error) {
      setLoadingRequests(false);
      toast.error('Failed to fetch requests');
    }
  };

  const submitRequestResponse = async (status, responseReason) => {
    if (!selectedRequest) return null;
    const formData = new FormData();
    formData.append('status', status);
    formData.append('response_reason', responseReason);
    if (responseData.response_date) {
      formData.append('response_date', responseData.response_date);
    }
    if (responseData.fuel_retail_price_from) {
      formData.append('fuel_retail_price_from', responseData.fuel_retail_price_from);
    }
    if (responseData.fuel_retail_price_to) {
      formData.append('fuel_retail_price_to', responseData.fuel_retail_price_to);
    }
    if (responseData.supplier_name) {
      formData.append('supplier_name', responseData.supplier_name);
    }
    if (selectedRequest.existing_approved_letter) {
      formData.append('existing_approved_letter', selectedRequest.existing_approved_letter);
    }
    if (approvedLetter) {
      formData.append('approved_letter', approvedLetter);
    }

    const response = await api.put(`/respond/${selectedRequest.serviceType}/${selectedRequest.id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    const newStatus = response.data.status || status;
    const updatedRequest = {
      ...selectedRequest,
      ...response.data,
      status: newStatus,
      response_reason: response.data.response_reason || responseReason
    };
    setSelectedRequest(updatedRequest);
    setResponseData((current) => ({ ...current, status: newStatus, response_reason: '' }));
    setApprovedLetter(null);
    await fetchRequests();
    return updatedRequest;
  };

  const handleRespond = async (e) => {
    e.preventDefault();
    try {
      console.log('Submitting response for:', selectedRequest.id, 'status:', responseData.status);
      await submitRequestResponse(responseData.status, responseData.response_reason);
      toast.success('Response submitted successfully');
    } catch (error) {
      const errMsg = error.response?.data?.error || error.message;
      console.error('Failed to submit response:', errMsg);
      toast.error('Failed to submit response: ' + errMsg);
    }
  };

  const handleDirectFuelCustomerActions = async () => {
    const addPaymentRequest = showDirectFuelPaymentForm;
    const returnForCorrection = showDirectFuelCorrectionForm;
    if (!addPaymentRequest && !returnForCorrection) {
      toast.error('Select a payment request or correction action');
      return;
    }
    if (addPaymentRequest && !directFuelPaymentInstructions.trim()) {
      toast.error('Please enter the payment instructions');
      return;
    }
    if (returnForCorrection && !returnCustomerReason.trim()) {
      toast.error('Please describe the problem the customer must correct');
      return;
    }
    try {
      if (addPaymentRequest) {
        await submitRequestResponse('payment_pending', directFuelPaymentInstructions);
      }
      if (returnForCorrection) {
        await api.post('/request-assignments/return-to-customer', {
          service_type: selectedRequest.serviceType,
          request_id: selectedRequest.id,
          reason: returnCustomerReason
        });
      }
      toast.success(returnForCorrection
        ? addPaymentRequest ? 'Payment requested and request returned for correction' : 'Request returned to customer for correction'
        : 'Payment request submitted');
      setShowDirectFuelPaymentForm(false);
      setShowDirectFuelCorrectionForm(false);
      setShowDirectFuelCustomerActions(false);
      setDirectFuelPaymentInstructions('');
      setReturnCustomerReason('');
      fetchRequests();
    } catch (error) {
      toast.error('Failed to return request to customer: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleBackOfficerApprovalLetter = async () => {
    if (!deptHeadApproved) {
      toast.error('The Department Head must approve this request before generating the approval letter');
      return;
    }
    if (NO_FUEL_PRICE_TYPES.includes(selectedRequest?.serviceType)) {
      await handleGenerateCertificate();
      return;
    }
    await generateApprovalLetter(selectedRequest);
  };

  const handleDeptHeadQuickApprove = async () => {
    if (!selectedRequest) return;
    if (!canApproveAsDeptHead) {
      toast.error('Only the Department Head or Team Leader can approve this request');
      return;
    }
    try {
      await submitRequestResponse('approved', 'Approved by Department Head');
      toast.success('Request approved');
    } catch (error) {
      toast.error('Failed to approve request: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSendClientLetter = async () => {
    if (!selectedRequest || !clientLetterFile) {
      toast.error('Please choose a letter file to attach');
      return;
    }
    setClientLetterSending(true);
    try {
      const fd = new FormData();
      fd.append('letter', clientLetterFile);
      const res = await api.post(`/client-letter/${selectedRequest.serviceType}/${selectedRequest.id}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Letter attached and sent to the client');
      setClientLetterFile(null);
      setSelectedRequest((cur) => cur ? {
        ...cur,
        client_letter: res.data.client_letter,
        client_letter_name: res.data.client_letter_name,
        client_letter_sent_at: res.data.client_letter_sent_at
      } : cur);
      fetchRequests();
    } catch (error) {
      toast.error('Failed to attach letter: ' + (error.response?.data?.error || error.message));
    } finally {
      setClientLetterSending(false);
    }
  };

  const handleEmailClientLetter = async () => {
    if (!selectedRequest) return;
    const to = (clientLetterEmail || selectedRequest.email || '').trim();
    if (!to) {
      toast.error('Enter the recipient email address');
      return;
    }
    setClientLetterEmailSending(true);
    try {
      await api.post(`/email-letter/${selectedRequest.serviceType}/${selectedRequest.id}`, {
        to,
        subject: `PEA Service - Letter for application ${selectedRequest.reference_no || ''}`,
        message: 'Please find the attached letter regarding your application.'
      });
      toast.success(`Letter emailed to ${to}`);
    } catch (error) {
      toast.error('Failed to send email: ' + (error.response?.data?.error || error.message));
    } finally {
      setClientLetterEmailSending(false);
    }
  };

  const handleVerifyPaymentReceipt = async () => {
    if (!selectedRequest || !selectedRequest.payment_receipt) {
      toast.error('No payment receipt available to verify');
      return;
    }
    try {
      const res = await api.post(`/verify-payment/${selectedRequest.serviceType}/${selectedRequest.id}`);
      const updatedRequest = { ...selectedRequest, ...res.data, status: res.data.status || selectedRequest.status };
      setSelectedRequest(updatedRequest);
      setAllRequests((current) => current.map(req => req.id === updatedRequest.id && req.serviceType === updatedRequest.serviceType ? updatedRequest : req));
      fetchRequests();
      toast.success('Payment receipt verified and request marked as paid');
    } catch (error) {
      toast.error('Failed to verify payment receipt: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDirectFuelReturnToDeptHead = async (action, note) => {
    if (action === 'comment' && !note.trim()) {
      toast.error('Please enter a comment or review note');
      return;
    }
    try {
      const approvalMessage = action === 'approval'
        ? 'For Approval: Department Head approval required'
        : 'For Comment / Review';

      await api.post('/request-assignments/transfer', {
        service_type: selectedRequest.serviceType,
        request_id: selectedRequest.id,
        assigned_role: 'DeptHead',
        assigned_to: null,
        transfer_message: `${approvalMessage}${note.trim() ? `: ${note.trim()}` : ''}`
      });
      toast.success(action === 'approval' ? 'Request returned to Department Head for approval' : 'Request returned to Department Head for comment / review');
      setShowDirectFuelDeptActions(false);
      setShowDirectFuelDeptApprovalForm(false);
      setShowDirectFuelDeptCommentForm(false);
      setDirectFuelDeptApprovalNote('');
      setDirectFuelDeptCommentNote('');
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      toast.error('Failed to return request: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleOpenDetails = async (req) => {
    setSelectedRequest(req);
    setResponseData({ status: req.status || 'pending', response_reason: req.response_reason || '', response_date: '', fuel_retail_price_from: req.fuel_retail_price_from || '', fuel_retail_price_to: req.fuel_retail_price_to || '', supplier_name: req.supplier_name || '' });
    setShowDirectFuelApprovalAction(false);
    setShowDirectFuelCustomerActions(false);
    setShowDirectFuelDeptActions(false);
    setShowDirectFuelPaymentForm(false);
    setShowDirectFuelCorrectionForm(false);
    setShowDirectFuelDeptApprovalForm(false);
    setShowDirectFuelDeptCommentForm(false);
    setDirectFuelPaymentInstructions('');
    setDirectFuelDeptApprovalNote('');
    setDirectFuelDeptCommentNote('');
    setClientLetterFile(null);
    setClientLetterEmail(req.email || '');
    setReturnCustomerReason('');
    try {
      const res = await api.get(`/request-assignments/history/${req.serviceType}/${req.id}`);
      setWorkflowHistory(res.data || []);
    } catch (e) {
      setWorkflowHistory([]);
    }
  };

  const handleReturnToCustomer = async () => {
    if (!returnCustomerReason.trim()) { toast.error('Please describe the problem the customer must correct'); return; }
    try {
      await api.post('/request-assignments/return-to-customer', {
        service_type: selectedRequest.serviceType,
        request_id: selectedRequest.id,
        reason: returnCustomerReason
      });
      toast.success('Request returned to customer for correction');
      setShowReturnCustomerModal(false);
      setShowDirectFuelCustomerActions(false);
      setShowDirectFuelCorrectionForm(false);
      setReturnCustomerReason('');
      setSelectedRequest(null);
      fetchRequests();
    } catch (error) {
      toast.error('Failed to return request: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDownloadPDF = (item) => {
    generatePDF(item);
  };

  const handleArchive = async (req) => {
    if (!window.confirm(`Archive ${req.reference_no || 'this record'}? This will move it to the archive.`)) return;
    setArchiving(req.id);
    try {
      await api.post(`/archive/${req.serviceType}/${req.id}`);
      toast.success('Archived successfully');
      fetchRequests();
    } catch (e) {
      toast.error('Failed to archive: ' + (e.response?.data?.error || e.message));
    }
    setArchiving(null);
  };

  const handleDeleteRequest = async (req) => {
    if (!window.confirm(`Delete ${req.reference_no || 'this record'}? This will permanently remove it from the application and database.`)) return;
    setDeleting(req.id);
    try {
      await api.delete(`/request/${req.serviceType}/${req.id}`);
      toast.success('Request deleted successfully');
      fetchRequests();
    } catch (e) {
      toast.error('Failed to delete: ' + (e.response?.data?.error || e.message));
    }
    setDeleting(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved': return 'bg-green-500 text-white font-bold';
      case 'rejected': return 'bg-red-500 text-white font-bold';
      case 'returned': return 'bg-amber-500 text-white font-bold';
      case 'payment_pending': return 'bg-purple-500 text-white font-bold';
      case 'paid': return 'bg-teal-500 text-white font-bold';
      default: return 'bg-yellow-500 text-black font-bold';
    }
  };

  const queueCounts = {
    deptApproval: allRequests.filter(r => r.awaitingDeptHeadApproval).length,
    boReview: allRequests.filter(r => r.awaitingBackOfficeReview).length,
  };
  const showDeptApprovalChip = isTopRole || currentRole === 'DeptHead';
  const showBOReviewChip = isTopRole || currentRole === 'BackOfficer' || currentRole === 'TeamLeader';
  const activeFilter =
    (listFilter === 'deptApproval' && showDeptApprovalChip) ? 'deptApproval' :
    (listFilter === 'boReview' && showBOReviewChip) ? 'boReview' : 'all';
  const visibleRequests = allRequests.filter((r) => {
    if (activeFilter === 'deptApproval') return !!r.awaitingDeptHeadApproval;
    if (activeFilter === 'boReview') return !!r.awaitingBackOfficeReview;
    return true;
  });
  const selFlags = selectedRequest
    ? getQueueFlags(selectedRequest)
    : { awaitingDeptHeadApproval: false, awaitingBackOfficeReview: false };
  const chipClass = (active) =>
    `px-3 py-1.5 rounded-full text-xs font-bold border ${active
      ? 'bg-[#006400] text-white border-[#006400]'
      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'}`;

  return (
    <div className="container mx-auto p-4 md:p-6">
      <h2 className="text-xl md:text-2xl font-bold mb-4">የተጠየቁ አገልግሎቶች / Service Requests {loadingRequests ? '' : `(${visibleRequests.length})`}</h2>

      <div className="flex flex-wrap gap-2 mb-4">
        <button type="button" onClick={() => setListFilter('all')} className={chipClass(activeFilter === 'all')}>
          All ({allRequests.length})
        </button>
        {showDeptApprovalChip && (
          <button type="button" onClick={() => setListFilter('deptApproval')} className={chipClass(activeFilter === 'deptApproval')}>
            Awaiting Department Head approval ({queueCounts.deptApproval})
          </button>
        )}
        {showBOReviewChip && (
          <button type="button" onClick={() => setListFilter('boReview')} className={chipClass(activeFilter === 'boReview')}>
            Awaiting back-office action ({queueCounts.boReview})
          </button>
        )}
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto"><table className="min-w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference No</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service Type</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applicant/Organization</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Request Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Response Reason</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Response to</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Documents</th>
              {['Director', 'DeputyDir', 'DeptHead', 'SuperAdmin'].includes(currentRole) && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned To</th>
              )}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {visibleRequests.map((req) => (
              <tr key={`${req.serviceType}-${req.id}`}>
                <td className="px-4 py-3 font-mono text-sm">{req.reference_no}</td>
                <td className="px-4 py-3">
                   {req.serviceType === 'gas-station-transfer' ? 'የነዳጅ ማደያ ወደ ሌላ የነዳጅ አከፋፋይ ኩባንያ የዝውውር ትስስር ፈቃድ አገልግሎት / Service for existing gas station transfer from on company to the other' : 
                   req.serviceType === 'diversion-license' ? 'የዳይቨርሽን ፈቃድ አገልግሎት / Service for diversion permit' : 
                   req.serviceType === 'operator-change-license' ? 'የነዳጅ ማደያ የኦፕረተር/ዲለር ለውጥ ትስስር ፈቃድ አገልግሎት / Service for operator/dealer change permit' :
                   req.serviceType === 'new-fuel-company' ? 'New Fuel Distribution Company Connection License Service (አዲስ የነዳጅ አከፋፋይ ኩባንያ ትስስር ፈቃድ አገልግሎት)' :
                   req.serviceType === 'fuel-company-renewal' ? 'Fuel Distribution Company Connection License Renewal Service (የነዳጅ አከፋፋይ ኩባንያ ትስስር ፈቃድ እድሳት አገልግሎት)' :
                   req.serviceType === 'fuel-company-transfer' ? 'Fuel Distribution Company Connection License Transfer Service (የነዳጅ አከፋፋይ ኩባንያ ትስስር ፈቃድ ዝውውር አገልግሎት)' :
                   req.serviceType === 'new-gas-station' ? 'New Gas Station Connection License Service (አዲስ የነዳጅ ማደያ ትስስር ፈቃድ አገልግሎት)' :
                    req.serviceType === 'gas-station-renewal' ? 'Service for the renewal of Gas Station Permit (የነዳጅ ማደያ ትስስር ፈቃድ እድሳት አገልግሎት)' :
                    req.serviceType === 'gas-station-connection-transfer' ? 'Service for the transfer of Gas Station Permit (የነዳጅ ማደያ ትስስር ፈቃድ ዝውውር አገልግሎት)' :
                    req.serviceType === 'new-depot-fuel-cargo' ? 'Service for New Fuel Loading Connection Permit from Depot (አዲስ ከዲፓ የነዳጅ ጭነት ትስስር ፈቃድ አገልግሎት)' :
                    req.serviceType === 'depot-fuel-cargo-renewal' ? 'Service for the Renewal of Fuel Loading Connection Permit from Depot (ከዲፓ የነዳጅ ጭነት ትስስር ፈቃድ እድሳት አገልግሎት)' :
                    req.serviceType === 'depot-fuel-cargo-transfer' ? 'Service for the transfer of Fuel Loading Connection Permit from Depot (ከዲፓ የነዳጅ ጭነት ትስስር ፈቃድ ዝውውር አገልግሎት)' :
                   req.serviceType === 'new-gas-station-retail' ? 'New Fuel Station Retail Competency and Licensing Service (አዲስ የነዳጅ ማደያ ችርቻሮ ብቃትና ፈቃድ አገልግሎት)' :
                   req.serviceType === 'new-fuel-distributor' ? 'New Fuel Distributor Competency and Licensing Service (አዲስ የነዳጅ አከፋፋይ ብቃትና ፈቃድ አገልግሎት)' :
                   req.serviceType === 'new-fuel-import' ? 'New Fuel Importer Competency and Licensing Service (አዲስ የነዳጅ አስመጪነት ብቃትና ፈቃድ አገልግሎት)' :
                   req.serviceType === 'new-gas-import' ? 'New Gas Importer Competency and Licensing Service (አዲስ የጋዝ አስመጪነት ብቃትና ፈቃድ አገልግሎት)' :
                   req.serviceType === 'new-oil-exporter' ? 'New Fuel Exporter Competency and Licensing Service (አዲስ የነዳጅ ላኪነት ብቃትና ፈቃድ አገልግሎት)' :
                   req.serviceType === 'new-fuel-refinery' ? 'New Fuel Refining Competency and Licensing Service (አዲስ የነዳጅ ማጣራት ብቃትና ፈቃድ አገልግሎት)' :
                   req.serviceType === 'new-fuel-transportation' ? 'New Fuel Transportation Competency and Licensing Service (አዲስ የነዳጅ ማጓጓዝ ብቃትና ፈቃድ አገልግሎት)' :
                    req.serviceType === 'gas-station-retail-renewal' ? 'Fuel Station Retail Competency and License Renewal Service (የነዳጅ ማደያ ችርቻሮ ብቃትና ፈቃድ እድሳት አገልግሎት)' :
                    req.serviceType === 'fuel-distributor-renewal' ? 'Fuel Distributor Competency and License Renewal Service (የነዳጅ አከፋፋይ ብቃትና ፈቃድ እድሳት አገልግሎት)' :
                    req.serviceType === 'fuel-import-renewal' ? 'Fuel Importer Competency and License Renewal Service (የነዳጅ አስመጪነት ብቃትና ፈቃድ እድሳት አገልግሎት)' :
                    req.serviceType === 'gas-import-renewal' ? 'Gas Importer Competency and License Renewal Service (የጋዝ አስመጪነት ብቃትና ፈቃድ እድሳት አገልግሎት)' :
                    req.serviceType === 'oil-exporter-renewal' ? 'Fuel Exporter Competency and License Renewal Service (የነዳጅ ላኪነት ብቃትና ፈቃድ እድሳት አገልግሎት)' :
                    req.serviceType === 'fuel-refinery-renewal' ? 'Fuel Refining Competency and License Renewal Service (የነዳጅ ማጣራት ብቃትና ፈቃድ እድሳት አገልግሎት)' :
                    req.serviceType === 'fuel-transportation-renewal' ? 'Fuel Transportation Competency and License Renewal Service (የነዳጅ ማጓጓዝ ብቃትና ፈቃድ እድሳት አገልግሎት)' :
                    req.serviceType === 'fuel-order-cancellation' ? 'የነዳጅ ጭነት ትእዛዝ ማቋረጥ' :
                    req.serviceType === 'fuel-order-cancellation-service' ? 'የነዳጅ ጭነት ትእዛዝ አገልግሎት ማቋረጥ / Fuel delivery order cancellation service' :
                    req.serviceType === 'vehicle-breakdown-reporting' ? 'ብልሽት የገጠመውን ተሸከርካሪ ማሳወቅ አገልግሎት / Vehicle Breakdown Reporting Service' :
                    req.serviceType === 'fuel-delivery-extension' ? 'የነዳጅ አቅርቦት ማራዘም ጥያቄ አገልግሎት / Fuel Delivery Extension Service' :
                    req.serviceType === 'electric-vehicle-charging' ? 'የኤሌክትሪክ ተሽከርካሪ ቻርጅ መሙሌት አገልግሎት / Electric Vehicle Charging Service License Request' :
                    req.serviceType === 'Fuel_Station_Retail_Construction_Permit' ? 'Fuel Station Retail Construction Permit Service (የነዳጅ ማደያ ችርቻሮ ኮንስትራክሽን ፈቃድ አገልግሎት)' :
                    req.serviceType === 'Fuel_Distributor_Construction_Permit' ? 'Fuel Distributor Construction Permit Service (የነዳጅ አከፋፋይ ኮንስትራክሽን ፈቃድ አገልግሎት)' :
                    req.serviceType === 'Fuel_Importer_ Construction_Permit' ? 'Fuel Importer Construction Permit Service (የነዳጅ አስመጪ ኮንስትራክሽን ፈቃድ አገልግሎት)' :
                    req.serviceType === 'Gas_Importer_Construction_Permit' ? 'Gas Importer Construction Permit Service (የጋዝ አስመጪ ኮንስትራክሽን ፈቃድ አገልግሎት)' :
                    req.serviceType === 'Fuel_Exporter_Construction_permit' ? 'Fuel Exporter Construction Permit Service (የነዳጅ ላኪ ኮንስትራክሽን ፈቃድ አገልግሎት)' :
                    req.serviceType === 'Fuel_Refining_Construction_permit' ? 'Fuel Refining Construction Permit Service (የነዳጅ ማጣሪያ ኮንስትራክሽን ፈቃድ አገልግሎት)' :
                    req.serviceType === 'Fuel_Transportation_Construction_permit' ? 'Fuel Transportation Construction Permit Service (የነዳጅ ማጓጓዣ ኮንስትራክሽን ፈቃድ አገልግሎት)' :
                   req.service_type || 'የነዳጅ ቀጥታ ተጠቃሚ ተቋማት ትስስር ፈቃድ አገልግሎት / Service for direct fuel beneficiary'}
                </td>
                <td className="px-4 py-3">
                  {req.serviceType === 'gas-station-transfer' ? req.nameofapplicant : 
                   req.serviceType === 'diversion-license' ? req.fuel_distributer_name : 
                   req.serviceType === 'operator-change-license' ? req.dealer_requester_name :
                   req.serviceType === 'new-fuel-company' ? (req.applicant_name || req.company_name) :
                   req.serviceType === 'fuel-company-renewal' ? (req.existing_ref_no || req.reference_no) :
                   req.serviceType === 'fuel-company-transfer' ? (req.transfer_from || req.reference_no) :
                   req.serviceType === 'new-gas-station' ? (req.applicant_name || req.gas_station_name) :
                   req.serviceType === 'gas-station-renewal' ? (req.existing_ref_no || req.reference_no) :
                   req.serviceType === 'gas-station-connection-transfer' ? (req.transfer_from || req.reference_no) :
                   req.serviceType === 'new-depot-fuel-cargo' ? (req.applicant_name || req.depot_name) :
                   req.serviceType === 'depot-fuel-cargo-renewal' ? (req.existing_ref_no || req.reference_no) :
                   req.serviceType === 'depot-fuel-cargo-transfer' ? (req.transfer_from || req.reference_no) :
                   req.serviceType === 'new-gas-station-retail' ? (req.applicant_name || req.gas_station_retail_name) :
                   req.serviceType === 'new-fuel-distributor' ? (req.applicant_name || req.distributor_company_name) :
                   req.serviceType === 'new-fuel-import' ? (req.applicant_name || req.importer_company_name) :
                   req.serviceType === 'new-gas-import' ? (req.applicant_name || req.gas_importer_company_name) :
                   req.serviceType === 'new-oil-exporter' ? (req.applicant_name || req.exporter_company_name) :
                   req.serviceType === 'new-fuel-refinery' ? (req.applicant_name || req.refinery_name) :
                   req.serviceType === 'new-fuel-transportation' ? (req.applicant_name || req.transporter_company_name) :
                   req.serviceType === 'gas-station-retail-renewal' ? (req.existing_ref_no || req.reference_no) :
                   req.serviceType === 'fuel-distributor-renewal' ? (req.existing_ref_no || req.reference_no) :
                   req.serviceType === 'fuel-import-renewal' ? (req.existing_ref_no || req.reference_no) :
                   req.serviceType === 'gas-import-renewal' ? (req.existing_ref_no || req.reference_no) :
                   req.serviceType === 'oil-exporter-renewal' ? (req.existing_ref_no || req.reference_no) :
                   req.serviceType === 'fuel-refinery-renewal' ? (req.existing_ref_no || req.reference_no) :
                   req.serviceType === 'fuel-transportation-renewal' ? (req.existing_ref_no || req.reference_no) :
                    req.serviceType === 'fuel-order-cancellation' ? req.fuel_distributer_name :
                    req.serviceType === 'fuel-order-cancellation-service' ? req.fuel_company_name :
                    req.serviceType === 'vehicle-breakdown-reporting' ? (req.requester_name || req.reference_no) :
                    req.serviceType === 'fuel-delivery-extension' ? (req.requester_name || req.existing_ref_no || req.reference_no) :
                    req.serviceType === 'electric-vehicle-charging' ? (req.company_name || req.reference_no) :
                    req.serviceType === 'Fuel_Station_Retail_Construction_Permit' ? (req.applicant_name || req.gas_station_retail_name) :
                    req.serviceType === 'Fuel_Distributor_Construction_Permit' ? (req.applicant_name || req.distributor_company_name) :
                    req.serviceType === 'Fuel_Importer_ Construction_Permit' ? (req.applicant_name || req.importer_company_name) :
                    req.serviceType === 'Gas_Importer_Construction_Permit' ? (req.applicant_name || req.gas_importer_company_name) :
                    req.serviceType === 'Fuel_Exporter_Construction_permit' ? (req.applicant_name || req.exporter_company_name) :
                    req.serviceType === 'Fuel_Refining_Construction_permit' ? (req.applicant_name || req.refinery_name) :
                    req.serviceType === 'Fuel_Transportation_Construction_permit' ? (req.applicant_name || req.transporter_company_name) :
                   req.organization_name}
                 </td>
                 <td className="px-4 py-3 text-sm">
                   {(req.request_date || req.created_at) ? new Date(req.request_date || req.created_at).toLocaleDateString() : 'N/A'}
                 </td>
                  <td className="px-4 py-3">
                    <span className={`px-3 py-1 rounded text-xs font-bold uppercase ${getStatusColor(req.status || 'pending')}`}>
                     {req.status || 'pending'}
                   </span>
                   {req.awaitingDeptHeadApproval && (
                     <span className="mt-1 inline-flex px-2 py-1 rounded text-[11px] font-bold bg-indigo-100 text-indigo-800">
                       Awaiting Department Head approval
                     </span>
                   )}
                   {req.awaitingBackOfficeReview && (
                     <span className="mt-1 inline-flex px-2 py-1 rounded text-[11px] font-bold bg-amber-100 text-amber-800 border border-amber-300">
                       {req.transferMessage?.indexOf('[Approved]') === 0
                         ? 'Department Head approved — awaiting back-office printing'
                         : 'Correction resubmitted — awaiting back-office review'}
                     </span>
                   )}
                   {req.work_completed && (
                     <span className="mt-1 inline-flex px-2 py-1 rounded text-[11px] font-bold bg-green-600 text-white">
                       ✓ Completed
                     </span>
                   )}
                 </td>
                <td className="px-4 py-3">
                  {req.response_reason ? (
                    <div className="max-w-xs">
                      <p className="text-xs font-bold text-amber-700 mb-1 flex items-center gap-1"><FaExclamationCircle className="text-amber-500" /> {req.status === 'returned' ? 'Correction message' : req.status === 'payment_pending' ? 'Payment Instructions' : 'Response Reason'}</p>
                      <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2 break-words">{req.response_reason}</p>
                    </div>
                  ) : (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {req.customerResponses?.length ? (
                    <div className="max-w-xs space-y-2">
                      {req.customerResponses.map((response, index) => (
                        <div key={`${response.type || 'response'}-${index}`}>
                          <p className="text-xs font-bold text-blue-700 mb-1 flex items-center gap-1"><FaReply className="text-blue-500" /> {response.type === 'Payment' ? 'Payment Request' : response.type === 'Correction' ? 'Correction Request' : 'Sent to Customer'}</p>
                          <p className="text-xs text-blue-800 bg-blue-50 border border-blue-200 rounded p-2 break-words">{response.message}</p>
                          {response.by_name && (
                            <p className="text-[10px] text-gray-500 mt-1">
                              by {response.by_name}
                              {response.at ? ` on ${new Date(response.at).toLocaleString()}` : ''}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-gray-400 text-xs">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {getRequestDocuments(req).length > 0 && (
                    <button type="button" onClick={() => setDocumentViewerRequest(req)} title="View all documents" className="text-blue-600 hover:text-blue-800 flex items-center gap-1">
                      <FaFolderOpen /> <span className="text-xs">View ({getRequestDocuments(req).length})</span>
                    </button>
                  )}
                  {req.payment_receipt && (
                    <button type="button" onClick={() => setDocumentViewerRequest({ ...req, __paymentOnly: true })} title="View payment receipt" className="text-purple-600 hover:text-purple-800 flex items-center gap-1">
                      <FaFile /> <span className="text-xs">Receipt</span>
                    </button>
                  )}
                </td>
                {['Director', 'DeputyDir', 'DeptHead', 'SuperAdmin'].includes(currentRole) && (
                  <td className="px-4 py-3 text-sm">
                    {req.assignedToName ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
                        {req.assignedToName}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">—</span>
                    )}
                  </td>
                )}
                <td className="px-4 py-3">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleOpenDetails(req)}
                      className="text-[#006400] hover:text-green-600 font-medium flex items-center gap-1"
                    >
                      <FaEye /> View
                    </button>
                    {currentRole === 'SuperAdmin' && (
                      <button
                        onClick={() => handleDeleteRequest(req)}
                        disabled={deleting === req.id}
                        className="text-red-600 hover:text-red-800 font-medium flex items-center gap-1"
                        title="Delete request (SuperAdmin only)"
                      >
                        {deleting === req.id ? '...' : <><FaTrash /> Delete</>}
                      </button>
                    )}
                    {(req.status === 'approved' || req.status === 'paid') && (
                      <button
                        onClick={() => handleArchive(req)}
                        disabled={archiving === req.id}
                        className="text-purple-600 hover:text-purple-800 font-medium flex items-center gap-1"
                        title="Archive after letter/cert generation"
                      >
                        {archiving === req.id ? '...' : <FaFolderOpen />}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {visibleRequests.length === 0 && (
          <p className="p-6 text-center text-gray-500">
            {activeFilter === 'all' ? 'No requests found' : 'No requests in this queue'}
          </p>
        )}
      </div>

      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-xl font-bold">Request Details - {selectedRequest.reference_no}</h3>
              <button onClick={() => { setSelectedRequest(null); setApprovedLetter(null); }} className="text-gray-500 hover:text-gray-700">
                <FaTrash />
              </button>
            </div>
            
            <div className="mb-4 p-4 bg-gray-50 rounded">
              <h4 className="font-bold mb-2">Customer Documents</h4>
              <div className="flex items-center gap-3 flex-wrap">
                {getRequestDocuments(selectedRequest).length > 0 ? (
                  <button type="button" onClick={() => setDocumentViewerRequest(selectedRequest)} className="flex items-center gap-2 text-blue-600 hover:text-blue-800">
                    <FaFolderOpen /> View all documents ({getRequestDocuments(selectedRequest).length})
                  </button>
                ) : (
                  <span className="text-gray-500">No documents uploaded</span>
                )}
                {selectedRequest.payment_receipt && (
                  <button type="button" onClick={() => setDocumentViewerRequest({ ...selectedRequest, __paymentOnly: true })} className="flex items-center gap-2 text-purple-600 hover:text-purple-800">
                    <FaFile /> View payment receipt
                  </button>
                )}
                {!['paid', 'rejected'].includes(selectedRequest.status) && ['Director', 'DeputyDir', 'DeptHead', 'TeamLeader', 'BackOfficer', 'SuperAdmin', 'ITAdmin'].includes(currentRole) && (
                  <button
                    type="button"
                    onClick={handleVerifyPaymentReceipt}
                    disabled={!selectedRequest.payment_receipt}
                    title={selectedRequest.payment_receipt ? 'Verify payment receipt' : 'Waiting for the customer to upload a payment receipt'}
                    className={`flex items-center gap-2 font-medium ${selectedRequest.payment_receipt ? 'text-green-600 hover:text-green-800' : 'text-gray-400 cursor-not-allowed'}`}
                  >
                    <FaCheckCircle /> {selectedRequest.payment_receipt ? 'Verify payment receipt' : 'Waiting for payment receipt'}
                  </button>
                )}
              </div>
            </div>

              {(selectedRequest.id_photo || hasIdPhotoPlaceholder) && (
                <div className="mb-4">
                  <h4 className="font-bold mb-2">ID Photo</h4>
                  {selectedRequest.id_photo ? (
                    <img
                      src={fileUrl(selectedRequest.id_photo)}
                      alt="Applicant ID"
                      className="w-[3in] h-[4in] rounded border bg-gray-50 object-contain"
                    />
                  ) : (
                    <div className="w-[3in] h-[4in] rounded border border-dashed bg-gray-50 flex items-center justify-center text-sm text-gray-500">
                      ID photo not provided
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
                <div><strong>Service Type:</strong> {
                  selectedRequest.serviceType === 'gas-station-transfer' ? 'የነዳጅ ማደያ ወደ ሌላ የነዳጅ አከፋፋይ ኩባንያ የዝውውር ትስስር ፈቃድ አገልግሎት / Service for existing gas station transfer from on company to the other' :
                  selectedRequest.serviceType === 'diversion-license' ? 'የዳይቨርሽን ፈቃድ አገልግሎት / Service for diversion permit' :
                  selectedRequest.serviceType === 'operator-change-license' ? 'የነዳጅ ማደያ የኦፕረተር/ዲለር ለውጥ ትስስር ፈቃድ አገልግሎት / Service for operator/dealer change permit' :
                  selectedRequest.serviceType === 'new-fuel-company' ? 'New Fuel Distribution Company Connection License Service (አዲስ የነዳጅ አከፋፋይ ኩባንያ ትስስር ፈቃድ አገልግሎት)' :
                  selectedRequest.serviceType === 'fuel-company-renewal' ? 'Fuel Distribution Company Connection License Renewal Service (የነዳጅ አከፋፋይ ኩባንያ ትስስር ፈቃድ እድሳት አገልግሎት)' :
                  selectedRequest.serviceType === 'fuel-company-transfer' ? 'Fuel Distribution Company Connection License Transfer Service (የነዳጅ አከፋፋይ ኩባንያ ትስስር ፈቃድ ዝውውር አገልግሎት)' :
                  selectedRequest.serviceType === 'new-gas-station' ? 'New Gas Station Connection License Service (አዲስ የነዳጅ ማደያ ትስስር ፈቃድ አገልግሎት)' :
                   selectedRequest.serviceType === 'gas-station-renewal' ? 'Service for the renewal of Gas Station Permit (የነዳጅ ማደያ ትስስር ፈቃድ እድሳት አገልግሎት)' :
                   selectedRequest.serviceType === 'gas-station-connection-transfer' ? 'Service for the transfer of Gas Station Permit (የነዳጅ ማደያ ትስስር ፈቃድ ዝውውር አገልግሎት)' :
                   selectedRequest.serviceType === 'new-depot-fuel-cargo' ? 'Service for New Fuel Loading Connection Permit from Depot (አዲስ ከዲፓ የነዳጅ ጭነት ትስስር ፈቃድ አገልግሎት)' :
                   selectedRequest.serviceType === 'depot-fuel-cargo-renewal' ? 'Service for the Renewal of Fuel Loading Connection Permit from Depot (ከዲፓ የነዳጅ ጭነት ትስስር ፈቃድ እድሳት አገልግሎት)' :
                   selectedRequest.serviceType === 'depot-fuel-cargo-transfer' ? 'Service for the transfer of Fuel Loading Connection Permit from Depot (ከዲፓ የነዳጅ ጭነት ትስስር ፈቃድ ዝውውር አገልግሎት)' :
                   selectedRequest.serviceType === 'new-gas-station-retail' ? 'New Fuel Station Retail Competency and Licensing Service (አዲስ የነዳጅ ማደያ ችርቻሮ ብቃትና ፈቃድ አገልግሎት)' :
                   selectedRequest.serviceType === 'new-fuel-distributor' ? 'New Fuel Distributor Competency and Licensing Service (አዲስ የነዳጅ አከፋፋይ ብቃትና ፈቃድ አገልግሎት)' :
                   selectedRequest.serviceType === 'new-fuel-import' ? 'New Fuel Importer Competency and Licensing Service (አዲስ የነዳጅ አስመጪነት ብቃትና ፈቃድ አገልግሎት)' :
                   selectedRequest.serviceType === 'new-gas-import' ? 'New Gas Importer Competency and Licensing Service (አዲስ የጋዝ አስመጪነት ብቃትና ፈቃድ አገልግሎት)' :
                   selectedRequest.serviceType === 'new-oil-exporter' ? 'New Fuel Exporter Competency and Licensing Service (አዲስ የነዳጅ ላኪነት ብቃትና ፈቃድ አገልግሎት)' :
                   selectedRequest.serviceType === 'new-fuel-refinery' ? 'New Fuel Refining Competency and Licensing Service (አዲስ የነዳጅ ማጣራት ብቃትና ፈቃድ አገልግሎት)' :
                   selectedRequest.serviceType === 'new-fuel-transportation' ? 'New Fuel Transportation Competency and Licensing Service (አዲስ የነዳጅ ማጓጓዝ ብቃትና ፈቃድ አገልግሎት)' :
                   selectedRequest.serviceType === 'gas-station-retail-renewal' ? 'Fuel Station Retail Competency and License Renewal Service (የነዳጅ ማደያ ችርቻሮ ብቃትና ፈቃድ እድሳት አገልግሎት)' :
                   selectedRequest.serviceType === 'fuel-distributor-renewal' ? 'Fuel Distributor Competency and License Renewal Service (የነዳጅ አከፋፋይ ብቃትና ፈቃድ እድሳት አገልግሎት)' :
                   selectedRequest.serviceType === 'fuel-import-renewal' ? 'Fuel Importer Competency and License Renewal Service (የነዳጅ አስመጪነት ብቃትና ፈቃድ እድሳት አገልግሎት)' :
                   selectedRequest.serviceType === 'gas-import-renewal' ? 'Gas Importer Competency and License Renewal Service (የጋዝ አስመጪነት ብቃትና ፈቃድ እድሳት አገልግሎት)' :
                   selectedRequest.serviceType === 'oil-exporter-renewal' ? 'Fuel Exporter Competency and License Renewal Service (የነዳጅ ላኪነት ብቃትና ፈቃድ እድሳት አገልግሎት)' :
                   selectedRequest.serviceType === 'fuel-refinery-renewal' ? 'Fuel Refining Competency and License Renewal Service (የነዳጅ ማጣራት ብቃትና ፈቃድ እድሳት አገልግሎት)' :
                   selectedRequest.serviceType === 'fuel-transportation-renewal' ? 'Fuel Transportation Competency and License Renewal Service (የነዳጅ ማጓጓዝ ብቃትና ፈቃድ እድሳት አገልግሎት)' :
                    selectedRequest.serviceType === 'fuel-order-cancellation' ? 'የነዳጅ ጭነት ትእዛዝ ማቋረጥ' :
                    selectedRequest.serviceType === 'Fuel_Station_Retail_Construction_Permit' ? 'Fuel Station Retail Construction Permit Service (የነዳጅ ማደያ ችርቻሮ ኮንስትራክሽን ፈቃድ አገልግሎት)' :
                    selectedRequest.serviceType === 'Fuel_Distributor_Construction_Permit' ? 'Fuel Distributor Construction Permit Service (የነዳጅ አከፋፋይ ኮንስትራክሽን ፈቃድ አገልግሎት)' :
                    selectedRequest.serviceType === 'Fuel_Importer_ Construction_Permit' ? 'Fuel Importer Construction Permit Service (የነዳጅ አስመጪ ኮንስትራክሽን ፈቃድ አገልግሎት)' :
                    selectedRequest.serviceType === 'Gas_Importer_Construction_Permit' ? 'Gas Importer Construction Permit Service (የጋዝ አስመጪ ኮንስትራክሽን ፈቃድ አገልግሎት)' :
                    selectedRequest.serviceType === 'Fuel_Exporter_Construction_permit' ? 'Fuel Exporter Construction Permit Service (የነዳጅ ላኪ ኮንስትራክሽን ፈቃድ አገልግሎት)' :
                    selectedRequest.serviceType === 'Fuel_Refining_Construction_permit' ? 'Fuel Refining Construction Permit Service (የነዳጅ ማጣሪያ ኮንስትራክሽን ፈቃድ አገልግሎት)' :
                    selectedRequest.serviceType === 'Fuel_Transportation_Construction_permit' ? 'Fuel Transportation Construction Permit Service (የነዳጅ ማጓጓዣ ኮንስትራክሽን ፈቃድ አገልግሎት)' :
                    selectedRequest.serviceType === 'vehicle-breakdown-reporting' ? 'ብልሽት የገጠመውን ተሸከርካሪ ማሳወቅ አገልግሎት / Vehicle Breakdown Reporting Service' :
                    selectedRequest.serviceType === 'fuel-delivery-extension' ? 'የነዳጅ አቅርቦት ማራዘም ጥያቄ አገልግሎት / Fuel Delivery Extension Service' :
                    selectedRequest.serviceType === 'electric-vehicle-charging' ? 'የኤሌክትሪክ ተሽከርካሪ ቻርጅ መሙሌት አገልግሎት / Electric Vehicle Charging Service License Request' :
                   selectedRequest.service_type || 'የነዳጅ ቀጥታ ተጠቃሚ ተቋማት ትስስር ፈቃድ አገልግሎት / Service for direct fuel beneficiary'
                }</div>
                <div><strong>Customer Username:</strong> {usersById[String(selectedRequest.user_id)]?.username || 'N/A'}</div>
                <div><strong>Customer Full Name:</strong> {usersById[String(selectedRequest.user_id)]?.fullname || usersById[String(selectedRequest.user_id)]?.full_name || 'N/A'}</div>
                {(() => {
  const EXCLUDED = new Set(['id', 'user_id', 'serviceType', 'service_type', 'status', 'created_at', 'updated_at', 'request_date', 'decision_date', 'response_reason', 'rejection_reason', 'remark', 'approved_letter', 'application_letter', 'support_letter', 'aggrement_letter', 'certificate_path', 'assignedToName', 'assignedByName', 'transferMessage', 'existing_approved_letter', 'reference_no', 'password', 'fan_national_id', 'additional_attachments', 'fuel_retail_price_from', 'fuel_retail_price_to', 'dept_head_approved', 'work_completed', 'client_letter', 'client_letter_name', 'client_letter_sent_at', 'archive_document', 'letter_released', 'letter_released_at']);
                  const LABEL_MAP = {
                    nameofapplicant: 'Applicant Name', nationality: 'Nationality', idno: 'ID No', location: 'Location',
                    dateandplaceissued: 'Date Issued', applicant_region: 'Applicant Region', applicant_subcity: 'Applicant SubCity',
                    applicant_wereda: 'Applicant Wereda', applicant_hno: 'Applicant H.No', applicant_pobox: 'Applicant P.O.Box',
                    applicant_telno: 'Applicant Tel No', applicant_email: 'Applicant Email',
                    site_region: 'Site Region', site_zone: 'Site Zone', site_subcity: 'Site SubCity',
                    site_woreda: 'Site Woreda', site_hno: 'Site H.No', site_phone: 'Site Phone',
                    existingcompanyname: 'Existing Company', transferto: 'Transfer To',
                    station_type: 'የማደያ አይነት / Station Type',
                    site_area: 'የቦታው ስፋት በካሬ / Site Area',
                    fuel_distributer_name: 'Fuel Distributer', manager_name: 'Manager Name',
                    manager_nationality: 'Manager Nationality', manager_idno: 'Manager ID No',
                    manager_region: 'Manager Region', manager_subcity: 'Manager SubCity',
                    manager_woreda: 'Manager Woreda', manager_hno: 'Manager H.No',
                    manager_k_ketema: 'Manager K/Ketema', manager_wereda: 'Manager Wereda',
                    manager_telno: 'Manager Tel No', manager_mobno: 'Manager Mobile',
                    manager_mobile: 'Manager Mobile', manager_email: 'Manager Email',
                    manager_fixed_phone: 'Manager Fixed Phone', manager_pobox: 'Manager P.O.Box',
                    new_dealer_name: 'New Dealer Name', merchant_id_short_code: 'Merchant ID',
                    phone_orders: 'Phone Orders', dealer_requester_name: 'Dealer Requester',
                    first_destination_name: 'First Destination', first_destination_region: 'First Destination Region',
                    first_destination_woreda: 'First Destination Woreda', first_destination_specific_place: 'First Destination Place',
                    gas_station_name: 'Gas Station Name', gas_station_region: 'Gas Station Region',
                    gas_station_woreda: 'Gas Station Woreda', gas_station_specific_place: 'Gas Station Place',
                    change_of_place: 'Change of Place', changing_truck: 'Changing Truck',
                    detailed_reason: 'Detailed Reason',
                    organization_name: 'Organization', project_name: 'Project',
                    product_type: 'Product Type', product_amount: 'Product Amount',
                    no_of_machines: 'No. of Machines', fuel_type: 'Fuel Type',
                    amount_of_fuel: 'Amount of Fuel', monthly_fuel_need: 'Monthly Fuel Need',
                    supplier_name: 'Supplier', support_organization_name: 'Support Organization',
                    project_end_date: 'Project End Date', fuel_distributer: 'Fuel Distributer',
                    fuel_distributor_letter_no: 'Fuel Distributor Letter No',
                    fuel_distributor_letter_date: 'Fuel Distributor Letter Date',
                    support_org_letter_no: 'Support Org Letter No',
                    support_org_letter_date: 'Support Org Letter Date',
                    company_name: 'Company Name', importer_company_name: 'Importer Company Name',
                    exporter_company_name: 'Exporter Company Name', refinery_name: 'Refinery Name',
                    transporter_company_name: 'Transporter Company Name',
                    depot_name: 'Depot Name', distributor_company_name: 'Distributor Company Name',
                    gas_station_retail_name: 'Gas Station Retail Name',
                    gas_importer_company_name: 'Gas Importer Company Name',
                    existing_ref_no: 'Existing Ref No', renewal_year: 'Renewal Year',
                    payment_fee: 'Payment Fee', renewal_date: 'Renewal Date',
    transfer_from: 'Transfer From', transfer_to: 'Transfer To', transfer_date: 'Transfer Date', transfer_reason: 'የሚዛወርበት ምክንያት / Reason for Transfer',
                    type_of_operation: 'Type of Operation', remark: 'Remark',
                    applicant_name: 'Applicant Name', place_of_renewed: 'Place of Renewed',
                    date_of_renewed: 'Date of Renewed', region: 'Region', subcity: 'SubCity',
                    woreda: 'Woreda', hno: 'H.No', pobox: 'P.O.Box', telno: 'Tel No', email: 'Email',
                    manager_date_place_issued: 'Manager Date Issued',
                    manager_ketema: 'Manager Ketema',
                    fuel_loading_date: 'Fuel Loading Date', fuel_load_amount: 'Fuel Load Amount',
                    plate_number: 'Plate Number', trailer_plate_number: 'Trailer Plate Number',
                    fdc: 'FDC', loading_fuel_type: 'Type of Fuel', replacing_plate_number: 'Replacing Plate Number',
                    replacing_trailer_plate_number: 'Replacing Trailer Plate Number',
                    replaced_plate_number: 'Replaced Plate Number',
                    replaced_trailer_plate_number: 'Replaced Trailer Plate Number',
                    name: 'Name', name_amharic: 'Name (Amharic)',
                    work_address: 'Work Address', subcity_zone: 'SubCity Zone',
                    woreda_kebele: 'Woreda/Kebele', id_place: 'ID Place', id_date: 'ID Date',
                    product_work_type: 'Product Work Type', work_percentage: 'Work Percentage',
                    machine_type: 'Machine Type', fuel_amount: 'Fuel Amount',
                    monthly_fuel_consumption: 'Monthly Fuel Consumption',
                    type_of_machine: 'Type of Machine', id_renewal_date: 'ID Renewal Date',
                    id_renewal_place: 'ID Renewal Place', phone_no: 'Phone No',
                    po_box: 'P.O.Box',
                    fuel_company_name: 'የነዳጅ ካምፓኒ ሥም / Fuel Company Name',
                    letter_issued_date: 'ደብዳቤ የተጻፈበት ቀን / Letter Issued Date',
                    letter_number: 'የደብዳቤ ቁጥር / Letter Number',
                    fuel_unloading_site: 'የነዳጅ ጭነት የሚራገፍበት ቦታ / Fuel Unloading Site',
                    reason_for_cancellation: 'የሚሰረዝበት ምክንያት / Reason for Cancellation',
                    requester_name: 'የጠየቀው ካምፓኒ / ግለሰብ ሥም / Requester Company or Person Name',
                    fuel_load_date: 'ነዳጅ የተጫነበት ቀን / Fuel Loading Date',
                    order_number: 'የትዕዛዝ ቁጥር / Order Number',
                    fdc_number: 'የFDC ቁጥር / FDC Number',
                    letter_date: 'ማመልከቻ / ደብዳቤ የተጻፈበት ቀን / Application Letter Date',
                    breakdown_location: 'ብልሽቱ የተከሰተበት ቦታ / Breakdown Location',
                    breakdown_date: 'ብልሽቱ የተከሰሰበት ቀን / Breakdown Date',
                    breakdown_description: 'የብልሽቱ ዓይነት በአጭሩ / Brief Description of Breakdown',
                    report_date: 'ያመለከቱበት ቀን / Reporting Date',
                    extension_start_date: 'የሚራዘምለት መነሻ ቀን / Extension Start Date',
                    extension_end_date: 'የሚራዘምለት መጨረሻ ቀን / Extension End Date',
                    fuel_quantity: 'የነዳጅ መጠን / Fuel Quantity (L)',
                    license_type: 'የፈቃድ ዓይነት / Type of License', license_category: 'የፍቃድ ደረጃ / License Category',
                    company_city: 'ከተማ / City', company_subcity: 'ክፍለ ከተማ / Sub-City', company_woreda: 'ወረዳ / Woreda',
                    company_zone: 'ዞን / Zone', company_phone_no: 'ስልክ ቁ / Phone No', company_po_box: 'የፓ.ሳ.ቁ / P.O. Box',
                    company_tin_no: 'የግ.ከ.ቁ / TIN No', company_fax_no: 'ፋክስ ቁጥር / Fax No', company_email: 'ኢሜይል / Email',
                    company_landmark: 'የቦታዉ ልዩ ስም (አመላካች) / Specific Name/Landmark',
                    site_city: 'ከተማ / Site City', site_house_no: 'የቤት ቁ / House No', site_phone_no: 'ስልክ ቁ / Site Phone No',
                    gm_name: 'የሥራ አስኪያጅ ስም / General Manager Name', gm_job_title: 'የስራ ኃላፊነት / Job Title/Position',
                    gm_city: 'ከተማ / City', gm_phone_no: 'የስልክ ቁጥር / Phone Number', gm_email: 'ኢ.ሜ.ል / Email',
                    charging_capacity_kw: 'የቻርጅ መሙያ አገልግሎት አቅም / Charging Capacity (KW/KVA)',
                    electric_supply_source: 'የኤሌክትሪክ አቅርቦት ምንጭ / Source of Electric Supply',
                    estimated_start_date: 'ግምታዊ መጀመሪያ ቀን / Estimated Start Date',
                    id_photo: 'የመታወቂያ ፎቶ / ID Photo',
                  };
                  const entries = Object.entries(selectedRequest).filter(([k, v]) => !EXCLUDED.has(k) && (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') && v !== '' && !k.startsWith('_'));
                  if (entries.length === 0) return null;
                  return entries.map(([k, v]) => {
                    let displayVal = v;
                    if (v instanceof Date || (typeof v === 'string' && v.match(/^\d{4}-\d{2}-\d{2}/))) {
                      try { displayVal = new Date(v).toLocaleDateString(); } catch(e) {}
                    }
                    if (typeof v === 'boolean') displayVal = v ? 'Yes' : 'No';
                    const label = LABEL_MAP[k] || k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
                    return <div key={k}><strong>{label}:</strong> {displayVal}</div>;
                  });
                })()}
                <div><strong>Request Date:</strong> {selectedRequest.request_date ? new Date(selectedRequest.request_date).toLocaleDateString() : 'N/A'}</div>
                <div className="col-span-2 bg-green-100 p-2 rounded border border-green-500 flex items-center justify-between">
                  <div>
                    <strong>Current Status: </strong>
                    <span className={`px-3 py-1 rounded text-sm font-bold uppercase ${getStatusColor(selectedRequest.status)}`}>
                      {selectedRequest.status || 'pending'}
                    </span>
                    {selectedRequest.work_completed && (
                      <span className="ml-2 px-3 py-1 rounded text-sm font-bold bg-green-600 text-white">✓ Work Completed</span>
                    )}
                  </div>
                  {selectedRequest.assignedToName && (
                    <div className="text-sm">
                      <strong>Assigned To: </strong>
                      <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs font-medium">
                        {selectedRequest.assignedToName}
                      </span>
                    </div>
                  )}
                </div>
                <div className="col-span-2"><strong>Remark:</strong> {selectedRequest.remark || 'N/A'}</div>
                {selectedRequest.rejection_reason && (
                  <div className="col-span-2"><strong>Rejection Reason:</strong> {selectedRequest.rejection_reason}</div>
                )}
              </div>

              {!NO_FUEL_PRICE_TYPES.includes(selectedRequest.serviceType) && (
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">የነዳጅ ችርቻሮ ዋጋ የሚጀምርበት ቀን / Fuel retail price from date</label>
                    {selectedRequest.fuel_retail_price_from && (
                      <div className="text-sm text-gray-500 mb-1">{selectedRequest.fuel_retail_price_from}</div>
                    )}
                    <EthiopianDatePicker
                      name="fuel_retail_price_from"
                      value={responseData.fuel_retail_price_from}
                      onChange={(e) => setResponseData({ ...responseData, fuel_retail_price_from: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">የነዳጅ ችርቻሮ ዋጋ የሚያልቅበት ቀን / Fuel retail price to date</label>
                    {selectedRequest.fuel_retail_price_to && (
                      <div className="text-sm text-gray-500 mb-1">{selectedRequest.fuel_retail_price_to}</div>
                    )}
                    <EthiopianDatePicker
                      name="fuel_retail_price_to"
                      value={responseData.fuel_retail_price_to}
                      onChange={(e) => setResponseData({ ...responseData, fuel_retail_price_to: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      required={false}
                    />
                  </div>
                </div>
              )}

              {!NO_FUEL_PRICE_TYPES.includes(selectedRequest.serviceType) && (
              <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">የነዳጅ አቅራቢ ድርጅት ሥም / Supplier name</label>
                  {selectedRequest.supplier_name && (
                    <div className="text-sm text-gray-500 mb-1">{selectedRequest.supplier_name}</div>
                  )}
                  <input
                    name="supplier_name"
                    value={responseData.supplier_name}
                    onChange={(e) => setResponseData({ ...responseData, supplier_name: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
              )}

            <form onSubmit={handleRespond}>
              {selectedRequest.transferMessage && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
                  <label className="block text-sm font-medium text-blue-800 mb-1">
                    <FaExchangeAlt className="inline mr-1" /> DeptHead Transfer Note
                  </label>
                  <p className="text-sm text-blue-700">{selectedRequest.transferMessage}</p>
                </div>
              )}
              {selFlags.awaitingDeptHeadApproval && (
                <div className="mb-4 p-4 bg-indigo-50 border-2 border-indigo-300 rounded">
                  <p className="text-sm font-bold text-indigo-900 flex items-center gap-2"><FaExclamationCircle /> The back office sent this request to you for approval.</p>
                  <p className="text-xs text-indigo-700 mt-1">
                    This submission was sent from the back office for the Department Head to review. Please approve it to continue.
                  </p>
                  {canApproveAsDeptHead && (
                    <button
                      type="button"
                      onClick={handleDeptHeadQuickApprove}
                      className="mt-3 px-5 py-2 bg-indigo-600 text-white rounded font-bold hover:bg-indigo-700"
                    >
                      ✓ Approve
                    </button>
                  )}
                </div>
              )}
              {selFlags.awaitingBackOfficeReview && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded">
                  {selectedRequest.transferMessage?.indexOf('[Approved]') === 0 ? (
                    <>
                      <p className="text-sm font-bold text-amber-800">Department Head approved — awaiting back-office printing</p>
                      <p className="text-xs text-amber-700 mt-1">
                        The request was approved and assigned back to the Back Officer to generate and print the approval letter or certificate.
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm font-bold text-amber-800">Corrected submission — awaiting back-office review</p>
                      <p className="text-xs text-amber-700 mt-1">
                        The customer resubmitted the corrected submission; a back-office officer must review it.
                      </p>
                    </>
                  )}
                </div>
              )}
              {canSendClientLetter && (
                <div className="mb-4 border rounded p-4 bg-gray-50">
                  <p className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2"><FaUpload /> Send letter to client</p>
                  {selectedRequest.client_letter ? (
                    <p className="text-xs text-green-700 mb-2">
                      Letter attached{selectedRequest.client_letter_sent_at ? ` on ${new Date(selectedRequest.client_letter_sent_at).toLocaleString()}` : ''}:{' '}
                      <a href={fileUrl(selectedRequest.client_letter)} target="_blank" rel="noopener noreferrer" className="underline font-semibold">
                        {selectedRequest.client_letter_name || 'view letter'}
                      </a>
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 mb-2">No letter attached for the client yet.</p>
                  )}
                  <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setClientLetterFile(e.target.files[0] || null)}
                      className="w-full border rounded px-3 py-2 bg-white"
                    />
                    <button
                      type="button"
                      onClick={handleSendClientLetter}
                      disabled={clientLetterSending || !clientLetterFile}
                      className={`px-4 py-2 rounded whitespace-nowrap ${clientLetterSending || !clientLetterFile ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                    >
                      {clientLetterSending ? 'Sending...' : 'Attach & send to client'}
                    </button>
                  </div>
                  <p className="text-[11px] text-gray-500 mt-1">Once attached, the letter appears in the client&apos;s "My Applications" for online download.</p>
                  <div className="mt-3 border-t pt-3">
                    <label className="block text-xs font-medium text-gray-700 mb-1">Email the letter to another institution</label>
                    <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                      <input
                        type="email"
                        value={clientLetterEmail}
                        onChange={(e) => setClientLetterEmail(e.target.value)}
                        placeholder="name@institution.org"
                        className="w-full border rounded px-3 py-2 bg-white"
                      />
                      <button
                        type="button"
                        onClick={handleEmailClientLetter}
                        disabled={clientLetterEmailSending}
                        className={`px-4 py-2 rounded whitespace-nowrap ${clientLetterEmailSending ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-[#006400] text-white hover:bg-green-800'}`}
                      >
                        {clientLetterEmailSending ? 'Sending...' : 'Send by email'}
                      </button>
                    </div>
                    <p className="text-[11px] text-gray-500 mt-1">Emails the attached client letter (or the approval letter if none is attached).</p>
                  </div>
                </div>
              )}
              {isBackOfficerRequestWorkflow ? (
                <div className="space-y-4 mb-4">
                  <div className="border rounded p-4">
                    {isDirectFuelBackOfficer && deptHeadApproved && (
                      <button
                        type="button"
                        onClick={() => handleDownloadPDF(selectedRequest)}
                        className="px-4 py-2 mr-2 bg-blue-700 text-white rounded hover:bg-blue-800"
                      >
                        Download request details (PDF)
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={printBlocked}
                      onClick={() => setShowDirectFuelApprovalAction((visible) => !visible)}
                      className={`px-4 py-2 rounded ${printBlocked ? 'bg-gray-300 text-gray-600 cursor-not-allowed' : 'bg-green-700 text-white hover:bg-green-800'}`}
                      title={printBlocked ? 'Department Head approval is required before printing the letter / certificate' : 'Print letter / certificate'}
                    >
                      Print letter/certificate
                    </button>
                    {printBlocked && (
                      <p className="mt-2 text-xs text-amber-700">
                        Disabled: the Department Head must approve this request before the letter / certificate can be printed.
                      </p>
                    )}
                    {showDirectFuelApprovalAction && (
                      <div className="mt-3">
                        <button
                          type="button"
                          onClick={handleBackOfficerApprovalLetter}
                          className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2"
                        >
                          <FaFilePdf /> Generate Approval Letter
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="border rounded p-4">
                    <button
                      type="button"
                      onClick={() => setShowDirectFuelCustomerActions((visible) => !visible)}
                      className="px-4 py-2 bg-amber-500 text-white rounded hover:bg-amber-600"
                    >
                      Return to Customer
                    </button>
                    {showDirectFuelCustomerActions && (
                      <div className="mt-4 space-y-4">
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium">
                            <input
                              type="checkbox"
                              checked={showDirectFuelPaymentForm}
                              onChange={(event) => {
                                setShowDirectFuelPaymentForm(event.target.checked);
                                if (!event.target.checked) setDirectFuelPaymentInstructions('');
                              }}
                            />
                            Add Request Payment
                          </label>
                          {showDirectFuelPaymentForm && (
                            <div className="mt-2">
                              <label className="block text-sm font-medium mb-1">Payment Instructions</label>
                              <textarea
                                value={directFuelPaymentInstructions}
                                onChange={(event) => setDirectFuelPaymentInstructions(event.target.value)}
                                className="w-full border rounded px-3 py-2"
                                rows="3"
                              />
                              <div className="flex justify-end gap-2 mt-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowDirectFuelPaymentForm(false);
                                    setDirectFuelPaymentInstructions('');
                                  }}
                                  className="px-4 py-2 border rounded hover:bg-gray-100"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={handleDirectFuelCustomerActions}
                                  className="px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700"
                                >
                                  Submit
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium">
                            <input
                              type="checkbox"
                              checked={showDirectFuelCorrectionForm}
                              onChange={(event) => {
                                setShowDirectFuelCorrectionForm(event.target.checked);
                                if (!event.target.checked) setReturnCustomerReason('');
                              }}
                            />
                            Problem / what must be corrected
                          </label>
                          {showDirectFuelCorrectionForm && (
                            <div className="mt-2">
                              <textarea
                                value={returnCustomerReason}
                                onChange={(event) => setReturnCustomerReason(event.target.value)}
                                className="w-full border rounded px-3 py-2"
                                rows="4"
                                placeholder="Describe the problem and what the customer should correct..."
                              />
                              <div className="flex justify-end gap-2 mt-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowDirectFuelCorrectionForm(false);
                                    setReturnCustomerReason('');
                                  }}
                                  className="px-4 py-2 border rounded hover:bg-gray-100"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={handleDirectFuelCustomerActions}
                                  className="px-4 py-2 bg-amber-500 text-white rounded hover:bg-amber-600"
                                >
                                  Submit
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="border rounded p-4">
                    <button
                      type="button"
                      onClick={() => setShowDirectFuelDeptActions((visible) => !visible)}
                      className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                    >
                      Return to Department Head
                    </button>
                    {showDirectFuelDeptActions && (
                      <div className="mt-4 space-y-4">
                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium">
                            <input
                              type="checkbox"
                              checked={showDirectFuelDeptApprovalForm}
                              onChange={(event) => {
                                const checked = event.target.checked;
                                setShowDirectFuelDeptApprovalForm(checked);
                                if (checked) setShowDirectFuelDeptCommentForm(false);
                              }}
                            />
                            For Approval
                          </label>
                          <p className="text-xs text-amber-700 mt-1">Department Head approval is required before this request can be approved.</p>
                          {showDirectFuelDeptApprovalForm && (
                            <div className="mt-2">
                              <textarea
                                value={directFuelDeptApprovalNote}
                                onChange={(event) => setDirectFuelDeptApprovalNote(event.target.value)}
                                className="w-full border rounded px-3 py-2"
                                rows="3"
                                placeholder="Add an approval note (optional)..."
                              />
                              <div className="flex justify-end gap-2 mt-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowDirectFuelDeptApprovalForm(false);
                                    setDirectFuelDeptApprovalNote('');
                                  }}
                                  className="px-4 py-2 border rounded hover:bg-gray-100"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDirectFuelReturnToDeptHead('approval', directFuelDeptApprovalNote)}
                                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                                >
                                  Submit
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        <div>
                          <label className="flex items-center gap-2 text-sm font-medium">
                            <input
                              type="checkbox"
                              checked={showDirectFuelDeptCommentForm}
                              onChange={(event) => {
                                const checked = event.target.checked;
                                setShowDirectFuelDeptCommentForm(checked);
                                if (checked) setShowDirectFuelDeptApprovalForm(false);
                              }}
                            />
                            For Comment / Review
                          </label>
                          <p className="text-xs text-gray-600 mt-1">This returns the request for review and does not require the Department Head's final approval.</p>
                          {showDirectFuelDeptCommentForm && (
                            <div className="mt-2">
                              <textarea
                                value={directFuelDeptCommentNote}
                                onChange={(event) => setDirectFuelDeptCommentNote(event.target.value)}
                                className="w-full border rounded px-3 py-2"
                                rows="3"
                                placeholder="Enter a comment or review note..."
                              />
                              <div className="flex justify-end gap-2 mt-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setShowDirectFuelDeptCommentForm(false);
                                    setDirectFuelDeptCommentNote('');
                                  }}
                                  className="px-4 py-2 border rounded hover:bg-gray-100"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleDirectFuelReturnToDeptHead('comment', directFuelDeptCommentNote)}
                                  className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                                >
                                  Submit
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-2">Response Status</label>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => setResponseData({ ...responseData, status: 'approved' })}
                        className={`px-4 py-2 rounded font-medium flex-1 ${responseData.status === 'approved' ? 'bg-[#4CAF50] text-white' : 'bg-gray-200 text-gray-700'}`}
                      >
                        Approved
                      </button>
                      <button
                        type="button"
                        onClick={() => setResponseData({ ...responseData, status: 'rejected' })}
                        className={`px-4 py-2 rounded font-medium flex-1 ${responseData.status === 'rejected' ? 'bg-[#4CAF50] text-white' : 'bg-gray-200 text-gray-700'}`}
                      >
                        Rejected
                      </button>
                      <button
                        type="button"
                        onClick={() => setResponseData({ ...responseData, status: 'pending' })}
                        className={`px-4 py-2 rounded font-medium flex-1 ${responseData.status === 'pending' ? 'bg-[#4CAF50] text-white' : 'bg-gray-200 text-gray-700'}`}
                      >
                        Pending
                      </button>
                      <button
                        type="button"
                        onClick={() => setResponseData({ ...responseData, status: 'payment_pending' })}
                        className={`px-4 py-2 rounded font-medium flex-1 ${responseData.status === 'payment_pending' ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-700'}`}
                      >
                        Request Payment
                      </button>
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">
                      {responseData.status === 'rejected' ? 'Rejection Reason' : responseData.status === 'payment_pending' ? 'Payment Instructions' : 'Response Reason'}
                    </label>
                    <textarea
                      value={responseData.response_reason}
                      onChange={(e) => setResponseData({ ...responseData, response_reason: e.target.value })}
                      className="w-full border rounded px-3 py-2"
                      rows="3"
                      required
                    />
                  </div>

                  {responseData.status === 'approved' && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-1">Upload Approval Letter</label>
                      {selectedRequest.approved_letter && (
                        <div className="flex items-center gap-2 mb-2 text-sm text-green-600">
                          <FaFile /> Currently uploaded:
                          <button type="button" onClick={() => setDocumentViewerRequest(selectedRequest)} className="underline">
                            Preview file
                          </button>
                        </div>
                      )}
                      <input
                        type="file"
                        onChange={(e) => setApprovedLetter(e.target.files[0])}
                        className="w-full border rounded px-3 py-2"
                        accept=".pdf"
                      />
                      <input type="hidden" value={selectedRequest.approved_letter || ''} name="existing_approved_letter" />
                    </div>
                  )}

                  {responseData.status === 'rejected' && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-1">Upload Rejection Letter</label>
                      {selectedRequest.approved_letter && (
                        <div className="flex items-center gap-2 mb-2 text-sm text-green-600">
                          <FaFile /> Currently uploaded:
                          <button type="button" onClick={() => setDocumentViewerRequest(selectedRequest)} className="underline">
                            Preview file
                          </button>
                        </div>
                      )}
                      <input
                        type="file"
                        onChange={(e) => setApprovedLetter(e.target.files[0])}
                        className="w-full border rounded px-3 py-2"
                        accept=".pdf"
                      />
                      <input type="hidden" value={selectedRequest.approved_letter || ''} name="existing_approved_letter" />
                    </div>
                  )}
                </>
              )}
              
              <div className="flex justify-between gap-2">
                {!isBackOfficerRequestWorkflow && selectedRequest.status && selectedRequest.status !== 'pending' && !(NO_FUEL_PRICE_TYPES.includes(selectedRequest.serviceType) && selectedRequest.status === 'approved') && (
                  <button 
                    type="button" 
                    onClick={() => generateApprovalLetter(selectedRequest)}
                    className="px-4 py-2 border rounded hover:bg-gray-100 flex items-center gap-2"
                  >
                    <FaFilePdf /> Generate {selectedRequest.status === 'approved' ? 'Approval' : 'Rejection'} Letter
                  </button>
                )}
                {NO_FUEL_PRICE_TYPES.includes(selectedRequest.serviceType) && selectedRequest.status === 'approved' && (
                  <button 
                    type="button" 
                    onClick={handleGenerateCertificate}
                    disabled={generatingCert || printBlocked}
                    title={printBlocked ? 'Department Head approval is required before printing the certificate' : 'Generate certificate'}
                    className={`px-4 py-2 border rounded flex items-center gap-2 ${generatingCert || printBlocked ? 'bg-gray-100 text-gray-500 cursor-not-allowed' : 'hover:bg-gray-100'}`}
                  >
                    <FaFilePdf /> {generatingCert ? 'Generating...' : selectedRequest.certificate_path ? 'Regenerate Certificate' : 'Generate Certificate'}
                  </button>
                )}
                {selectedRequest.certificate_path && (
                  <a 
                    href={`${API_URL.replace('/api', '')}${selectedRequest.certificate_path}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="px-4 py-2 border rounded bg-green-50 text-green-600 hover:bg-green-100 flex items-center gap-2"
                  >
                    <FaDownload /> Download Certificate
                  </a>
                )}
                <div className="flex gap-2 flex-wrap">
                  <button type="button" onClick={() => { setSelectedRequest(null); setApprovedLetter(null); setResponseData({ status: '', response_reason: '' }); }} className="px-4 py-2 border rounded hover:bg-gray-100">
                    Close
                  </button>
                  {(canTransferForward || currentRole === 'TeamLeader') && (
                    <button type="button" onClick={() => {
                      setTransferTargetRole(currentRole === 'TeamLeader' ? 'BackOfficer' : '');
                      setSelectedBackOfficer('');
                      setTransferMessage('');
                      setShowTransferModal(true);
                    }} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-2">
                      <FaExchangeAlt /> Transfer for Review
                    </button>
                  )}
                  {!isBackOfficerRequestWorkflow && canReturnToDeptHead && (
                    <button type="button" onClick={() => { setTransferMessage(''); setShowReturnDeptModal(true); }} className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center gap-2">
                      <FaUndo /> Return to Department Head
                    </button>
                  )}
                  {!isBackOfficerRequestWorkflow && canReturnToCustomer && (
                    <button type="button" onClick={() => { setReturnCustomerReason(''); setShowReturnCustomerModal(true); }} className="px-4 py-2 bg-amber-500 text-white rounded hover:bg-amber-600 flex items-center gap-2">
                      <FaExclamationCircle /> Return to Customer
                    </button>
                  )}
                  {!isBackOfficerRequestWorkflow && (!canReturnToDeptHead || String(selectedRequest.assignedTo) === String(localStorage.getItem('userId'))) && (
                    <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-600">
                      Submit Response
                    </button>
                  )}
                </div>
              </div>
            </form>

            {workflowHistory.length > 0 && (
              <div className="mt-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="font-bold text-sm text-gray-700 mb-3">Workflow History</h3>
                <div className="space-y-2">
                  {workflowHistory.map(h => {
                    const actionLabel = h.assigned_role === 'Customer'
                      ? 'Returned to customer for correction'
                      : h.assigned_role === 'DeptHead'
                        ? 'Returned to Department Head for approval'
                        : `Transferred to ${h.assigned_role}${h.assigned_to_name ? ' (' + h.assigned_to_name + ')' : ''}`;
                    return (
                      <div key={h.id} className="text-sm border-l-4 border-blue-400 pl-3 py-1">
                        <p className="text-gray-800">
                          <strong>{actionLabel}</strong>
                          {h.assigned_by_name && <span className="text-gray-500"> — by {h.assigned_by_name}</span>}
                        </p>
                        <p className="text-xs text-gray-500">{h.transferred_at ? new Date(h.transferred_at).toLocaleString() : ''}</p>
                        {h.transfer_message && <p className="text-xs text-gray-700 mt-1 italic">"{h.transfer_message}"</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {showTransferModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                  <h3 className="text-lg font-bold mb-4">Transfer for Review</h3>
                  {(canTransferToTL || canTransferToBO) && currentRole !== 'TeamLeader' && (
                    <div className="mb-4">
                      <label className="block text-sm font-medium mb-1">Transfer to:</label>
                      <select
                        value={transferTargetRole}
                        onChange={(e) => { setTransferTargetRole(e.target.value); setSelectedBackOfficer(''); }}
                        className="w-full border rounded px-3 py-2"
                      >
                        <option value="">-- Select role --</option>
                        {canTransferToTL && <option value="TeamLeader">Team Leader (for reviewing)</option>}
                        {canTransferToBO && <option value="BackOfficer">Back Officer (for reviewing)</option>}
                      </select>
                    </div>
                  )}
                  {transferTargetRole === 'BackOfficer' || transferTargetRole === 'TeamLeader' ? (
                    <>
                      <p className="text-sm text-gray-600 mb-2">Select a {transferTargetRole === 'TeamLeader' ? 'Team Leader' : 'Back Officer'}:</p>
                      <select
                        value={selectedBackOfficer}
                        onChange={(e) => setSelectedBackOfficer(e.target.value)}
                        className="w-full border rounded px-3 py-2 mb-4"
                      >
                        <option value="">-- Select user --</option>
                        {Object.entries(transferCandidateGroups).map(([sector, users]) => (
                          <optgroup key={sector} label={sector}>
                            {users.map(o => (
                              <option key={o.id} value={o.id}>{o.fullname || o.username} (ID: {o.id})</option>
                            ))}
                          </optgroup>
                        ))}
                      </select>
                      {transferCandidates.length === 0 && (
                        <p className="text-sm text-red-500 mb-4">No {transferTargetRole === 'TeamLeader' ? 'Team Leader' : 'Back Officer'} users found</p>
                      )}
                      {selectedBackOfficer && (() => {
                        const officer = transferCandidates.find(o => String(o.id) === String(selectedBackOfficer));
                        if (!officer) return null;
                        return (
                          <div className="mb-4 p-3 bg-gray-50 border border-gray-200 rounded">
                            <p className="text-sm font-medium text-gray-700 mb-1">Selected User Details</p>
                            <p className="text-sm"><strong>Name:</strong> {officer.fullname || officer.username || 'N/A'}</p>
                            {officer.phone && <p className="text-sm"><strong>Phone:</strong> {officer.phone}</p>}
                            {officer.email && <p className="text-sm"><strong>Email:</strong> {officer.email}</p>}
                            {officer.sector && <p className="text-sm"><strong>Sector:</strong> {officer.sector}</p>}
                          </div>
                        );
                      })()}
                    </>
                  ) : null}
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Message (optional)</label>
                    <textarea
                      value={transferMessage}
                      onChange={(e) => setTransferMessage(e.target.value)}
                      className="w-full border rounded px-3 py-2"
                      rows="3"
                      placeholder="Enter a message explaining the reason for transfer..."
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setShowTransferModal(false)} className="px-4 py-2 border rounded hover:bg-gray-100">
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        if (!transferTargetRole) { toast.error('Please select a target role'); return; }
                        if (transferTargetRole !== 'DeptHead' && !selectedBackOfficer) { toast.error('Please select a user'); return; }
                        try {
                          await api.post('/request-assignments/transfer', {
                            service_type: selectedRequest.serviceType,
                            request_id: selectedRequest.id,
                            assigned_role: transferTargetRole,
                            assigned_to: selectedBackOfficer || null,
                            transfer_message: transferMessage || null
                          });
                          toast.success(`Request transferred to ${transferTargetRole}`);
                          setShowTransferModal(false);
                          setSelectedRequest(null);
                          fetchRequests();
                        } catch (error) {
                          toast.error('Failed to transfer request: ' + (error.response?.data?.error || error.message));
                        }
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                      disabled={!transferTargetRole}
                    >
                      Transfer
                    </button>
                  </div>
                </div>
              </div>
            )}

            {showReturnDeptModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                  <h3 className="text-lg font-bold mb-2">Return to Department Head</h3>
                  <p className="text-sm text-gray-600 mb-4">This reviewed request will be returned to the Department Head for approval.</p>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Review note (optional)</label>
                    <textarea
                      value={transferMessage}
                      onChange={(e) => setTransferMessage(e.target.value)}
                      className="w-full border rounded px-3 py-2"
                      rows="3"
                      placeholder="Add a review summary or comment..."
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setShowReturnDeptModal(false)} className="px-4 py-2 border rounded hover:bg-gray-100">
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={async () => {
                        try {
                          await api.post('/request-assignments/transfer', {
                            service_type: selectedRequest.serviceType,
                            request_id: selectedRequest.id,
                            assigned_role: 'DeptHead',
                            assigned_to: null,
                            transfer_message: transferMessage
                              ? `For Approval: ${transferMessage}`
                              : 'For Approval: Department Head approval required'
                          });
                          toast.success('Request returned to Department Head for approval');
                          setShowReturnDeptModal(false);
                          setSelectedRequest(null);
                          fetchRequests();
                        } catch (error) {
                          toast.error('Failed to return request: ' + (error.response?.data?.error || error.message));
                        }
                      }}
                      className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700"
                    >
                      Return to Department Head
                    </button>
                  </div>
                </div>
              </div>
            )}

            {showReturnCustomerModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 w-full max-w-md">
                  <h3 className="text-lg font-bold mb-2">Return to Customer</h3>
                  <p className="text-sm text-gray-600 mb-4">The customer will be notified to correct their application information.</p>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Problem / what must be corrected *</label>
                    <textarea
                      value={returnCustomerReason}
                      onChange={(e) => setReturnCustomerReason(e.target.value)}
                      className="w-full border rounded px-3 py-2"
                      rows="4"
                      placeholder="Describe the problem found and what information the customer should correct..."
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setShowReturnCustomerModal(false)} className="px-4 py-2 border rounded hover:bg-gray-100">
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleReturnToCustomer}
                      className="px-4 py-2 bg-amber-500 text-white rounded hover:bg-amber-600"
                    >
                      Return to Customer
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {documentViewerRequest && (
        <DocumentViewerModal
          request={documentViewerRequest}
          paymentOnly={!!documentViewerRequest.__paymentOnly}
          onClose={() => setDocumentViewerRequest(null)}
        />
      )}
    </div>
  );
}

function Login({ onLogin }) {
  const [searchParams] = useSearchParams();
  const resetToken = searchParams.get('token');
  const [mode, setMode] = useState(resetToken ? 'reset' : 'login');
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    if (resetToken) setMode('reset');
  }, [resetToken]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (mode === 'register') {
        const response = await api.post('/auth/register', { ...formData, email, fullName });
        localStorage.setItem('username', response.data.user.username);
        localStorage.setItem('role', response.data.user.role || 'BackOfficer');
        localStorage.setItem('sector', response.data.user.sector || '');
        localStorage.setItem('userId', response.data.user.id);
        localStorage.setItem('services', JSON.stringify(response.data.user.services || []));
        toast.success('Registration successful!');
        onLogin();
      } else if (mode === 'forgot') {
        await api.post('/auth/forgot-password', { email });
        toast.success('If an account with that email exists, a password reset link has been sent.');
        setMode('login');
      } else if (mode === 'reset') {
        if (!resetToken) {
          toast.error('Invalid reset link. Please request a new password reset.');
          return;
        }
        if (newPassword !== confirmPassword) {
          toast.error('Passwords do not match');
          return;
        }
        await api.post('/auth/reset-password', { token: resetToken, newPassword });
        toast.success('Password reset successfully. Please sign in.');
        navigate('/');
        setMode('login');
      } else {
        const response = await api.post('/auth/login', formData);
        localStorage.setItem('username', response.data.user.username);
        localStorage.setItem('role', response.data.user.role || 'BackOfficer');
        localStorage.setItem('sector', response.data.user.sector || '');
        localStorage.setItem('userId', response.data.user.id);
        localStorage.setItem('services', JSON.stringify(response.data.user.services || []));
        localStorage.setItem('must_change_password', response.data.user.must_change_password ? 'true' : 'false');
        toast.success('Login successful!');
        onLogin();
      }
    } catch (error) {
      toast.error(error.response?.data?.error || 'Request failed');
    }
  };

  const titles = {
    login: 'PEA Backoffice',
    register: 'Create Account',
    forgot: 'Forgot Password',
    reset: 'Reset Password'
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="bg-white rounded-lg shadow-lg p-4 md:p-8 w-full max-w-md">
        <h2 className="text-2xl font-bold text-center mb-4 text-[#006400]">
          {titles[mode]}
        </h2>
        {mode !== 'reset' && (
          <div className="flex mb-6 border border-gray-300 rounded overflow-hidden">
            <button type="button" onClick={() => setMode('login')} className={`flex-1 py-2 text-sm font-medium ${mode === 'login' ? 'bg-[#006400] text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
              Login
            </button>
            <button type="button" onClick={() => setMode('forgot')} className={`flex-1 py-2 text-sm font-medium border-l border-gray-300 ${mode === 'forgot' ? 'bg-[#006400] text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}>
              Forgot Password
            </button>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          {(mode === 'login' || mode === 'register') && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Username</label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>
              {mode === 'register' && (
                <>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Full Name</label>
                    <input
                      type="text"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="w-full border rounded px-3 py-2"
                      required
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium mb-1">Email</label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full border rounded px-3 py-2"
                      required
                    />
                  </div>
                </>
              )}
            </>
          )}
          {(mode === 'login' || mode === 'register') && (
            <div className="mb-6">
              <label className="block text-sm font-medium mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full border rounded px-3 py-2 pr-10"
                  required
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
          )}
          {mode === 'forgot' && (
            <div className="mb-6">
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border rounded px-3 py-2"
                placeholder="Enter your account email"
                required
              />
            </div>
          )}
          {mode === 'reset' && (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  minLength="4"
                  required
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium mb-1">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full border rounded px-3 py-2"
                  minLength="4"
                  required
                />
              </div>
            </>
          )}
          <button type="submit" className="w-full bg-[#4CAF50] text-white py-2 rounded hover:bg-green-600">
            {mode === 'login' && 'Sign In'}
            {mode === 'register' && 'Register'}
            {mode === 'forgot' && 'Send Reset Link'}
            {mode === 'reset' && 'Reset Password'}
          </button>
        </form>
        <button
          type="button"
          onClick={() => {
            setMode('login');
            setFormData({ username: '', password: '' });
            setEmail('');
            setFullName('');
            setNewPassword('');
            setConfirmPassword('');
            navigate('/');
          }}
          className="w-full mt-3 border py-2 rounded hover:bg-gray-100"
        >
          Cancel
        </button>
        <div className="mt-4 text-center text-sm space-y-2">
          {mode === 'login' && (
            <p>
              Forgot your password?{' '}
              <button type="button" onClick={() => setMode('forgot')} className="text-green-700 font-semibold hover:underline">
                Reset Password
              </button>
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function LatestNews() {
  const [news, setNews] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [formData, setFormData] = useState({ title: '', content: '', title_amharic: '', is_active: true });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      const response = await api.get('/news');
      setNews(response.data);
    } catch (error) {
      toast.error('Failed to fetch news');
    }
  };

  const contentRef = React.createRef();

  const insertFormat = (before, after) => {
    const textarea = contentRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = formData.content;
      const newText = text.substring(0, start) + before + text.substring(start, end) + after + text.substring(end);
      setFormData({ ...formData, content: newText });
      setTimeout(() => {
        textarea.selectionStart = start + before.length;
        textarea.selectionEnd = start + before.length + (end - start);
        textarea.focus();
      }, 0);
    }
  };

  const insertLink = () => {
    const textarea = contentRef.current;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const selectedText = formData.content.substring(start, end);
      let url = window.prompt('Enter URL:', 'https://');
      if (url && selectedText) {
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
          url = 'https://' + url;
        }
        const newText = formData.content.substring(0, start) + '<a href="' + url + '" target="_blank" rel="noopener noreferrer">' + selectedText + '</a>' + formData.content.substring(end);
        setFormData({ ...formData, content: newText });
      }
    }
  };

  const [contextMenu, setContextMenu] = useState(null);

  const handleContextMenu = (e) => {
    const textarea = contentRef.current;
    if (textarea && textarea.selectionStart !== textarea.selectionEnd) {
      e.preventDefault();
      setContextMenu({ x: e.clientX, y: e.clientY });
    }
  };

  useEffect(() => {
    const closeMenu = () => setContextMenu(null);
    if (contextMenu) {
      document.addEventListener('click', closeMenu);
      return () => document.removeEventListener('click', closeMenu);
    }
  }, [contextMenu]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingNews) {
        await api.put(`/news/${editingNews.id}`, formData);
        toast.success('News updated successfully');
      } else {
        await api.post('/news', formData);
        toast.success('News created successfully');
      }
      setFormData({ title: '', content: '', title_amharic: '', is_active: true });
      setShowForm(false);
      setEditingNews(null);
      fetchNews();
    } catch (error) {
      toast.error('Failed to save news');
    }
  };

  const handleEdit = (item) => {
    setEditingNews(item);
    setFormData({ title: item.title, content: item.content, title_amharic: item.title_amharic, is_active: item.is_active });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this news?')) {
      try {
        await api.delete(`/news/${id}`);
        toast.success('News deleted successfully');
        fetchNews();
      } catch (error) {
        toast.error('Failed to delete news');
      }
    }
  };

  return (
    <div className="container mx-auto p-4 md:p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl md:text-2xl font-bold">ወቅታዊ ዜና ማስገቢያ  / Latest News Management</h2>
        <button onClick={() => { setShowForm(!showForm); setEditingNews(null); setFormData({ title: '', content: '', title_amharic: '', is_active: true }); }} className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-600">
          <FaPlus className="inline mr-2" />
          Add News
        </button>
      </div>

      {showForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-xl font-bold mb-4">{editingNews ? 'Edit News' : 'Add New News'}</h3>
          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium mb-1">Title (English)</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Title (Amharic)</label>
                <input
                  type="text"
                  value={formData.title_amharic}
                  onChange={(e) => setFormData({ ...formData, title_amharic: e.target.value })}
                  className="w-full border rounded px-3 py-2"
                />
              </div>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Content</label>
              <div className="border rounded">
                <div className="flex flex-wrap gap-1 p-2 bg-gray-100 border-b">
                  <button type="button" onClick={() => insertFormat('<b>', '</b>')} className="px-3 py-1 border rounded hover:bg-gray-200 font-bold">B</button>
                  <button type="button" onClick={() => insertFormat('<i>', '</i>')} className="px-3 py-1 border rounded hover:bg-gray-200 italic">I</button>
                  <button type="button" onClick={() => insertFormat('<u>', '</u>')} className="px-3 py-1 border rounded hover:bg-gray-200 underline">U</button>
                  <button type="button" onClick={() => insertFormat('<p>', '</p>')} className="px-3 py-1 border rounded hover:bg-gray-200">P</button>
                  <button type="button" onClick={() => insertFormat('<br/>', '')} className="px-3 py-1 border rounded hover:bg-gray-200">BR</button>
                  <button type="button" onClick={insertLink} className="px-3 py-1 border rounded hover:bg-gray-200 underline" title="Insert Hyperlink">&#128279; Link</button>
                  <span className="border-l mx-1"></span>
                  <button type="button" onClick={() => insertFormat('<p style="text-align:left">', '</p>')} className="px-3 py-1 border rounded hover:bg-gray-200" title="Align Left">&#8678;</button>
                  <button type="button" onClick={() => insertFormat('<p style="text-align:center">', '</p>')} className="px-3 py-1 border rounded hover:bg-gray-200" title="Center">&#8596;</button>
                  <button type="button" onClick={() => insertFormat('<p style="text-align:right">', '</p>')} className="px-3 py-1 border rounded hover:bg-gray-200" title="Align Right">&#8680;</button>
                  <button type="button" onClick={() => insertFormat('<p style="text-align:justify">', '</p>')} className="px-3 py-1 border rounded hover:bg-gray-200" title="Justify">J&#8801;</button>
                </div>
                <textarea
                  ref={contentRef}
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  onContextMenu={handleContextMenu}
                  className="w-full border-0 px-3 py-2"
                  rows="5"
                />
                {contextMenu && (
                  <div
                    className="fixed bg-white border rounded shadow-lg z-50 py-1"
                    style={{ left: contextMenu.x, top: contextMenu.y }}
                  >
                    <button
                      type="button"
                      onClick={() => { insertLink(); setContextMenu(null); }}
                      className="block w-full text-left px-4 py-2 hover:bg-gray-100 text-sm"
                    >
                      &#128279; Insert Link
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div className="mb-4">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                Active
              </label>
            </div>
            <div className="flex gap-2">
              <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-600">
                {editingNews ? 'Update' : 'Save'}
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditingNews(null); }} className="border px-4 py-2 rounded hover:bg-gray-100">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto"><table className="min-w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title (Amharic)</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {news.map((item) => (
              <tr key={item.id}>
                <td className="px-4 py-3">{item.title}</td>
                <td className="px-4 py-3">{item.title_amharic}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs ${item.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                    {item.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3">{new Date(item.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3">
                  <button onClick={() => handleEdit(item)} className="text-blue-600 hover:text-blue-800 mr-3">
                    <FaEdit />
                  </button>
                  <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-800">
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
            {news.length === 0 && (
              <tr>
                <td colSpan="5" className="px-4 py-8 text-center text-gray-500">No news available</td>
              </tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function TemplateManager() {
  const [templates, setTemplates] = useState([]);
  const [tables, setTables] = useState([]);
  const [columns, setColumns] = useState([]);
  const [placeholders, setPlaceholders] = useState([]);
  const [manualEntries, setManualEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showGenerate, setShowGenerate] = useState(null);
  const [recordId, setRecordId] = useState('');
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', table_name: '', mappings: {} });
  const [file, setFile] = useState(null);

  useEffect(() => {
    fetchTemplates();
    fetchTables();
  }, []);

  const fetchTemplates = async () => {
    try {
      const res = await api.get('/templates');
      setTemplates(res.data);
    } catch (e) { toast.error('Failed to load templates'); }
  };

  const fetchTables = async () => {
    try {
      const res = await api.get('/templates/tables/list');
      setTables(res.data);
    } catch (e) { /* ignore */ }
  };

  const fetchColumns = async (table) => {
    try {
      const res = await api.get(`/templates/tables/${table}/columns`);
      setColumns(res.data);
    } catch (e) { setColumns([]); }
  };

  const handleTableChange = (table) => {
    setForm(f => ({ ...f, table_name: table, mappings: {} }));
    setColumns([]);
    setPlaceholders([]);
    setManualEntries([]);
    if (table) fetchColumns(table);
  };

  const handleFileChange = async (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    const fd = new FormData();
    fd.append('file', f);
    try {
      const res = await api.post('/templates/scan', fd);
      const keys = res.data.placeholders || [];
      setPlaceholders(keys);
      const m = {};
      keys.forEach(k => { m[k] = k; });
      setForm(fm => ({ ...fm, mappings: m }));
    } catch (e) { toast.error('Failed to scan template'); }
  };

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', description: '', table_name: '', mappings: {} });
    setFile(null);
    setPlaceholders([]);
    setManualEntries([]);
    setColumns([]);
    setShowForm(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({ name: t.name, description: t.description || '', table_name: t.table_name, mappings: t.placeholder_mappings || {} });
    setFile(null);
    setPlaceholders(Object.keys(t.placeholder_mappings || {}));
    setManualEntries([]);
    setColumns([]);
    setShowForm(true);
    if (t.table_name) fetchColumns(t.table_name);
  };

  const handleSave = async () => {
    if (!form.name || !form.table_name) {
      return toast.error('Name and table are required');
    }
    const fd = new FormData();
    fd.append('name', form.name);
    fd.append('description', form.description);
    fd.append('table_name', form.table_name);
    fd.append('placeholder_mappings', JSON.stringify(form.mappings));
    if (file) fd.append('file', file);
    try {
      if (editing) {
        await api.put(`/templates/${editing.id}`, fd);
        toast.success('Template updated');
      } else {
        if (!file) return toast.error('Please select a template file');
        await api.post('/templates', fd);
        toast.success('Template created');
      }
      setShowForm(false);
      fetchTemplates();
    } catch (e) { toast.error('Failed to save template'); }
  };

  const handleDelete = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try {
      await api.delete(`/templates/${t.id}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (e) { toast.error('Failed to delete template'); }
  };

  const handleGenerate = async () => {
    if (!recordId) return toast.error('Enter a record ID');
    setGenerating(true);
    try {
      const res = await api.post('/templates/generate', { template_id: showGenerate.id, record_id: parseInt(recordId) }, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${showGenerate.name.replace(/\s+/g, '_')}_${recordId}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Document generated');
      setShowGenerate(null);
      setRecordId('');
    } catch (e) { toast.error('Failed to generate document'); }
    finally { setGenerating(false); }
  };

  return (
    <div className="container mx-auto p-4 md:p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl md:text-2xl font-bold">ተቀርጾ የተዘጋጁ ሰነዶች / Template Manager</h2>
        <button onClick={openCreate} className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 flex items-center gap-2">
          <FaPlus /> አዲስ ሰነድ ጨምር / Add Template
        </button>
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">{editing ? 'Edit Template' : 'Add Template'}</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-gray-700"><FaTimes /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Template Name / የሰነድ ስም</label>
                <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  className="w-full border rounded px-3 py-2" placeholder="e.g. Approval Letter" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Description / መግለጫ</label>
                <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full border rounded px-3 py-2" rows="2" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">DB Table / የውሂብ ጎታ ሰንጠረዥ</label>
                <select value={form.table_name} onChange={e => handleTableChange(e.target.value)}
                  className="w-full border rounded px-3 py-2">
                  <option value="">-- Select Table --</option>
                  {tables.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Template File (.docx) / የሰነድ ፋይል</label>
                <input type="file" accept=".docx" onChange={handleFileChange}
                  className="w-full border rounded px-3 py-2" />
                {!editing && placeholders.length === 0 && file && <p className="text-sm text-yellow-600 mt-1">Scanning for placeholders...</p>}
                {editing && <p className="text-sm text-gray-500 mt-1">Leave empty to keep current file</p>}
              </div>
              {form.table_name && columns.length > 0 && (
                <div>
                  <label className="block text-sm font-medium mb-1">Placeholder Mapping / የቦታ ያዥ ካርታ</label>
                  <p className="text-xs text-gray-500 mb-2">Map each template placeholder {`{key}`} to a database column. Placeholders detected from the .docx file appear automatically.</p>
                  <table className="w-full text-sm border">
                    <thead><tr className="bg-gray-100"><th className="px-3 py-1 text-left">Placeholder</th><th className="px-3 py-1 text-left">DB Column</th><th className="px-3 py-1 text-center">Action</th></tr></thead>
                    <tbody>
                      {placeholders.map(k => (
                        <tr key={k}>
                          <td className="px-3 py-1 font-mono">{`{${k}}`}</td>
                          <td className="px-3 py-1">
                            <select value={form.mappings[k] || ''} onChange={e => setForm(f => ({ ...f, mappings: { ...f.mappings, [k]: e.target.value } }))}
                              className="w-full border rounded px-2 py-1">
                              <option value="">-- Select --</option>
                              {columns.map(c => <option key={c.name} value={c.name}>{c.name} ({c.type})</option>)}
                            </select>
                          </td>
                          <td className="px-3 py-1 text-center text-xs text-gray-400">auto</td>
                        </tr>
                      ))}
                      {manualEntries.map((entry, idx) => (
                        <tr key={`manual-${idx}`}>
                          <td className="px-3 py-1">
                            <input type="text" value={entry}
                              onChange={e => {
                                const oldVal = entry;
                                const newVal = e.target.value;
                                const next = [...manualEntries];
                                next[idx] = newVal;
                                setManualEntries(next);
                                setForm(f => {
                                  const m = { ...f.mappings };
                                  if (oldVal && oldVal !== newVal) delete m[oldVal];
                                  if (newVal) m[newVal] = f.mappings[oldVal] || '';
                                  return { ...f, mappings: m };
                                });
                              }}
                              className="w-full border rounded px-2 py-1 font-mono text-xs" placeholder="Enter key" />
                          </td>
                          <td className="px-3 py-1">
                            <select value={form.mappings[entry] || ''} onChange={e => {
                              const col = e.target.value;
                              const oldKey = entry;
                              const next = [...manualEntries];
                              next[idx] = col;
                              setManualEntries(next);
                              setForm(f => {
                                const m = { ...f.mappings };
                                delete m[oldKey];
                                if (col) m[col] = col;
                                return { ...f, mappings: m };
                              });
                            }}
                              className="w-full border rounded px-2 py-1">
                              <option value="">-- Select --</option>
                              {columns.map(c => <option key={c.name} value={c.name}>{c.name} ({c.type})</option>)}
                            </select>
                          </td>
                          <td className="px-3 py-1 text-center">
                            <button onClick={() => {
                              setManualEntries(manualEntries.filter((_, i) => i !== idx));
                              setForm(f => {
                                const m = { ...f.mappings };
                                delete m[entry];
                                return { ...f, mappings: m };
                              });
                            }} className="text-red-500 hover:text-red-700"><FaTimes /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <button onClick={() => setManualEntries([...manualEntries, ''])}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-800">
                    + Add Mapping
                  </button>
                </div>
              )}
              <div className="flex justify-end gap-2 pt-4">
                <button onClick={() => setShowForm(false)} className="px-4 py-2 border rounded hover:bg-gray-100">Cancel</button>
                <button onClick={handleSave} className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
                  {editing ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showGenerate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold">Generate / ማመንጨት</h3>
              <button onClick={() => { setShowGenerate(null); setRecordId(''); }} className="text-gray-500 hover:text-gray-700"><FaTimes /></button>
            </div>
            <p className="mb-3 text-sm">Template: <strong>{showGenerate.name}</strong></p>
            <p className="mb-3 text-sm">Table: <strong>{showGenerate.table_name}</strong></p>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Record ID / የመረጃ መለያ ቁጥር</label>
              <input type="number" value={recordId} onChange={e => setRecordId(e.target.value)}
                className="w-full border rounded px-3 py-2" placeholder="Enter record ID" />
            </div>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setShowGenerate(null); setRecordId(''); }} className="px-4 py-2 border rounded hover:bg-gray-100">Cancel</button>
              <button onClick={handleGenerate} disabled={generating}
                className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50">
                {generating ? 'Generating...' : 'Generate / አመንጭ'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left">ID</th>
              <th className="px-4 py-3 text-left">Name / ስም</th>
              <th className="px-4 py-3 text-left">Table / ሰንጠረዥ</th>
              <th className="px-4 py-3 text-left">Mappings / ካርታዎች</th>
              <th className="px-4 py-3 text-left">Created / የተፈጠረበት</th>
              <th className="px-4 py-3 text-center">Actions / ተግባራት</th>
            </tr>
          </thead>
          <tbody>
            {templates.map(t => (
              <tr key={t.id} className="border-t hover:bg-gray-50">
                <td className="px-4 py-3">{t.id}</td>
                <td className="px-4 py-3 font-medium">{t.name}<br /><span className="text-xs text-gray-500">{t.description}</span></td>
                <td className="px-4 py-3 font-mono text-xs">{t.table_name}</td>
                <td className="px-4 py-3">
                  <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                    {Object.keys(t.placeholder_mappings || {}).length} placeholders
                  </span>
                </td>
                <td className="px-4 py-3 text-xs">{new Date(t.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-3 text-center">
                  <div className="flex justify-center gap-2">
                    <button onClick={() => setShowGenerate(t)} className="text-green-600 hover:text-green-800" title="Generate Document">
                      <FaDownload />
                    </button>
                    <button onClick={() => openEdit(t)} className="text-blue-600 hover:text-blue-800" title="Edit">
                      <FaEdit />
                    </button>
                    <button onClick={() => handleDelete(t)} className="text-red-600 hover:text-red-800" title="Delete">
                      <FaTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {templates.length === 0 && (
              <tr><td colSpan="6" className="px-4 py-8 text-center text-gray-500">No templates yet. Click "Add Template" to create one.</td></tr>
            )}
          </tbody>
        </table>
        </div>
      </div>
  );
}

function ArchivedServices() {
  const [data, setData] = useState({ data: [], total: 0, page: 1 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const role = localStorage.getItem('role') || '';
  const canDelete = role === 'SuperAdmin';

  useEffect(() => { fetchArchive(); }, []);

  const fetchArchive = async (p = 1) => {
    setLoading(true);
    try {
      const params = { page: p, limit: 50 };
      if (search) params.search = search;
      const res = await api.get('/archive', { params });
      setData(res.data);
    } catch (e) { toast.error('Failed to load archive'); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this record from archive?')) return;
    try {
      await api.delete(`/archive/${id}`);
      toast.success('Removed from archive');
      fetchArchive(data.page);
    } catch (e) { toast.error('Failed to remove'); }
  };

  const rd = (d) => d && new Date(d).toLocaleDateString('en-GB');
  const st = (s) => s === 'approved' ? 'bg-green-500' : s === 'rejected' ? 'bg-red-500' : s === 'payment_pending' ? 'bg-purple-500' : s === 'paid' ? 'bg-teal-500' : 'bg-yellow-500';

  return (
    <div className="container mx-auto p-4 md:p-6">
      <h2 className="text-xl md:text-2xl font-bold mb-6">ማህደር / Archived Services ({data.total})</h2>
      <div className="flex gap-2 mb-4">
        <input
          type="text" placeholder="Search by reference or content..."
          value={search} onChange={e => setSearch(e.target.value)}
          className="flex-1 px-4 py-2 border rounded-lg"
          onKeyDown={e => e.key === 'Enter' && fetchArchive()}
        />
        <button onClick={() => fetchArchive()} className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600">Search</button>
      </div>
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : data.data.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No archived records found.</div>
        ) : (
          <div className="overflow-x-auto"><table className="min-w-full">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reference No</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source Table</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Archived At</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.data.map(a => (
                <React.Fragment key={a.id}>
                  <tr className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-mono text-sm">{a.reference_no}</td>
                    <td className="px-4 py-3 text-sm">{a.source_table}</td>
                    <td className="px-4 py-3"><span className={`px-2 py-0.5 text-white text-xs rounded ${st(a.status)}`}>{a.status}</span></td>
                    <td className="px-4 py-3 text-sm">{rd(a.archived_at)}</td>
                    <td className="px-4 py-3 flex gap-2">
                      <button onClick={() => setExpanded(expanded === a.id ? null : a.id)} className="text-blue-500 hover:text-blue-700 text-sm">
                        {expanded === a.id ? 'Hide' : 'View'}
                      </button>
                      {canDelete && (
                        <button onClick={() => handleDelete(a.id)} className="text-red-500 hover:text-red-700 text-sm">Delete</button>
                      )}
                    </td>
                  </tr>
                  {expanded === a.id && (
                    <tr>
                      <td colSpan="5" className="px-4 py-6 bg-gray-50">
                        <div className="max-w-3xl mx-auto bg-white border rounded-lg shadow-sm p-8">
                          <div className="text-center mb-6">
                            <img src={process.env.PUBLIC_URL + '/Logo1.png'} alt="PEA Logo" className="h-20 mx-auto mb-3" />
                            <h3 className="text-xl font-bold text-[#006400]">የነዳጅና ኢነርጂ ባለሥልጣን</h3>
                            <p className="text-base text-gray-600 font-medium">Petroleum and Energy Authority</p>
                          </div>
                          {(() => {
                            try {
                              const rd = typeof a.record_data === 'string' ? JSON.parse(a.record_data) : a.record_data;
                              if (!rd || typeof rd !== 'object') return <p className="text-red-500">Invalid record data</p>;
                              const nameKeys = ['organization_name', 'applicant_name', 'company_name', 'fullname', 'name'];
                              const title = a.source_table?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Service Record';
                              const nameField = nameKeys.find(k => rd[k]);
                              const fields = buildArchiveFormFields(rd);
                              const files = extractArchiveFiles(rd);
                              return (
                                <div className="space-y-4">
                                  <div className="border-b pb-3 mb-4">
                                    <h4 className="text-xl font-bold text-center">{title}</h4>
                                    {rd.reference_no && <p className="text-center text-sm text-gray-500 mt-1">Ref: {rd.reference_no}</p>}
                                  </div>
                                  {nameField && (
                                    <div className="bg-gray-100 p-4 rounded">
                                      <span className="text-sm font-semibold text-gray-600">Applicant: </span>
                                      <span className="text-base font-bold">{rd[nameField]}</span>
                                    </div>
                                  )}
                                  {rd.archive_document && (
                                    <a href={`${API_ORIGIN}${toFileUrl(rd.archive_document)}`} target="_blank" rel="noreferrer"
                                      className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 text-sm font-medium">
                                      <FaDownload /> Download Archival Document (DOCX / የማህደር ሰነድ)
                                    </a>
                                  )}
                                  {fields.length > 0 && (
                                    <div>
                                      <h5 className="font-bold text-sm mb-2 text-gray-700">Archived Form / የማህደር ቅፅ</h5>
                                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 border rounded-lg p-4 bg-gray-50">
                                        {fields.map((f, i) => (
                                          <div key={i} className="text-sm flex flex-col">
                                            <span className="text-gray-500 text-xs uppercase tracking-wide">{f.label}</span>
                                            <span className="font-medium break-words whitespace-pre-wrap">{renderFormValue(f.value)}</span>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  <div>
                                    <h5 className="font-bold text-sm mb-2 text-gray-700">All Documents / የማህደር ሰነዶች ({files.length})</h5>
                                    {files.length === 0 ? (
                                      <p className="text-sm text-gray-500">No attached documents stored for this record.</p>
                                    ) : (
                                      <div className="space-y-2">
                                        {files.map((f, i) => (
                                          <a key={i} href={`${API_ORIGIN}${f.path}`} target="_blank" rel="noreferrer"
                                            className="flex items-center justify-between bg-green-50 border border-green-200 rounded px-4 py-3 hover:bg-green-100">
                                            <span className="text-sm font-medium"><FaFile /> {f.label}</span>
                                            <span className="text-xs bg-green-600 text-white px-3 py-1 rounded">Open / Download</span>
                                          </a>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                  {rd.status && (
                                    <div className="mt-2 pt-4 border-t text-center">
                                      <span className={`px-4 py-2 rounded text-sm font-bold text-white ${st(rd.status)}`}>{rd.status.toUpperCase()}</span>
                                    </div>
                                  )}
                                </div>
                              );
                            } catch (e) {
                              return <p className="text-red-500">Error parsing record data</p>;
                            }
                          })()}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>
      {data.total > data.limit && (
        <div className="flex justify-center gap-2 mt-4">
          {Array.from({ length: Math.ceil(data.total / data.limit) }, (_, i) => (
            <button key={i} onClick={() => fetchArchive(i + 1)}
              className={`px-3 py-1 rounded ${data.page === i + 1 ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}>
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const ARCHIVE_FILE_LABELS = {
  application_letter: 'Application Letter / የማመልከቻ ደብዳቤ',
  support_letter: 'Support Letter / የድጋፍ ደብዳቤ',
  aggrement_letter: 'Agreement Letter / የስምምነት ደብዳቤ',
  site_plan_drawing: 'Site Plan / Design Drawing',
  id_photo: 'ID Photo / የመታወቂያ ፎቶ',
  payment_receipt: 'Payment Receipt / የክፍያ ደረሰኝ',
  approved_letter: 'Approved Letter / የተፈቀደ ደብዳቤ',
  approval_letter: 'Approval Letter / የማረጋገጫ ደብዳቤ',
  certificate_path: 'Certificate / የምስክር ወረቀት',
  client_letter: 'Letter Sent to Client / ለደንበኛ የተላከ ደብዳቤ'
};

const ARCHIVE_FORM_LABELS = {
  fdc: 'FDC', remark: 'Remark', letter_no: 'Letter No', document_no: 'Document No',
  site_zone: 'Site Zone', site_region: 'Site Region', site_woreda: 'Site Woreda', site_subcity: 'Site Sub City', site_specific_place: 'Site Specific Place',
  manager_name: 'Manager Name', manager_idno: 'Manager ID No', manager_mobno: 'Manager Mobile', manager_telno: 'Manager Telephone', manager_region: 'Manager Region', manager_wereda: 'Manager Woreda', manager_hno: 'Manager House No', manager_k_ketema: 'Manager Kebele', manager_nationality: 'Manager Nationality',
  gas_station_name: 'Gas Station Name', fuel_station_name: 'Fuel Station Name', gas_station_region: 'Gas Station Region', gas_station_zone: 'Gas Station Zone', gas_station_woreda: 'Gas Station Woreda', gas_station_subcity: 'Gas Station Sub City', gas_station_specific_place: 'Gas Station Specific Place',
  plate_number: 'Plate Number', trailer_plate_number: 'Trailer Plate Number', replaced_plate_number: 'Replaced Plate Number', replacing_plate_number: 'Replacing Plate Number', replaced_trailer_plate_number: 'Replaced Trailer Plate Number', replacing_trailer_plate_number: 'Replacing Trailer Plate Number',
  changing_truck: 'Changing Truck', change_of_place: 'Change of Place', detailed_reason: 'Detailed Reason', response_reason: 'Response Reason', rejection_reason: 'Rejection Reason',
  fuel_load_amount: 'Fuel Load Amount (litres)', fuel_loading_date: 'Fuel Loading Date', loading_fuel_type: 'Loading Fuel Type', fuel_distributer_name: 'Fuel Distributer Name',
  first_destination_name: 'First Destination Name', first_destination_region: 'First Destination Region', first_destination_woreda: 'First Destination Woreda', first_destination_specific_place: 'First Destination Specific Place',
  request_date: 'Request Date', decision_date: 'Decision Date'
};

const toFileUrl = (p) => {
  if (!p) return p;
  let clean = String(p).replace(/\\/g, '/');
  if (!clean.startsWith('/') && !/^https?:\/\//i.test(clean)) clean = '/' + clean;
  return clean;
};

const extractArchiveFiles = (rd) => {
  const files = [];
  Object.entries(rd || {}).forEach(([k, v]) => {
    if (k === 'archive_document') return;
    if (typeof v === 'string' && /^(\/)?uploads\/.*\.\w+$/.test(v.replace(/\\/g, '/'))) files.push({ label: ARCHIVE_FILE_LABELS[k] || k.replace(/_/g, ' '), path: toFileUrl(v) });
  });
  let extra = rd?.additional_attachments;
  if (typeof extra === 'string') { try { extra = JSON.parse(extra); } catch (e) { extra = []; } }
  if (Array.isArray(extra)) {
    extra.forEach((a, i) => {
      const p = typeof a === 'string' ? a : a?.file;
      if (p && /^(\/)?uploads\/.*\.\w+$/.test(String(p).replace(/\\/g, '/'))) files.push({ label: (typeof a === 'object' && a?.name) || `Attachment ${i + 1}`, path: toFileUrl(p) });
    });
  }
  return files;
};

const archiveFormFields = (rd) => {
  const SKIP = ['id', 'user_id', 'status', 'created_at', 'updated_at', 'additional_attachments'];
  const out = [];
  Object.entries(rd || {}).forEach(([k, v]) => {
    if (SKIP.includes(k)) return;
    if (typeof v === 'string' && v.startsWith('/uploads/')) return;
    if (Array.isArray(v)) return;
    if (v === null || v === undefined || v === '') return;
    out.push({ label: ARCHIVE_FORM_LABELS[k] || k.replace(/_/g, ' '), value: v });
  });
  return out;
};

const renderFormValue = (v) => {
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/.test(v)) {
    const d = new Date(v);
    return isNaN(d.getTime()) ? v : d.toLocaleString('en-GB');
  }
  return String(v);
};

const downloadArchiveForm = (rd, fields, req) => {
  const lines = ['PEA Archived Record', `Reference No: ${req.reference_no}`, `Service: ${req.source_table}`, `Status: ${req.status}`, ''];
  fields.forEach(f => lines.push(`${f.label}: ${renderFormValue(f.value)}`));
  const blob = new Blob([lines.join('\r\n')], { type: 'text/plain;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `${req.reference_no || 'form'}_form.txt`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
};

// Same order and labels as the direct_fuel_beneficiary_archive.docx template.
const DIRECT_FUEL_TEMPLATE_FIELDS = [
  ['reference_no', 'Ref / ሪፈረንስ ቁጥር'],
  ['organization_name', 'የድርጅቱ ሥም / Organization Name'],
  ['project_name', 'ፐሮጀክት / ኢንዱስትሪ ሥም / Project/Industry Name'],
  ['location', 'አድራሻ / Location'],
  ['manager_name', 'የስራ አስኪያጅ ስም / Manager Name'],
  ['nationality', 'ዜግነት / Nationality'],
  ['fan_national_id', 'የፋይዳ ተለዋጭ ቁጥር / FAN National ID'],
  ['region', 'ክልል / Region'],
  ['subcity', 'Subcity / ክፍለ ከተማ'],
  ['woreda', 'ወረዳ / ቀበሌ / Wereda/Kebele'],
  ['hno', 'የቤት ቁጥር / HNO'],
  ['phone_no', 'ስልክ ቁጥር / Phone No'],
  ['email', 'ኢሜይል / Email'],
  ['po_box', 'ፖስታ ሳጥን / PO Box'],
  ['product_type', 'የምርት አይነት / Product/Work Type'],
  ['product_amount', 'የምርት መጠን / Product Amount'],
  ['no_of_machines', 'የማሽን ብዛት / No. of Machines'],
  ['type_of_machine', 'የማሽን አይነት / Type of Machine'],
  ['fuel_type', 'የነዳጅ አይነት / Fuel Type'],
  ['amount_of_fuel', 'የነዳጅ መጠን / Amount of Fuel'],
  ['monthly_fuel_need', 'ወርሃዊ የነዳጅ ፍጆታ / Monthly Fuel Consumption'],
  ['project_end_date', 'ፐሮጀክቱ የሚያልቅበት ጊዜት / Project End Date'],
  ['supplier_name', 'አቅራቢ ድርጅት / Supplier Name'],
  ['fuel_distributer', 'የነዳጅ አከፋፋይ ድርጅት / Fuel Distributer'],
  ['fuel_distributor_letter_no', 'የነዳጅ አከፋፋይ ደብዳቤ ቁጥር / Fuel Distributer Letter No'],
  ['fuel_distributor_letter_date', 'የነዳጅ አከፋፋይ ደብዳቤ ቀን / Fuel Distributer Letter Date'],
  ['support_organization_name', 'የድጋፍ ሰጪ ተቋም ሥም / Name of Supporting Organization'],
  ['support_org_letter_no', 'የድጋፍ ሰጪ ተቋም ደብዳቤ ቁጥር / Support Organization Letter No'],
  ['support_org_letter_date', 'የድጋፍ ሰጪ ተቋም ደብዳቤ ቀን / Support Organization Letter Date'],
  ['request_date', 'Request Date / የጥያቄ ቀን'],
  ['decision_date', 'Decision Date / የውሳኔ ቀን'],
  ['request_letter_no', 'Request Letter No / የጥያቄ ደብዳቤ ቁጥር'],
  ['request_letter_date', 'Request Letter Date / የጥያቄ ደብዳቤ ቀን'],
  ['payment_fee', 'Payment Fee (ETB) / የክፍያ መጠን'],
  ['renewal_year', 'Renewal Year / የእድሳት ዓመት'],
  ['response_reason', 'Response Reason / የምላሽ ምክንያት']
];

const buildArchiveFormFields = (rd) => {
  const SKIP = new Set(['id', 'user_id', 'password', 'service_type', 'created_at', 'updated_at', 'additional_attachments', 'archive_document']);
  const FILE_KEYS = new Set(['application_letter', 'support_letter', 'aggrement_letter', 'approval_letter', 'approved_letter', 'payment_receipt', 'certificate_path', 'id_photo', 'client_letter', 'existing_approved_letter']);
  const fields = [];
  const added = new Set();
  const add = (key, label) => {
    const v = rd && rd[key];
    if (v === null || v === undefined || v === '') return;
    if (typeof v === 'object') return;
    if (FILE_KEYS.has(key)) return;
    fields.push({ label, value: v });
    added.add(key);
  };
  DIRECT_FUEL_TEMPLATE_FIELDS.forEach(([key, label]) => {
    if (!(key in (rd || {}))) return;
    add(key, label);
  });
  Object.keys(rd || {}).sort().forEach((key) => {
    if (SKIP.has(key) || added.has(key) || FILE_KEYS.has(key)) return;
    const v = rd[key];
    if (v === null || v === undefined || v === '') return;
    if (typeof v === 'object') return;
    if (typeof v === 'string' && /^(\/)?uploads\//.test(v.replace(/\\/g, '/'))) return;
    fields.push({ label: ARCHIVE_FORM_LABELS[key] || key.replace(/_/g, ' '), value: v });
  });
  return fields;
};

function ArchiveRequests() {
  const role = localStorage.getItem('role') || '';
  const isArchive = role === 'Archive';
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [requestingId, setRequestingId] = useState(null);
  const [reason, setReason] = useState('');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [sentDetail, setSentDetail] = useState(null);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const res = await api.get('/archive-requests', { params: statusFilter ? { status: statusFilter } : {} });
      setRequests(res.data);
    } catch (e) { toast.error('Failed to load archive requests'); }
    setLoading(false);
  };

  useEffect(() => { fetchRequests(); }, [statusFilter]);

  const handleSearch = async () => {
    setSearching(true);
    try {
      const res = await api.get('/archive-requests/search', { params: { search: searchTerm } });
      setSearchResults(res.data);
    } catch (e) { toast.error('Search failed'); }
    setSearching(false);
  };

  const submitRequest = async () => {
    if (!reason.trim()) { toast.warning('Please provide a reason for the request'); return; }
    try {
      await api.post('/archive-requests', { archived_id: requestingId, request_reason: reason });
      toast.success('Request submitted to the archive officer');
      setRequestingId(null); setReason('');
      handleSearch();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to submit request'); }
  };

  const respond = async (id, status) => {
    const message = status === 'sent'
      ? (window.prompt('Optional message to the requester:', '') || '')
      : (window.prompt('Reason for rejection:', '') || '');
    if (status === 'rejected' && !message.trim()) { toast.warning('Please provide a rejection reason'); return; }
    try {
      await api.put(`/archive-requests/${id}/respond`, { status, message });
      toast.success(status === 'sent' ? 'File sent to requester' : 'Request rejected');
      fetchRequests();
    } catch (e) { toast.error(e.response?.data?.error || 'Action failed'); }
  };

  const viewSent = async (req) => {
    try {
      const res = await api.get(`/archive-requests/${req.id}`);
      setSentDetail(res.data);
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to open request'); }
  };

  const cancelRequest = async (req) => {
    if (!window.confirm('Cancel this archive request?')) return;
    try { await api.delete(`/archive-requests/${req.id}`); toast.success('Request cancelled'); fetchRequests(); }
    catch (e) { toast.error('Failed to cancel request'); }
  };

  const badge = (s) => {
    const map = { pending: 'bg-yellow-500', sent: 'bg-green-500', rejected: 'bg-red-500' };
    return <span className={`px-2 py-0.5 text-white text-xs rounded ${map[s] || 'bg-gray-500'}`}>{s.toUpperCase()}</span>;
  };

  return (
    <div className="container mx-auto p-4 md:p-6">
      <h2 className="text-xl md:text-2xl font-bold mb-4">የመዝገብ ቤት ጥያቄ / Archive Requests</h2>
      {isArchive && (
        <div className="mb-4 flex gap-2 flex-wrap">
          {['', 'pending', 'sent', 'rejected'].map(f => (
            <button key={f || 'all'} onClick={() => setStatusFilter(f)}
              className={`px-3 py-1 rounded text-sm ${(statusFilter || '') === f ? 'bg-[#006400] text-white' : 'bg-gray-200 hover:bg-gray-300'}`}>
              {f === '' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      )}

      {!isArchive && (
        <div className="mb-6 bg-white rounded-lg shadow p-4">
          <h3 className="font-bold mb-2">Search the archive to request a file</h3>
          <div className="flex gap-2">
            <input value={searchTerm} onChange={e => setSearchTerm(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="Search by reference number or service table..." className="flex-1 px-4 py-2 border rounded-lg" />
            <button onClick={handleSearch} disabled={searching} className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 disabled:opacity-50">
              {searching ? 'Searching...' : 'Search'}
            </button>
          </div>
          {searchResults.length > 0 && (
            <div className="mt-4 overflow-x-auto"><table className="min-w-full text-sm">
              <thead className="bg-gray-100"><tr>
                <th className="px-3 py-2 text-left">Reference No</th>
                <th className="px-3 py-2 text-left">Service / Table</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-left">Archived At</th>
                <th className="px-3 py-2 text-left">Action</th>
              </tr></thead>
              <tbody className="divide-y divide-gray-200">
                {searchResults.map(a => (
                  <tr key={a.id}>
                    <td className="px-3 py-2 font-mono">{a.reference_no}</td>
                    <td className="px-3 py-2">{a.source_table}</td>
                    <td className="px-3 py-2">{a.status}</td>
                    <td className="px-3 py-2">{a.archived_at ? new Date(a.archived_at).toLocaleDateString('en-GB') : '—'}</td>
                    <td className="px-3 py-2">
                      {a.has_open_request ? (
                        <span className="text-xs text-gray-500">Pending request already sent</span>
                      ) : (
                        <button onClick={() => setRequestingId(a.id)} className="text-[#006400] font-medium hover:underline">Request file</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          )}
          {searchResults.length === 0 && !searching && searchTerm && <p className="mt-3 text-sm text-gray-500">No archived records found.</p>}
        </div>
      )}

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-4 py-3 border-b font-bold">{isArchive ? 'All archive requests' : 'My archive requests'}</div>
        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : requests.length === 0 ? (
          <div className="p-8 text-center text-gray-500">No archive requests found.</div>
        ) : (
          <div className="overflow-x-auto"><table className="min-w-full text-sm">
            <thead className="bg-gray-100"><tr>
              <th className="px-3 py-2 text-left">Reference No</th>
              <th className="px-3 py-2 text-left">Service / Table</th>
              {isArchive && <th className="px-3 py-2 text-left">Requested By</th>}
              <th className="px-3 py-2 text-left">Reason</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Requested At</th>
              <th className="px-3 py-2 text-left">Action</th>
            </tr></thead>
            <tbody className="divide-y divide-gray-200">
              {requests.map(r => (
                <tr key={r.id}>
                  <td className="px-3 py-2 font-mono">{r.reference_no}</td>
                  <td className="px-3 py-2">{r.source_table}</td>
                  {isArchive && <td className="px-3 py-2">{r.requested_by_name || r.requester_fullname || r.requested_by}</td>}
                  <td className="px-3 py-2 max-w-[220px]"><div className="truncate" title={r.request_reason}>{r.request_reason}</div></td>
                  <td className="px-3 py-2">{badge(r.status)}</td>
                  <td className="px-3 py-2">{r.requested_at ? new Date(r.requested_at).toLocaleString('en-GB') : '—'}</td>
                  <td className="px-3 py-2 flex gap-2">
                    {isArchive && r.status === 'pending' && (
                      <>
                        <button onClick={() => respond(r.id, 'sent')} className="text-green-600 font-medium hover:underline">Send file</button>
                        <button onClick={() => respond(r.id, 'rejected')} className="text-red-600 font-medium hover:underline">Reject</button>
                      </>
                    )}
                    {(isArchive || (r.status === 'sent' && !isArchive)) && (
                      <button onClick={() => viewSent(r)} className="text-blue-600 font-medium hover:underline">View</button>
                    )}
                    {!isArchive && r.status === 'pending' && (
                      <button onClick={() => cancelRequest(r)} className="text-gray-500 font-medium hover:underline">Cancel</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      {requestingId && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-bold mb-3">Request this archived file</h3>
            <textarea value={reason} onChange={e => setReason(e.target.value)} rows={4} className="w-full border rounded px-3 py-2 mb-4"
              placeholder="Why do you need this file?" />
            <div className="flex justify-end gap-2">
              <button onClick={() => { setRequestingId(null); setReason(''); }} className="px-4 py-2 border rounded hover:bg-gray-100">Cancel</button>
              <button onClick={submitRequest} className="px-4 py-2 bg-[#006400] text-white rounded hover:bg-green-700">Submit request</button>
            </div>
          </div>
        </div>
      )}

      {sentDetail && (() => {
        let rd = null, files = [], fields = [];
        try { rd = typeof sentDetail.record_data === 'string' ? JSON.parse(sentDetail.record_data) : sentDetail.record_data; }
        catch (e) { rd = null; }
        if (rd) { files = extractArchiveFiles(rd); fields = archiveFormFields(rd); }
        return (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[70] p-4">
            <div className="bg-white rounded-lg w-full max-w-4xl max-h-[92vh] overflow-y-auto p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-bold">Archived request details</h3>
                  <p className="text-sm text-gray-500">Ref: {sentDetail.reference_no} · {sentDetail.source_table} · {sentDetail.status}</p>
                  {sentDetail.response_message && <p className="text-sm text-gray-600 mt-1">Officer note: {sentDetail.response_message}</p>}
                </div>
                <button onClick={() => setSentDetail(null)} className="text-gray-500 hover:text-gray-800 text-xl">×</button>
              </div>
              {!rd ? (
                <p className="text-red-500 text-sm">Could not parse the record data.</p>
              ) : (
                <>
                  {fields.length > 0 && (
                    <div className="mb-6">
                      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                        <h4 className="font-bold">Filled form</h4>
                        <button onClick={() => downloadArchiveForm(rd, fields, sentDetail)}
                          className="text-xs bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700">Download form (.txt)</button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 border rounded-lg p-4 bg-gray-50">
                        {fields.map(f => (
                          <div key={f.label} className="text-sm flex flex-col">
                            <span className="text-gray-500 text-xs uppercase tracking-wide">{f.label}</span>
                            <span className="font-medium break-words whitespace-pre-wrap">{renderFormValue(f.value)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <h4 className="font-bold mb-2">Attached files</h4>
                  {files.length === 0 ? (
                    <p className="text-sm text-gray-500">No attached files stored for this record.</p>
                  ) : (
                    <div className="space-y-2">
                      {files.map((f, i) => (
                        <a key={i} href={f.path} target="_blank" rel="noreferrer"
                          className="flex items-center justify-between bg-green-50 border border-green-200 rounded px-4 py-3 hover:bg-green-100">
                          <span className="text-sm font-medium">{f.label}</span>
                          <span className="text-xs bg-green-600 text-white px-3 py-1 rounded">Open / Download</span>
                        </a>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

function AppLayout({ children, onLogout, onOpenPasswordChange }) {
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const ALL = ['Director', 'DeputyDir', 'DeptHead', 'TeamLeader', 'BackOfficer', 'Archive', 'Communication', 'Ethics', 'ITAdmin', 'SuperAdmin'];
  const navItems = [
    { id: 'dashboard', label: 'ዳሽ ቦርድ-Dashboard', icon: FaChartBar, allowedRoles: ALL },
    { id: 'services', label: 'አገልግሎቶች-Services', icon: FaBox, allowedRoles: ['Director', 'DeputyDir', 'ITAdmin', 'SuperAdmin'] },
    { id: 'requests', label: 'የተጠየቁ አገልግሎቶች / Service Requests', icon: FaFileAlt, allowedRoles: ['Director', 'DeputyDir', 'DeptHead', 'TeamLeader', 'BackOfficer', 'ITAdmin', 'SuperAdmin'] },
    { id: 'news', label: 'ዜናዎች-Latest News', icon: FaClock, allowedRoles: ['Director', 'DeputyDir', 'Communication', 'ITAdmin', 'SuperAdmin'] },
    { id: 'complaints', label: 'ቅሬታዎች / Complaints', icon: FaFileAlt, allowedRoles: ['Director', 'DeputyDir', 'Ethics', 'ITAdmin', 'SuperAdmin'] },
    { id: 'feedback', label: 'ገንቢ ሃሳብ / Feedback', icon: FaFileAlt, allowedRoles: ['Director', 'DeputyDir', 'Communication', 'ITAdmin', 'SuperAdmin'] },
    { id: 'users', label: 'ተጠቃሚ ማስተዳደሪያ-User Management', icon: FaUser, allowedRoles: ['Director', 'DeputyDir', 'ITAdmin', 'SuperAdmin'] },
    { id: 'manual', label: 'ማኑዋል-Manual', icon: FaFileAlt, allowedRoles: ALL },
    { id: 'templates', label: 'ቴምፕሌት-Templates', icon: FaCog, allowedRoles: ['Director', 'DeputyDir', 'ITAdmin', 'SuperAdmin'] },
    { id: 'archive', label: 'መዝገብ ቤት-Archive', icon: FaBox, allowedRoles: ['Archive', 'BackOfficer', 'ITAdmin', 'SuperAdmin'] },
    { id: 'archive-requests', label: 'የመዝገብ ቤት ጥያቄ-Archive Requests', icon: FaExchangeAlt, allowedRoles: ['DeptHead', 'TeamLeader', 'Director', 'DeputyDir', 'Archive', 'ITAdmin', 'SuperAdmin'] },
  ];

  return (
    <div className="min-h-screen flex">
      <div className={`fixed inset-0 bg-black bg-opacity-50 z-40 ${sidebarOpen ? 'block lg:hidden' : 'hidden'}`} onClick={() => setSidebarOpen(false)} />
      <aside className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#FF6B00] text-white flex flex-col transition-transform duration-300 ease-in-out`}>
        <div className="flex items-center justify-between p-4 border-b border-orange-700">
          <h2 className="text-xl font-bold">PEA Backoffice</h2>
          <button onClick={() => setSidebarOpen(false)} className="lg:hidden p-1 hover:bg-orange-700 rounded"><FaTimes /></button>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {navItems.map(item => {
            if (item.allowedRoles && !item.allowedRoles.includes(localStorage.getItem('role'))) return null;
            return (
              <button key={item.id}
                onClick={() => { setCurrentPage(item.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-6 py-3 hover:underline text-left ${currentPage === item.id ? 'bg-orange-700 border-l-4 border-green-600' : ''}`}
              >
                <item.icon /> <span className="text-sm">{item.label}</span>
              </button>
            );
          })}
        </nav>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <div className="bg-[#FF6B00] text-white lg:hidden flex items-center gap-2 px-4 py-2">
          <button onClick={() => setSidebarOpen(true)} className="p-2 hover:bg-orange-700 rounded"><FaBars /></button>
        </div>
        <header className="bg-gradient-to-r from-[#FF6B00] to-[#cc5500] text-white shadow-lg">
          <div className="container mx-auto px-4 md:px-6 py-2">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-2 md:gap-3 min-w-0">
                <img src={process.env.PUBLIC_URL + '/Logo1.png'} alt="PEA Logo" className="h-12 md:h-20 flex-shrink-0" />
                <h1 className="text-sm md:text-lg lg:text-2xl font-bold leading-tight truncate">የነዳጅና ኢነርጂ ባለሥልጣን<br className="hidden md:inline" /> Petroleum and Energy Authority</h1>
              </div>
              <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
                <span className="flex items-center gap-1 md:gap-2 bg-green-600 px-2 md:px-3 py-1 rounded-full text-xs md:text-sm truncate max-w-[150px] md:max-w-none">
                  <FaUser /> <span className="truncate">{localStorage.getItem('username') || 'Admin'}{localStorage.getItem('sector') ? ` (${localStorage.getItem('sector')})` : ` (${localStorage.getItem('role') || 'N/A'})`}</span>
                </span>
                <button onClick={onOpenPasswordChange} className="flex items-center gap-1 bg-green-600 hover:bg-green-700 px-2 md:px-4 py-1 md:py-2 rounded text-xs md:text-sm font-medium transition">
                  <FaLock /> <span className="hidden md:inline">Change Password</span>
                </button>
                <button onClick={onLogout} className="flex items-center gap-1 bg-red-500 hover:bg-red-600 px-2 md:px-4 py-1 md:py-2 rounded text-xs md:text-sm font-medium transition">
                  <FaSignOutAlt /> <span className="hidden sm:inline">Logout</span>
                </button>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 min-w-0 bg-gray-50 overflow-auto">
          {(() => {
            const role = localStorage.getItem('role');
            const canAccess = (id) => {
              const item = navItems.find(n => n.id === id);
              return item && item.allowedRoles && item.allowedRoles.includes(role);
            };
            return (
              <>
                {currentPage === 'dashboard' && <Dashboard />}
                {currentPage === 'services' && canAccess('services') && <Services />}
                {currentPage === 'requests' && canAccess('requests') && <ServiceRequests />}
                {currentPage === 'news' && canAccess('news') && <LatestNews />}
                {currentPage === 'complaints' && canAccess('complaints') && <ContactList title="ቅሬታዎች / Complaints" endpoint="/complaints" fields={['name', 'email', 'phone', 'subject', 'complaint']} />}
                {currentPage === 'feedback' && canAccess('feedback') && <ContactList title="ገንቢ ሃሳብ / Feedback" endpoint="/feedback" fields={['name', 'email', 'phone', 'rating', 'feedback']} />}
                {currentPage === 'users' && canAccess('users') && <UserManagement />}
                {currentPage === 'manual' && canAccess('manual') && <ManualPage />}
                {currentPage === 'templates' && canAccess('templates') && <TemplateManager />}
                {currentPage === 'archive' && canAccess('archive') && <ArchivedServices />}
                {currentPage === 'archive-requests' && canAccess('archive-requests') && <ArchiveRequests />}
              </>
            );
          })()}
        </main>
        <Footer />
      </div>
    </div>
  );
}

function ContactList({ title, endpoint, fields }) {
  const [items, setItems] = useState([]);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await api.get(endpoint);
      setItems(response.data);
    } catch (error) {
      toast.error(`Failed to fetch ${title.toLowerCase()}`);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm(`Are you sure you want to delete this ${title.toLowerCase().slice(0, -1)}?`)) {
      try {
        await api.delete(`${endpoint}/${id}`);
        toast.success(`${title.slice(0, -1)} deleted successfully`);
        fetchItems();
      } catch (error) {
        toast.error(`Failed to delete ${title.toLowerCase().slice(0, -1)}`);
      }
    }
  };

  return (
    <div className="container mx-auto p-4 md:p-6">
      <h2 className="text-xl md:text-2xl font-bold mb-6">{title}</h2>
      <div className="space-y-4">
        {items.map((item) => (
          <div key={item.id} className="bg-white rounded-lg shadow p-4">
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                  <div><strong>Name:</strong> {item.name}</div>
                  <div><strong>Email:</strong> {item.email}</div>
                  <div><strong>Phone:</strong> {item.phone || 'N/A'}</div>
                  <div><strong>Subject:</strong> {item.subject}</div>
                  {item.rating && <div><strong>Rating:</strong> {item.rating}/5</div>}
                </div>
                {expandedId === item.id && (
                  <div className="mt-3 pt-3 border-t">
                    <strong>{fields[fields.length - 1].charAt(0).toUpperCase() + fields[fields.length - 1].slice(1)}:</strong>
                    <p className="mt-1 text-gray-700">{item[fields[fields.length - 1]]}</p>
                  </div>
                )}
              </div>
              <div className="flex gap-2 ml-4">
                <button onClick={() => setExpandedId(expandedId === item.id ? null : item.id)} className="text-blue-600 hover:text-blue-800">
                  <FaEye />
                </button>
                <button onClick={() => handleDelete(item.id)} className="text-red-600 hover:text-red-800">
                  <FaTrash />
                </button>
              </div>
            </div>
            <div className="text-xs text-gray-500 mt-2">Date: {new Date(item.created_at).toLocaleString()}</div>
          </div>
        ))}
        {items.length === 0 && (
          <p className="text-gray-500 text-center py-8">No {title.toLowerCase()} available</p>
        )}
      </div>
    </div>
  );
}

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [backofficeUsers, setBackofficeUsers] = useState([]);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({ fullname: '', email: '', role: '', sector: '' });
  const [newUser, setNewUser] = useState({ username: '', password: '', email: '', fullname: '', role: 'Director', sector: '' });
  const [userType, setUserType] = useState('backoffice');
  const [sectorServices, setSectorServices] = useState([]);
  const [selectedServices, setSelectedServices] = useState([]);

  useEffect(() => {
    fetchUsers();
    fetchSectorServices();
  }, []);

  useEffect(() => {
    if (editingUser) return;
    const sector = newUser.sector;
    if (!sector || sectorServices.length === 0) {
      setSelectedServices([]);
      return;
    }
    if (sector === 'both') {
      setSelectedServices(sectorServices.flatMap(([, services]) => services.map(s => s.type)));
    } else {
      const found = sectorServices.find(([s]) => s === sector);
      setSelectedServices(found ? found[1].map(s => s.type) : []);
    }
  }, [newUser.sector, sectorServices, editingUser]);

  const fetchSectorServices = async () => {
    try {
      const res = await api.get('/services');
      const sectors = {};
      res.data.forEach(s => {
        const sec = s.sector || 'Unassigned';
        if (!sectors[sec]) sectors[sec] = [];
        sectors[sec].push({ name: s.name, type: s.type });
      });
      setSectorServices(Object.entries(sectors));
    } catch (e) {
      console.error('Failed to fetch services');
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/auth/users');
      const allUsers = response.data;
      setBackofficeUsers(allUsers.filter(u => ['Director', 'DeputyDir', 'DeptHead', 'TeamLeader', 'BackOfficer', 'ITAdmin', 'Archive', 'Communication', 'Ethics', 'SuperAdmin'].includes(u.role)));
      setUsers(allUsers.filter(u => u.role === 'user'));
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await api.post('/auth/register-backoffice', { ...newUser, services: selectedServices });
      toast.success('User created successfully');
      setShowCreateForm(false);
      setNewUser({ username: '', password: '', email: '', fullname: '', role: 'Director', sector: '' });
      setSelectedServices([]);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create user');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    try {
      await api.put(`/auth/users/${resetPasswordUser.id}/reset-password`, { newPassword });
      toast.success('Password reset successfully');
      setResetPasswordUser(null);
      setNewPassword('');
      fetchUsers();
    } catch (error) {
      toast.error('Failed to reset password');
    }
  };

  const handleEditUser = (user) => {
    setEditFormData({
      fullname: user.fullname || '',
      email: user.email || '',
      role: user.role || '',
      sector: user.sector || ''
    });
    setSelectedServices(Array.isArray(user.services) ? user.services : []);
    setEditingUser(user);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;
    try {
      await api.put(`/auth/users/${editingUser.id}`, { ...editFormData, services: selectedServices });
      toast.success('User updated successfully');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update user');
    }
  };

  const handleDeleteUser = async (id) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await api.delete(`/auth/users/${id}`);
        toast.success('User deleted successfully');
        fetchUsers();
      } catch (error) {
        toast.error('Failed to delete user');
      }
    }
  };

  return (
    <div className="container mx-auto p-4 md:p-6">
      <h2 className="text-xl md:text-2xl font-bold mb-6">የተጠቃሚ ማስተዳደሪያ / User Management</h2>
      
      {resetPasswordUser && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-bold mb-4">Reset Password for: {resetPasswordUser.username}</h3>
          <form onSubmit={handleResetPassword} className="flex gap-4">
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              className="flex-1 border rounded px-3 py-2"
              required
            />
            <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-600">
              Reset Password
            </button>
            <button type="button" onClick={() => { setResetPasswordUser(null); setNewPassword(''); }} className="border px-4 py-2 rounded hover:bg-gray-100">
              Cancel
            </button>
          </form>
        </div>
      )}

      {editingUser && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-bold mb-4">Edit User: {editingUser.username}</h3>
          <form onSubmit={handleUpdateUser} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Full Name *</label>
              <input type="text" value={editFormData.fullname} onChange={(e) => setEditFormData({...editFormData, fullname: e.target.value})} className="w-full border rounded px-3 py-2" required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input type="email" value={editFormData.email} onChange={(e) => setEditFormData({...editFormData, email: e.target.value})} className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Role *</label>
              <select value={editFormData.role} onChange={(e) => setEditFormData({...editFormData, role: e.target.value})} className="w-full border rounded px-3 py-2">
                <option value="Director">Director</option>
                <option value="DeputyDir">Deputy Director</option>
                <option value="DeptHead">Department Head</option>
                <option value="TeamLeader">Team Leader</option>
                <option value="BackOfficer">Back Officer</option>
                <option value="ITAdmin">IT Admin</option>
                <option value="Archive">Archive</option>
                <option value="Communication">Communication</option>
                <option value="Ethics">Ethics</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Sector</label>
              <select value={editFormData.sector} onChange={(e) => setEditFormData({...editFormData, sector: e.target.value})} className="w-full border rounded px-3 py-2">
                <option value="">-- No Sector --</option>
                <option value="Petroleum competency certification">Petroleum competency certification</option>
                <option value="Petroleum marketing, supply & distribution">Petroleum marketing, supply & distribution</option>
                <option value="Energy investment licensing">Energy investment licensing</option>
                <option value="both">Both</option>
              </select>
            </div>
            <div className="md:col-span-2 flex gap-2">
              <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">
                Update User
              </button>
              <button type="button" onClick={() => setEditingUser(null)} className="border px-4 py-2 rounded hover:bg-gray-100">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {showCreateForm && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h3 className="text-lg font-bold mb-4">Create New Backoffice User</h3>
          <form onSubmit={handleCreateUser} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Full Name *</label>
              <input type="text" value={newUser.fullname} onChange={(e) => setNewUser({...newUser, fullname: e.target.value})} className="w-full border rounded px-3 py-2" required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Username *</label>
              <input type="text" value={newUser.username} onChange={(e) => setNewUser({...newUser, username: e.target.value})} className="w-full border rounded px-3 py-2" required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Password *</label>
              <input type="password" value={newUser.password} onChange={(e) => setNewUser({...newUser, password: e.target.value})} className="w-full border rounded px-3 py-2" required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Email</label>
              <input type="email" value={newUser.email} onChange={(e) => setNewUser({...newUser, email: e.target.value})} className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Role *</label>
              <select value={newUser.role} onChange={(e) => setNewUser({...newUser, role: e.target.value})} className="w-full border rounded px-3 py-2">
                <option value="Director">Director</option>
                <option value="DeputyDir">Deputy Director</option>
                <option value="DeptHead">Department Head</option>
                <option value="TeamLeader">Team Leader</option>
                <option value="BackOfficer">Back Officer</option>
                <option value="ITAdmin">IT Admin</option>
                <option value="Archive">Archive</option>
                <option value="Communication">Communication</option>
                <option value="Ethics">Ethics</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Sector</label>
              <select value={newUser.sector} onChange={(e) => setNewUser({...newUser, sector: e.target.value})} className="w-full border rounded px-3 py-2">
                <option value="">-- No Sector --</option>
                <option value="Petroleum competency certification">Petroleum competency certification</option>
                <option value="Petroleum marketing, supply & distribution">Petroleum marketing, supply & distribution</option>
                <option value="Energy investment licensing">Energy investment licensing</option>
                <option value="both">Both</option>
              </select>
            </div>
            <div className="md:col-span-2 flex gap-2">
              <button type="submit" className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-600">
                Create User
              </button>
              <button type="button" onClick={() => setShowCreateForm(false)} className="border px-4 py-2 rounded hover:bg-gray-100">
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {(editFormData.sector || newUser.sector) && (
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <label className="block text-sm font-medium mb-2">Service Access ({selectedServices.length} selected)</label>
        <div className="space-y-3">
          {sectorServices.filter(([sector]) => {
            const active = editFormData.sector || newUser.sector;
            return active === 'both' || active === sector;
          }).map(([sector, services]) => (
            <div key={sector}>
              <h4 className="font-bold text-sm text-blue-700 mb-1">{sector}</h4>
              <div className="border rounded p-2 space-y-1 max-h-40 overflow-y-auto">
                {services.map((s, i) => (
                  <label key={i} className="flex items-center gap-2 text-sm hover:bg-gray-50 p-1 rounded cursor-pointer">
                    <input type="checkbox" checked={selectedServices.includes(s.type)} onChange={() => {
                      setSelectedServices(prev =>
                        prev.includes(s.type) ? prev.filter(v => v !== s.type) : [...prev, s.type]
                      );
                    }} className="rounded" />
                    {s.name}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
      )}

      <div className="flex gap-2 mb-4">
        <button onClick={() => setShowCreateForm(true)} className="bg-[#4CAF50] text-white px-4 py-2 rounded hover:bg-green-600">
          <FaPlus className="inline mr-2" />Create Backoffice User
        </button>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
        <div className="bg-purple-100 p-4 border-b">
          <h3 className="font-bold text-purple-800">Backoffice Users (Admin)</h3>
        </div>
        <div className="overflow-x-auto"><table className="min-w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Full Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sector</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-gray-500">Loading...</td>
              </tr>
            ) : backofficeUsers.length === 0 ? (
              <tr>
                <td colSpan="7" className="px-4 py-8 text-center text-gray-500">No backoffice users</td>
              </tr>
            ) : (
              backofficeUsers.map((user) => {
                const roleColors = {
                  'Director': 'bg-red-100 text-red-800',
                  'DeputyDir': 'bg-orange-100 text-orange-800',
                  'DeptHead': 'bg-yellow-100 text-yellow-800',
                  'SuperAdmin': 'bg-gradient-to-r from-red-100 to-yellow-100 text-red-900 border border-red-300',
                  'TeamLeader': 'bg-blue-100 text-blue-800',
                  'BackOfficer': 'bg-purple-100 text-purple-800',
                  'ITAdmin': 'bg-green-100 text-green-800',
                  'Archive': 'bg-indigo-100 text-indigo-800',
                  'Communication': 'bg-teal-100 text-teal-800',
                  'Ethics': 'bg-pink-100 text-pink-800'
                };
                const roleColor = roleColors[user.role] || 'bg-gray-100 text-gray-800';
                return (
                  <tr key={user.id}>
                    <td className="px-4 py-3">{user.id}</td>
                    <td className="px-4 py-3">{user.fullname || user.username}</td>
                    <td className="px-4 py-3">{user.username}</td>
                    <td className="px-4 py-3">{user.email || 'N/A'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded text-xs ${roleColor}`}>{user.role}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-1 rounded text-xs bg-purple-100 text-purple-800">{user.sector || 'N/A'}</span>
                    </td>
                    <td className="px-4 py-3">
                      <button onClick={() => handleEditUser(user)} className="text-green-600 hover:text-green-800 mr-3">
                        <FaEdit /> Edit
                      </button>
                      <button onClick={() => setResetPasswordUser(user)} className="text-blue-600 hover:text-blue-800 mr-3">
                        <FaEdit /> Reset Pwd
                      </button>
                      <button onClick={() => handleDeleteUser(user.id)} className="text-red-600 hover:text-red-800">
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="bg-blue-100 p-4 border-b">
          <h3 className="font-bold text-blue-800">Customer Users</h3>
          <p className="text-sm text-blue-600">These users register from the customer portal</p>
        </div>
        <div className="overflow-x-auto"><table className="min-w-full">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Username</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Full Name</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-center text-gray-500">Loading...</td>
              </tr>
            ) : users.length === 0 ? (
              <tr>
                <td colSpan="6" className="px-4 py-8 text-center text-gray-500">No customer users</td>
              </tr>
            ) : (
              users.map((user) => (
                <tr key={user.id}>
                  <td className="px-4 py-3">{user.id}</td>
                  <td className="px-4 py-3">{user.username}</td>
                  <td className="px-4 py-3">{user.email || 'N/A'}</td>
                  <td className="px-4 py-3">{user.fullname || 'N/A'}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">Customer</span>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => setResetPasswordUser(user)} className="text-blue-600 hover:text-blue-800 mr-3">
                      <FaEdit /> Reset Pwd
                    </button>
                    <button onClick={() => handleDeleteUser(user.id)} className="text-red-600 hover:text-red-800">
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function ManualPage() {
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('video', file);
      await api.post('/manual/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Manual video updated successfully! Refresh the page to see the new video.');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to upload video');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="container mx-auto p-4 md:p-6">
      <div className="bg-white rounded-lg p-4 md:p-8 shadow">
        <h2 className="text-xl md:text-2xl font-bold mb-4">የተጠቃሚ መመሪያ / User Manual</h2>
        <p className="text-gray-700 mb-4">Watch the video below for instructions on how to use the PEA Backoffice portal.</p>
        <video controls className="w-full max-w-4xl mx-auto rounded-lg shadow" style={{ maxHeight: '70vh' }}>
          <source src={`${API_ORIGIN}/uploads/manual.mp4`} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <div className="mt-8 p-4 border-2 border-dashed border-gray-300 rounded-lg">
          <h3 className="text-lg font-semibold mb-2">Update Manual Video</h3>
          <p className="text-sm text-gray-600 mb-3">Upload a new MP4 video to replace the current manual.</p>
          <label className={`inline-flex items-center gap-2 px-4 py-2 rounded cursor-pointer ${uploading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'} text-white`}>
            <FaUpload />
            {uploading ? 'Uploading...' : 'Choose Video File'}
            <input type="file" accept="video/mp4,video/webm,video/ogg" onChange={handleUpload} disabled={uploading} className="hidden" />
          </label>
        </div>
      </div>
    </div>
  );
}

function PasswordChangeModal({ onPasswordChanged, onLogout, onCancel }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    if (newPassword.length < 4) {
      setError('New password must be at least 4 characters');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/change-password', { currentPassword, newPassword });
      localStorage.setItem('must_change_password', 'false');
      toast.success('Password changed successfully!');
      onPasswordChanged();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg p-4 md:p-8 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4 md:mb-6">
          <h2 className="text-xl md:text-2xl font-bold text-[#006400]">Change Your Password</h2>
          {onCancel && (
            <button onClick={onCancel} className="text-gray-500 hover:text-gray-800 text-2xl leading-none" aria-label="Close"><FaTimes /></button>
          )}
        </div>
        {!onCancel && (
          <p className="text-gray-600 mb-4 text-sm md:text-base text-center">You must change your password before continuing. This is required for first login or after a password reset.</p>
        )}
        {error && <p className="text-red-500 text-sm mb-4 text-center">{error}</p>}
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">Current Password</label>
            <input type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className="w-full border rounded px-3 py-2" required />
          </div>
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1">New Password</label>
            <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className="w-full border rounded px-3 py-2" required minLength={4} />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium mb-1">Confirm New Password</label>
            <input type="password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} className="w-full border rounded px-3 py-2" required />
          </div>
          <button type="submit" disabled={loading} className="w-full bg-[#006400] text-white py-2 rounded hover:bg-green-700 disabled:opacity-50">
            {loading ? 'Changing...' : 'Change Password'}
          </button>
        </form>
        <div className="mt-4 text-center">
          {onCancel ? (
            <button onClick={onCancel} className="text-sm text-gray-500 hover:text-gray-800">
              Cancel
            </button>
          ) : (
            <button onClick={onLogout} className="text-sm text-gray-500 hover:text-red-500 underline">
              Logout
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('username'));
  const [forcePasswordChange, setForcePasswordChange] = useState(localStorage.getItem('must_change_password') === 'true');
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  const handleLogin = () => {
    setIsAuthenticated(true);
    setForcePasswordChange(localStorage.getItem('must_change_password') === 'true');
  };

  const handlePasswordChanged = () => {
    setForcePasswordChange(false);
    setShowPasswordModal(false);
  };

  const handleLogout = () => {
    api.post('/auth/logout').catch(() => {});
    localStorage.removeItem('username');
    localStorage.removeItem('role');
    localStorage.removeItem('sector');
    localStorage.removeItem('userId');
    localStorage.removeItem('services');
    localStorage.removeItem('must_change_password');
    setIsAuthenticated(false);
    setForcePasswordChange(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login: handleLogin, logout: handleLogout }}>
      <Router basename="">
        {isAuthenticated ? (
          <AppLayout onLogout={handleLogout} onOpenPasswordChange={() => setShowPasswordModal(true)} />
        ) : (
          <Routes>
            <Route path="*" element={<Login onLogin={handleLogin} />} />
          </Routes>
        )}
        {(forcePasswordChange || showPasswordModal) && (
          <PasswordChangeModal
            onPasswordChanged={handlePasswordChanged}
            onLogout={handleLogout}
            onCancel={forcePasswordChange ? undefined : () => setShowPasswordModal(false)}
          />
        )}
      </Router>
    </AuthContext.Provider>
  );
}

export default App;
